/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { 
  Box, Typography, Button, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Paper, Dialog, DialogTitle, DialogContent, 
  DialogActions, IconButton, FormControl, InputLabel, MenuItem, Select,
  TextField, Checkbox, FormControlLabel, Grid, Snackbar, Alert,
  Tooltip, Divider, CircularProgress, Card, CardContent, Stack, Avatar,
  Chip, Link, Fab, Tabs, Tab, Badge, LinearProgress, List, ListItem,
  ListItemText, ListItemAvatar, Pagination, FormGroup, InputAdornment,
  BottomNavigation, BottomNavigationAction
} from '@mui/material';
import { 
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LinkedIn as LinkedInIcon,
  Event as EventIcon,
  CheckCircle as SuccessIcon,
  Cancel as FailIcon,
  Refresh as RefreshIcon,
  AccessTime as TimeIcon,
  EmojiEvents as TrophyIcon,
  Whatshot as FireIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  Language as LanguageIcon,
  Search as SearchIcon,
  Business as BusinessIcon,
  LocationOn as LocationOnIcon,
  Security as SecurityIcon,
  Shield as ShieldIcon,
  MarkEmailRead as MarkEmailReadIcon,
  GppGood as GppGoodIcon,
  GppMaybe as GppMaybeIcon,
  GppBad as GppBadIcon,
  Dns as DnsIcon,
  Microsoft as MicrosoftIcon,
  Google as GoogleIcon,
  QuestionMark as QuestionMarkIcon,
  Devices as DevicesIcon,
  Close as CloseIcon,
  FilterAlt as FilterIcon,
  Cancel as CancelIcon,
  OpenInNew as OpenInNewIcon,
  Info as InfoIcon,
  NetworkCheck as NetworkCheckIcon,
  Person as PersonIcon,
  WorkOutline as WorkIcon,
  ContactPage as ContactPageIcon,
  Save as SaveIcon,
  VerifiedUser as VerifiedUserIcon,
  Cloud as CloudIcon,
  CloudDone as CloudDoneIcon,
  People as PeopleIcon,
  Description as DescriptionIcon,
  Computer as ComputerIcon,
  Settings as SettingsIcon,
  Code as CodeIcon,
  AccountBalance as AccountBalanceIcon,
  ContentCopy as CopyIcon,
  Assignment as AssignmentIcon,
  MoreHoriz as MoreHorizIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Circle as CircleIcon,
  PersonAdd as PersonAddIcon,
  ArrowBack as ArrowBackIcon,
  Insights as InsightsIcon,
  AutoAwesome as AutoAwesomeIcon,
  EventRepeat as FollowUpIcon
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { format } from 'date-fns';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import CompanyResearch from './CompanyResearch';
import axios from 'axios';
import logger from './../utils/logger.js';
import useIsMobile from '../hooks/useIsMobile';
import { HEADER_HEIGHT } from '../constants/layout';
import EnrichedContactPanel from './EnrichedContactPanel';
import OutreachStats from './OutreachStats';

const MOBILE_COMPANY_HEADER_SPACER = 60;

// Add CSS for pulse animation
const pulseKeyframes = `
  @keyframes pulse {
    0% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
    100% {
      opacity: 1;
    }
  }
`;

// Inject the CSS into the document head
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = pulseKeyframes;
  document.head.appendChild(style);
}

const getWebsiteHref = (url) => (url?.startsWith('http') ? url : `https://${url}`);

const getWebsiteLabel = (url) => {
  if (!url) return '';
  try {
    return new URL(getWebsiteHref(url)).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
};

const SalesOutreach = ({ recordId, companyName, contactInfo, onClose, onOutreachUpdate, openWithUrlSearch = false }) => {
  const { user, team, isSupervisor } = useAuth();
  const hasTeammates = (team?.members || []).some((member) => member.username && member.username !== user?.username);
  const showOutreachUser = Boolean(recordId && (user?.is_admin || isSupervisor || hasTeammates));
  const isMobile = useIsMobile();
  
  // State for outreach records
  const [outreaches, setOutreaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Add state for tab management
  const [activeTab, setActiveTab] = useState(0);
  
  // Tab change handler
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  // State for form
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    contact_name: '',
    outreach_method: 'phone',
    contact_detail: '',
    outreach_date: new Date(),
    reached_intended_contact: false,
    followup_date: null,
    meeting_scheduled: false,
    meeting_date: null,
    notes: '',
    completed: false
  });
  const [editMode, setEditMode] = useState(false);
  const [followUpMode, setFollowUpMode] = useState(false);
  const [currentEditId, setCurrentEditId] = useState(null);
  
  const [statsRefreshKey, setStatsRefreshKey] = useState(0);
  
  // State for notifications
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  
  // State for tracking validation errors
  const [validationError, setValidationError] = useState('');
  
  // New state for DNS data
  const [dnsData, setDnsData] = useState(null);
  const [dnsLoading, setDnsLoading] = useState(false);
  const [dnsError, setDnsError] = useState(null);
  
  // State for DNS security changes
  const [securityChanges, setSecurityChanges] = useState(null);
  const [securityChangesLoading, setSecurityChangesLoading] = useState(false);
  
  // State for Research Dialog
  const [researchDialogOpen, setResearchDialogOpen] = useState(false);
  
  // State for company information from DNS lookup
  const [companyInfo, setCompanyInfo] = useState(null);
  
  // State for provider info dialog
  const [providerInfoOpen, setProviderInfoOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [providerSearchResult, setProviderSearchResult] = useState(null);
  const [providerSearchLoading, setProviderSearchLoading] = useState(false);
  
  // Add a state to force UI updates when IP lookups complete
  const [forceUpdate, setForceUpdate] = useState(0);
  
  // Language state (simplified)
  const language = "en";

  // State for Lusha contacts functionality
  const [lushaContacts, setLushaContacts] = useState(null);
  const [lushaLoading, setLushaLoading] = useState(false);
  const [lushaError, setLushaError] = useState(null);
  const [lushaContactsPage, setLushaContactsPage] = useState(0);
  const [lushaRequestId, setLushaRequestId] = useState(null);
  
  // Enhanced Lusha state for filtering
  const [lushaFilters, setLushaFilters] = useState({
    searchText: '',
    seniority_levels: [], // No default filter - show all contacts
    departments: [] // Dynamic department filter
  });
  const [filteredLushaContacts, setFilteredLushaContacts] = useState(null);
  const [enrichLoading, setEnrichLoading] = useState(false);
  const [enrichingContacts, setEnrichingContacts] = useState(new Set());
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactDetailsOpen, setContactDetailsOpen] = useState(false);
  
  // Company logo state
  const [companyLogo, setCompanyLogo] = useState(null);
  
  // Enrichment modal state
  const [enrichmentModal, setEnrichmentModal] = useState({
    open: false,
    data: null
  });
  
  // Salesforce integration state
  const [salesforceLeadsMap, setSalesforceLeadsMap] = useState({}); // Map email -> lead info
  const [checkingSalesforce, setCheckingSalesforce] = useState(false);
  const [addingToSalesforce, setAddingToSalesforce] = useState(new Set()); // Track which contacts are being added
  const [salesforceConnectionStatus, setSalesforceConnectionStatus] = useState(null);
  
  // HubSpot integration state
  const [hubspotContactsMap, setHubspotContactsMap] = useState({}); // Map email -> contact info
  const [checkingHubspot, setCheckingHubspot] = useState(false);
  const [addingToHubspot, setAddingToHubspot] = useState(new Set()); // Track which contacts are being added
  const [hubspotConnectionStatus, setHubspotConnectionStatus] = useState(null);
  
  // AI Research state variables - exactly like CompanyResearch
  const [researchData, setResearchData] = useState(null);
  const [researchLoading, setResearchLoading] = useState(false);
  const [researchError, setResearchError] = useState(null);
  const [openSourceDialog, setOpenSourceDialog] = useState(false);
  const [selectedSource, setSelectedSource] = useState(null);
  
  const lushaContactsPerPage = 25;
  
  // Add state for URL management
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [editingUrl, setEditingUrl] = useState(false);
  const [newUrl, setNewUrl] = useState('');
  
  // State variables for URL-related functionality
  const [urlSearchLoading, setUrlSearchLoading] = useState({});
  const [urlSearchError, setUrlSearchError] = useState({});
  
  // Add a state variable to track the last DNS check time
  const [lastDnsCheckTime, setLastDnsCheckTime] = useState(null);
  
  // Add state for reverse DNS lookup results
  const [reverseLookups, setReverseLookups] = useState({});
  const [pendingIps, setPendingIps] = useState([]);
  const [failedLookups, setFailedLookups] = useState({});
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  
  // Add state for tracking progress of reverse lookups
  const [lookupProgress, setLookupProgress] = useState({ current: 0, total: 0 });
  const [showLookupProgress, setShowLookupProgress] = useState(false);
  
  // Queue for processing reverse lookups - define as a ref to avoid re-renders
  const reverseLookupQueueRef = useRef([]);
  const isProcessingQueueRef = useRef(false);
  const reverseLookupResultsRef = useRef({});
  const failedLookupsRef = useRef({});
  const totalIpsToProcessRef = useRef(0);
  const ipsNeedingLookupRef = useRef([]);
  
  // Create a function to process needed lookups
  const processNeededLookups = useCallback(() => {
    if (!ipsNeedingLookupRef.current || ipsNeedingLookupRef.current.length === 0) return;
    if (isProcessingQueueRef.current || isDialogOpen) {
      logger.log(`Postponing ${ipsNeedingLookupRef.current.length} reverse lookups while busy or dialog is open`);
      return;
    }
    
    const realIpsToLookup = [...new Set(ipsNeedingLookupRef.current)]; // Remove duplicates
    logger.log(`Found ${realIpsToLookup.length} unique IPs needing lookup`);
    
    // For large numbers of IPs, we'll process ALL of them, not just a limited batch
    if (realIpsToLookup.length > 0) {
      // Reset progress tracking
      setLookupProgress({ current: 0, total: realIpsToLookup.length });
      totalIpsToProcessRef.current = realIpsToLookup.length;
      
      // Schedule processing outside the render cycle
      setTimeout(() => {
        processBatchOfIps(realIpsToLookup);
      }, 300);
      
      logger.log(`Processing all ${realIpsToLookup.length} IPs that need lookup`);
    }
  }, [isDialogOpen]);
  
  // Add an effect to handle initiating lookups, triggered by changes to ipsNeedingLookup
  useEffect(() => {
    if (ipsNeedingLookupRef.current.length > 0 && !isProcessingQueueRef.current && !isDialogOpen) {
      processNeededLookups();
    }
  }, [isDialogOpen, processNeededLookups, forceUpdate]);
  
  // Create these variables in component scope for backwards compatibility
  let reverseLookupQueue = reverseLookupQueueRef.current;
  let isProcessingQueue = isProcessingQueueRef.current;
  let reverseLookupResults = reverseLookupResultsRef.current;
  let failedLookupsCache = failedLookupsRef.current;
  
  // Add useEffect to sync failedLookups state with ref
  useEffect(() => {
    // Update the ref whenever the state changes
    failedLookupsRef.current = failedLookups;
    failedLookupsCache = failedLookupsRef.current;
  }, [failedLookups]);
  
  // Helper function to load outreach records
  const loadOutreaches = async () => {
    try {
      setLoading(true);
      
      // Determine the view mode based on user role and context:
      // - If looking at a specific company (recordId provided):
      //   - Admins see all users' outreaches for this company ('company_only')
      //   - Regular users see only their own outreaches for this company ('user_company')
      // - If not looking at a specific company:
      //   - Everyone only sees their own outreaches ('user_only')
      let viewMode = 'user_only';
      
      if (recordId) {
        // Non-admins request user_company. The server includes teammates and supervised users.
        viewMode = user?.is_admin ? 'company_only' : 'user_company';
      }
      
      logger.log(`Loading outreaches with viewMode: ${viewMode}, for ${recordId ? 'company ' + recordId : 'user'}, is_admin: ${user?.is_admin}`);
      
      const response = await api.getOutreaches(null, null, recordId, viewMode);
      
      if (response.success) {
        logger.log(`Loaded ${response.data?.length || 0} outreach records for ${recordId ? 'company ' + recordId : 'user'}`);
        setOutreaches(response.data || []);
      } else {
        setError(response.message || 'Failed to load outreach records');
      }
    } catch (err) {
      logger.error('Error loading outreach records:', err);
      setError('Failed to load outreach records');
    } finally {
      setLoading(false);
    }
  };
  
  // Utility function to extract base domain from a hostname
  const getBaseDomain = (hostname) => {
    try {
      const parts = hostname.split('.');
      if (parts.length >= 2) {
        // For most domains, return the last two parts (e.g., example.com)
        const tld = parts[parts.length - 1];
        const domain = parts[parts.length - 2];
        return `${domain}.${tld}`;
      }
    } catch (e) {}
    return hostname; // Fallback to full hostname
  };
  
  // Function to load security changes for a domain
  const loadSecurityChanges = useCallback(async (domain) => {
    if (!domain) return;
    
    try {
      setSecurityChangesLoading(true);
      logger.log(`Loading security changes for domain: ${domain}`);
      
      const response = await api.getDomainSecurityChanges(domain, 7); // Last 7 days
      
      if (response && response.success) {
        logger.log('Security changes loaded:', response.data);
        setSecurityChanges(response.data);
      } else {
        logger.error('Failed to load security changes:', response?.message);
        setSecurityChanges(null);
      }
    } catch (error) {
      logger.error('Error loading security changes:', error);
      setSecurityChanges(null);
    } finally {
      setSecurityChangesLoading(false);
    }
  }, []);
  
  // Function to check DNS records
  const checkDNS = useCallback(async (retryCount = 0, maxRetries = 3, forceRefresh = false) => {
    console.group(`checkDNS${forceRefresh ? ' (forced refresh)' : ''}`);
    const now = new Date().toISOString();
    logger.log(`check DNS called at ${now}, forceRefresh=${forceRefresh}, retryCount=${retryCount}`);
    
    // If no URL available, don't try to check DNS
    if (!websiteUrl && (!contactInfo || typeof contactInfo === 'string')) {
      logger.log('checkDNS - No website URL available to check');
      console.groupEnd();
      return;
    }
    
    // Get domain from website URL
    let domain = websiteUrl || '';
    
    try {
      // Try to parse the URL and get hostname
      if (domain.includes('://')) {
        const url = new URL(domain);
        domain = url.hostname;
      } else if (domain.includes('/')) {
        // Handle cases where URL might not have protocol
        domain = domain.split('/')[0];
      }
      
      // Clean up domain: remove www. prefix and any trailing dots
      domain = domain.replace(/^www\./, '').replace(/\.$/, '');
      
      // Make sure it's a valid domain (contains at least one dot)
      if (!domain.includes('.')) {
        logger.log(`Invalid domain: ${domain}`);
        console.groupEnd();
        return;
      }
      
      logger.log(`checkDNS - Extracted domain: ${domain}`);
    } catch (e) {
      logger.error("checkDNS - Failed to parse URL, using as is:", domain);
    }
    
    // If domain is empty or invalid after extraction, don't proceed
    if (!domain || domain.trim() === '') {
      logger.error('checkDNS - No valid domain extracted');
      console.groupEnd();
      return;
    }
    
    try {
      setDnsLoading(true);
      setDnsError(null);
      
      // Log whether this is a forced refresh
      logger.log(`checkDNS - Making DNS check request for domain: ${domain}${forceRefresh ? ' (forced refresh)' : ''}`);
      
      // Include forceRefresh parameter in API call
      const response = await api.checkDomainDNS(domain, forceRefresh);
      
      if (response && response.success) {
        logger.log("checkDNS - DNS check successful:", response);
        
        // Check if response data is empty or minimal (no useful DNS data)
        const isEmptyData = !response.data || 
                         (response.data && 
                         (!response.data.spf || !response.data.dkim || !response.data.dmarc) && 
                         (!response.data.email || !response.data.email.provider));
        
        if (isEmptyData && retryCount < maxRetries) {
          logger.warn(`checkDNS - Empty or minimal data returned for ${domain}, forcing refresh...`);
          // Automatically recheck with forced refresh
          setTimeout(() => checkDNS(retryCount + 1, maxRetries, true), 2000);
          setDnsLoading(false);
          console.groupEnd();
          return;
        }
        
        // Update DNS data and last check time
        setDnsData(response.data);
        
        // Update the last check time - prefer the server's timestamp if available
        if (response.data && response.data.last_check_datetime) {
          setLastDnsCheckTime(response.data.last_check_datetime);
          logger.log(`DNS check timestamp from server: ${response.data.last_check_datetime}`);
        } else if (response.cached && response.cache_date) {
          setLastDnsCheckTime(response.cache_date);
          logger.log(`DNS cache timestamp from server: ${response.cache_date}`);
        } else {
          setLastDnsCheckTime(now);
          logger.log(`Using local timestamp for DNS check: ${now}`);
        }
        
        // Add information about whether this was from cache
        if (response.cached) {
          logger.log(`DNS data was served from cache (${response.cache_date})`);
        } else {
          logger.log('DNS data was freshly checked, not from cache');
        }
        
        // If we need to cache company information, do it here
        if (response.data && response.data.company_info) {
          setCompanyInfo(response.data.company_info);
        }
        
        // Load security changes for this domain
        loadSecurityChanges(domain);
      } else {
        const errorMessage = response && response.message ? response.message : "DNS check failed";
        logger.error("checkDNS - DNS check failed:", errorMessage);
        setDnsError(errorMessage);
      }
    } catch (error) {
      logger.error("checkDNS - API error during DNS check:", error);
      const errorMessage = error.response && error.response.data && error.response.data.message
        ? error.response.data.message
        : error.message || "Failed to check DNS records";
      setDnsError(errorMessage);
      
      // Retry logic
      if (retryCount < maxRetries) {
        logger.log(`checkDNS - Retrying DNS check (${retryCount + 1}/${maxRetries})...`);
        setTimeout(() => checkDNS(retryCount + 1, maxRetries, forceRefresh), 2000);
        return;
      }
    } finally {
      setDnsLoading(false);
      console.groupEnd();
    }
  }, [recordId, contactInfo, websiteUrl, setDnsData, setDnsLoading, setDnsError, setCompanyInfo, loadSecurityChanges]);
  
  // Load outreach records on component mount
  useEffect(() => {
    const loadInitialData = async () => {
      // Log contactInfo for debugging
      if (contactInfo) {
        logger.log("Contact info available for auto-fill:", contactInfo);
        
        // Check if contactInfo is a string or an object (handling both cases)
        let url = '';
        
        if (typeof contactInfo === 'string') {
          // If contactInfo is a string (from EventsDashboard), use it as is if it looks like a URL
          if (contactInfo.includes('.') && 
              (contactInfo.toLowerCase().startsWith('http') || 
               contactInfo.toLowerCase().includes('www.') || 
               !contactInfo.includes('@'))) {
            url = contactInfo;
            logger.log("Extracted URL from contactInfo string:", url);
          }
        } else {
          // If contactInfo is an object, try to extract website URL using the same logic as checkDNS
          // Check for common website field names in order of priority
          const possibleFields = [
            'URL', 'url', 'Website', 'website', 'Domain', 'domain', 
            'Website URL', 'website_url', 'Company Website', 'company_website',
            'Corporate Website', 'Bedrijfswebsite', 'Homepage'
          ];
          
          for (const field of possibleFields) {
            if (contactInfo[field] && typeof contactInfo[field] === 'string') {
              url = contactInfo[field];
              logger.log(`Found website in field "${field}":`, url);
              break;
            }
          }
          
          // If no website found in common fields, try to find any field with URL-like value
          if (!url) {
            for (const field in contactInfo) {
              const value = contactInfo[field];
              if (value && typeof value === 'string' && 
                  value.includes('.') && 
                  (value.toLowerCase().startsWith('http') || 
                   value.toLowerCase().includes('www.'))) {
                url = value;
                logger.log(`Found URL-like value in field "${field}":`, url);
                break;
              }
            }
          }
          
          logger.log("Extracted URL from contactInfo object:", url);
        }

        if (url) {
          // Auto-populate the website field if a URL is found
          setWebsiteUrl(url);
          setNewUrl(url); // Also set the newUrl field with the same value
          
          // If we have a company name and URL, update the website title
          if (companyName && url) {
            if (!formData.id) {
              // Only auto-fill for new outreach records
              setFormData(prev => ({
                ...prev,
                title: `${companyName} - ${url}`
              }));
            }
          }
        }
      }
      
      // Load outreaches for this company
      await loadOutreaches();
      
      // Only try to run DNS check if there's contact info to work with
      if (contactInfo) {
        checkDNS();
      }
      
      // URL search has been removed as we always have the URL already
    };
    
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactInfo, recordId, companyName, checkDNS, openWithUrlSearch]);
  
  // Add handleToggleCompletion method after loadStatistics
  const handleToggleCompletion = async (id, isCompleted) => {
    try {
      logger.log(`API: Marking outreach ${id} as ${isCompleted ? 'completed' : 'not completed'}`);
      
      if (!id) {
        showNotification('Cannot update completion status: Missing outreach ID', 'error');
        return;
      }
      
      const response = await api.toggleOutreachCompletion(id, isCompleted);
      if (response.success) {
        showNotification(`Outreach ${isCompleted ? 'marked as completed' : 'marked as not completed'}`, 'success');
        
        // Update local state to avoid a full reload
        setOutreaches(prevOutreaches => 
          prevOutreaches.map(outreach => 
            outreach.id === id 
              ? { 
                  ...outreach, 
                  completed: isCompleted, 
                  completed_date: isCompleted ? new Date().toISOString() : null 
                } 
              : outreach
          )
        );
        
        setStatsRefreshKey((key) => key + 1);
        
        // Call the callback if provided
        if (onOutreachUpdate) {
          logger.log('SalesOutreach: Calling onOutreachUpdate callback after toggle completion');
          onOutreachUpdate();
        }
      } else {
        showNotification(response.message || 'Failed to update completion status', 'error');
      }
    } catch (err) {
      logger.error('Error toggling completion status:', err);
      showNotification('Failed to update completion status', 'error');
    }
  };
  
  // Validation functions for contact details
  const validatePhone = (phone) => {
    if (!phone) return false;
    
    // Clean up the phone number for validation
    const cleaned = String(phone).replace(/\s+/g, '').replace(/[().-]/g, '');
    
    // For the Dutch/European context
    if (cleaned.length < 6) return false; // Too short to be valid
    
    // Allow various phone formats
    // 1. Simple number check (at least 6 digits)
    if (/^\d{6,}$/.test(cleaned)) {
      return true;
    }
    
    // 2. International format with + prefix
    if (/^\+\d{6,}$/.test(cleaned)) {
      return true;
    }
    
    // 3. Traditional format with spaces, parentheses, dashes
    const phoneRegex = /^(?:\+\d{1,3}\s?)?\(?(?:\d{1,4})\)?[-.\s]?(?:\d{1,4})[-.\s]?(?:\d{1,9})$/;
    return phoneRegex.test(phone);
  };

  const validateEmail = (email) => {
    // Standard email validation regex, but lenient for real-world data
    // Allow basic email validation
    if (!email) return false;
    
    // Clean email
    const cleaned = String(email).trim();
    
    // Simple check: contains @ and at least one dot after @
    if (cleaned.includes('@') && cleaned.lastIndexOf('.') > cleaned.indexOf('@')) {
      // Additional check for standard email format
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      return emailRegex.test(cleaned);
    }
    
    return false;
  };

  // Function to validate LinkedIn URL format
  const validateLinkedIn = (value) => {
    if (!value) return false;
    // Simple validation for LinkedIn URLs
    return value.includes('linkedin.com/');
  };

  // Function to capitalize the first letter of each word
  const capitalizeWords = (str) => {
    if (!str) return '';
    return str
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Validate contact detail based on outreach method
  const validateContactDetail = (detail, method) => {
    if (!detail) return false;
    
    switch(method) {
      case 'phone':
        return validatePhone(detail);
      case 'email':
        return validateEmail(detail);
      case 'linkedin':
        return validateLinkedIn(detail);
      default:
        return true;
    }
  };
  
  // Handle form submission
  const handleSubmit = async () => {
    try {
      // Validate form data
      if (!formData.contact_name) {
        showNotification('Contact name is required', 'error');
        return;
      }
      
      // Validate contact detail exists
      if (!formData.contact_detail) {
        const detailType = getContactDetailLabel(formData.outreach_method).toLowerCase();
        showNotification(`${detailType} is required`, 'error');
        return;
      }
      
      // Validate contact detail format
      if (!validateContactDetail(formData.contact_detail, formData.outreach_method)) {
        const detailType = getContactDetailLabel(formData.outreach_method);
        showNotification(`Invalid ${detailType.toLowerCase()} format`, 'error');
        return;
      }
      
      // Create data object
      const outreachData = {
        ...formData,
        record_id: recordId,
        outreach_date: formData.outreach_date.toISOString(),
        followup_date: formData.followup_date ? formData.followup_date.toISOString() : null,
        meeting_date: formData.meeting_date ? formData.meeting_date.toISOString() : null
      };
      
      let response;
      if (editMode && currentEditId) {
        // Update existing record
        response = await api.updateOutreach(currentEditId, outreachData);
      } else {
        // Create new record
        response = await api.createOutreach(outreachData);
      }
      
      if (response.success) {
        // Show success message
        showNotification(
          editMode 
            ? 'Outreach record updated successfully!' 
            : 'New outreach record created successfully!', 
          'success'
        );
        
        // Reset form and form state
        resetForm();
        
        // Reload outreaches to show the new record
        await loadOutreaches();
        
        setStatsRefreshKey((key) => key + 1);
        
        // Call the callback if provided
        if (onOutreachUpdate) {
          logger.log('SalesOutreach: Calling onOutreachUpdate callback after submit');
          onOutreachUpdate();
        }
      } else {
        // Show error message
        showNotification(response.message || 'Failed to save outreach record', 'error');
      }
    } catch (err) {
      logger.error('Error saving outreach record:', err);
      showNotification('Failed to save outreach record', 'error');
    }
  };
  
  // Handle record deletion
  const handleDelete = async (id) => {
    try {
      if (!window.confirm('Are you sure you want to delete this outreach record? This cannot be undone.')) {
        return;
      }
      
      const response = await api.deleteOutreach(id);
      
      if (response.success) {
        showNotification('Outreach record deleted successfully!', 'success');
        
        // Remove from local state
        setOutreaches(prevOutreaches => prevOutreaches.filter(o => o.id !== id));
        
        setStatsRefreshKey((key) => key + 1);
        
        // Call the callback if provided
        if (onOutreachUpdate) {
          logger.log('SalesOutreach: Calling onOutreachUpdate callback after delete');
          onOutreachUpdate();
        }
      } else {
        showNotification(response.message || 'Failed to delete outreach record', 'error');
      }
    } catch (err) {
      logger.error('Error deleting outreach record:', err);
      showNotification('Failed to delete outreach record', 'error');
    }
  };
  
  // Handle edit button click
  const handleEdit = (outreach) => {
    setFormData({
      contact_name: outreach.contact_name || '',
      outreach_method: outreach.outreach_method || 'phone',
      contact_detail: outreach.contact_detail || '',
      outreach_date: outreach.outreach_date ? new Date(outreach.outreach_date) : new Date(),
      reached_intended_contact: outreach.reached_intended_contact || false,
      followup_date: outreach.followup_date ? new Date(outreach.followup_date) : null,
      meeting_scheduled: outreach.meeting_scheduled || false,
      meeting_date: outreach.meeting_date ? new Date(outreach.meeting_date) : null,
      notes: outreach.notes || '',
      completed: outreach.completed || false
    });
    setEditMode(true);
    setCurrentEditId(outreach.id);
    setShowForm(true);
  };
  
  // Function to find all phone numbers in the contactInfo object
  const findContactPhones = () => {
    if (!contactInfo) return [];
    
    // If contactInfo is a string, check if it's a phone number
    if (typeof contactInfo === 'string') {
      // If it looks like a phone number, return it as the only option
      if (validatePhone(contactInfo)) {
        logger.log('Using contactInfo string as phone number:', contactInfo);
        return [{
          number: contactInfo,
          label: 'Phone',
          field: 'contact_detail',
          priority: 1
        }];
      }
      return [];
    }
    
    // If contactInfo is an object, proceed as before
    const phones = [];
    
    // Check for common phone field names
    const possiblePhoneFields = [
      {field: 'Telefoonnummer Mobiel', label: 'Mobile Phone', priority: 1},
      {field: 'Mobile', label: 'Mobile Phone', priority: 1},
      {field: 'MobilePhone', label: 'Mobile Phone', priority: 1},
      {field: 'CellPhone', label: 'Mobile Phone', priority: 1},
      {field: 'mobile', label: 'Mobile Phone', priority: 1},
      {field: 'mobilePhone', label: 'Mobile Phone', priority: 1},
      {field: 'cell_phone', label: 'Mobile Phone', priority: 1},
      {field: 'mobile_phone', label: 'Mobile Phone', priority: 1},
      {field: 'Telefoon Mobiel', label: 'Mobile Phone', priority: 1},
      
      {field: 'Telefoonnummer', label: 'Office Phone', priority: 2},
      {field: 'Phone', label: 'Office Phone', priority: 2},
      {field: 'Telephone', label: 'Office Phone', priority: 2},
      {field: 'Tel', label: 'Office Phone', priority: 2},
      {field: 'phone', label: 'Office Phone', priority: 2},
      {field: 'telephone', label: 'Office Phone', priority: 2},
      {field: 'tel', label: 'Office Phone', priority: 2},
      {field: 'PhoneNumber', label: 'Office Phone', priority: 2},
      {field: 'phoneNumber', label: 'Office Phone', priority: 2},
      {field: 'phone_number', label: 'Office Phone', priority: 2},
      
      {field: 'DirectPhone', label: 'Direct Phone', priority: 3},
      {field: 'directPhone', label: 'Direct Phone', priority: 3},
      {field: 'direct_phone', label: 'Direct Phone', priority: 3},
      
      {field: 'HomePhone', label: 'Home Phone', priority: 4},
      {field: 'homePhone', label: 'Home Phone', priority: 4},
      {field: 'home_phone', label: 'Home Phone', priority: 4},
      
      {field: 'BusinessPhone', label: 'Business Phone', priority: 5},
      {field: 'businessPhone', label: 'Business Phone', priority: 5},
      {field: 'business_phone', label: 'Business Phone', priority: 5}
    ];
    
    // Check all possible phone fields
    for (const item of possiblePhoneFields) {
      if (contactInfo[item.field] && 
          contactInfo[item.field] !== 'null' && 
          validatePhone(contactInfo[item.field])) {
        phones.push({
          number: contactInfo[item.field],
          label: item.label,
          field: item.field,
          priority: item.priority
        });
      }
    }
    
    // Check if there are any other fields that might contain a phone number
    for (const key in contactInfo) {
      // Skip fields we've already checked
      if (possiblePhoneFields.some(item => item.field === key)) {
        continue;
      }
      
      if ((key.toLowerCase().includes('phone') || 
           key.toLowerCase().includes('tel') || 
           key.toLowerCase().includes('nummer') ||
           key.toLowerCase().includes('telefoon')) && 
          typeof contactInfo[key] === 'string' && 
          contactInfo[key] !== 'null' && 
          validatePhone(contactInfo[key])) {
        
        // Try to determine the type of phone number from the field name
        let label = 'Phone';
        let priority = 10;
        
        if (key.toLowerCase().includes('mob') || key.toLowerCase().includes('cell')) {
          label = 'Mobile Phone';
          priority = 1;
        } else if (key.toLowerCase().includes('direct')) {
          label = 'Direct Phone';
          priority = 3;
        } else if (key.toLowerCase().includes('home')) {
          label = 'Home Phone';
          priority = 4;
        } else if (key.toLowerCase().includes('business')) {
          label = 'Business Phone';
          priority = 5;
        } else if (key.toLowerCase().includes('office')) {
          label = 'Office Phone';
          priority = 2;
        }
        
        phones.push({
          number: contactInfo[key],
          label,
          field: key,
          priority
        });
      }
    }
    
    // Remove duplicates (same number, different fields)
    const uniquePhones = phones.filter((phone, index, self) => 
      index === self.findIndex(p => p.number === phone.number)
    );
    
    // Sort by priority (lower number = higher priority)
    uniquePhones.sort((a, b) => a.priority - b.priority);
    
    logger.log('Found phone numbers:', uniquePhones);
    return uniquePhones;
  };
  
  // Function to find a phone number in the contactInfo object (for backward compatibility)
  const findContactPhone = () => {
    const phones = findContactPhones();
    return phones.length > 0 ? phones[0].number : '';
  };
  
  // Function to find a contact name in the contactInfo
  const findContactName = () => {
    if (!contactInfo) return '';

    // If contactInfo is a string, it's not a name so return default
    if (typeof contactInfo === 'string') {
      return `Contact at ${companyName || 'Unknown Company'}`;
    }

    // Handle specific CP fields first (based on the example data)
    if (contactInfo['CP Voorletters Verantwoordelijk Bedrijfsvoering'] && 
        contactInfo['CP Achternaam Verantwoordelijk Bedrijfsvoering']) {
      
      const voorletters = contactInfo['CP Voorletters Verantwoordelijk Bedrijfsvoering'];
      const tussenvoegsel = contactInfo['CP Tussenvoegsel Verantwoordelijk Bedrijfsvoering'] || '';
      const achternaam = contactInfo['CP Achternaam Verantwoordelijk Bedrijfsvoering'];
      const titel = contactInfo['CP Titel Verantwoordelijk Bedrijfsvoering'] || '';
      
      // Format name based on Dutch naming conventions
      const formattedName = [
        titel, 
        voorletters, 
        tussenvoegsel, 
        achternaam
      ].filter(part => part && part !== 'null').join(' ').trim();
      
      if (formattedName) {
        logger.log(`Found contact name from CP fields: ${formattedName}`);
        return formattedName;
      }
    }
    
    // Then check for common name fields
    const possibleNameFields = [
      'ContactName', 'contactName', 'contact_name',
      'FullName', 'fullName', 'full_name',
      'Name', 'name',
      'Contact', 'contact',
      'FirstName', 'firstName', 'first_name',
      'PersonName', 'personName', 'person_name',
      'CP Naam', 'CPNaam', 'cp_naam'
    ];
    
    for (const field of possibleNameFields) {
      if (contactInfo[field] && contactInfo[field] !== 'null' && typeof contactInfo[field] === 'string') {
        logger.log(`Found contact name in ${field}: ${contactInfo[field]}`);
        return contactInfo[field];
      }
    }
    
    // If we have first name and last name separately
    if (contactInfo.FirstName && contactInfo.LastName) {
      return `${contactInfo.FirstName} ${contactInfo.LastName}`;
    }
    
    if (contactInfo.firstName && contactInfo.lastName) {
      return `${contactInfo.firstName} ${contactInfo.lastName}`;
    }
    
    if (contactInfo.first_name && contactInfo.last_name) {
      return `${contactInfo.first_name} ${contactInfo.last_name}`;
    }
    
    // If no contact person is found, use company name as a fallback
    if (contactInfo.Instellingsnaam) {
      logger.log('No contact person found, using company name instead');
      return `Contact at ${contactInfo.Instellingsnaam}`;
    }
    
    if (contactInfo.company_name || contactInfo.CompanyName || contactInfo.companyName) {
      const companyName = contactInfo.company_name || contactInfo.CompanyName || contactInfo.companyName;
      return `Contact at ${companyName}`;
    }
    
    return '';
  };
  
  // Function to find an email in the contactInfo object
  const findContactEmail = () => {
    if (!contactInfo) return '';
    
    // If contactInfo is a string, check if it's an email
    if (typeof contactInfo === 'string') {
      if (validateEmail(contactInfo)) {
        logger.log(`Found email from contactInfo string: ${contactInfo}`);
        return contactInfo;
      }
      return '';
    }
    
    // Check for common email field names
    const possibleEmailFields = [
      'Email', 'email', 'EmailAddress', 'emailAddress', 'email_address',
      'Mail', 'mail', 'MailAddress', 'mailAddress', 'mail_address',
      'ContactEmail', 'contactEmail', 'contact_email',
      'WorkEmail', 'workEmail', 'work_email',
      'BusinessEmail', 'businessEmail', 'business_email',
      'CP Email', 'CPEmail', 'cp_email',
      'E-mail', 'e-mail', 'E-mailadres', 'e-mailadres'
    ];
    
    for (const field of possibleEmailFields) {
      if (contactInfo[field] && contactInfo[field] !== 'null' && validateEmail(contactInfo[field])) {
        logger.log(`Found email in ${field}: ${contactInfo[field]}`);
        return contactInfo[field];
      }
    }
    
    // Check if there are any other fields that might contain an email
    for (const key in contactInfo) {
      if (
        (key.toLowerCase().includes('email') || 
         key.toLowerCase().includes('mail') || 
         key.toLowerCase().includes('e-mail')) && 
        typeof contactInfo[key] === 'string' && 
        contactInfo[key] !== 'null' && 
        validateEmail(contactInfo[key])
      ) {
        logger.log(`Found email in ${key}: ${contactInfo[key]}`);
        return contactInfo[key];
      }
    }
    
    // Generate an email based on domain if we have a URL
    if (contactInfo.URL || contactInfo.url || contactInfo.Website || contactInfo.website) {
      const url = contactInfo.URL || contactInfo.url || contactInfo.Website || contactInfo.website;
      if (url && typeof url === 'string') {
        try {
          // Extract domain from URL
          let domain = url;
          if (url.startsWith('http')) {
            const urlObj = new URL(url);
            domain = urlObj.hostname;
          } else if (url.startsWith('www.')) {
            domain = url;
          }
          
          // Remove www. if present
          domain = domain.replace(/^www\./, '');
          
          // Get contact name or initials for email
          const contactName = findContactName();
          if (contactName && !contactName.startsWith('Contact at ')) {
            // Try to create a business email format (first initial + last name)
            const nameParts = contactName.split(' ');
            if (nameParts.length > 1) {
              const firstName = nameParts[0];
              const lastName = nameParts[nameParts.length - 1];
              
              // Create email suggestion (not returning as a definite match)
              logger.log(`Suggesting potential email: ${firstName.charAt(0).toLowerCase()}${lastName.toLowerCase()}@${domain}`);
            }
          }
        } catch (error) {
          logger.error('Error generating email suggestion:', error);
        }
      }
    }
    
    return '';
  };

  // Function to find a LinkedIn URL in the contactInfo object
  const findContactLinkedIn = () => {
    if (!contactInfo) return '';
    
    // If contactInfo is a string, check if it's a LinkedIn URL
    if (typeof contactInfo === 'string') {
      if (validateLinkedIn(contactInfo)) {
        logger.log(`Found valid LinkedIn URL from contactInfo string: ${contactInfo}`);
        return contactInfo;
      }
      return '';
    }
    
    // Check for common LinkedIn field names
    const possibleLinkedInFields = [
      'LinkedIn', 'linkedin', 'LinkedInURL', 'linkedinURL', 'linkedin_url',
      'LinkedInProfile', 'linkedinProfile', 'linkedin_profile',
      'SocialLinkedIn', 'socialLinkedIn', 'social_linkedin',
      'LinkedIn Link'
    ];
    
    for (const field of possibleLinkedInFields) {
      if (contactInfo[field] && contactInfo[field] !== 'null' && typeof contactInfo[field] === 'string') {
        // Format LinkedIn URL if needed
        let linkedInUrl = contactInfo[field];
        
        // Check if it's a valid LinkedIn URL
        if (validateLinkedIn(linkedInUrl)) {
          logger.log(`Found valid LinkedIn URL in ${field}: ${linkedInUrl}`);
          return linkedInUrl;
        }
        
        // Try to fix common LinkedIn URL format issues
        if (!linkedInUrl.includes('linkedin.com')) {
          if (linkedInUrl.startsWith('company/') || linkedInUrl.startsWith('in/')) {
            linkedInUrl = `https://www.linkedin.com/${linkedInUrl}`;
          } else {
            linkedInUrl = `https://www.linkedin.com/company/${linkedInUrl}`;
          }
        } else if (!linkedInUrl.startsWith('http')) {
          linkedInUrl = `https://${linkedInUrl}`;
        }
        
        // Re-validate the fixed URL
        if (validateLinkedIn(linkedInUrl)) {
          logger.log(`Fixed LinkedIn URL from ${field}: ${linkedInUrl}`);
          return linkedInUrl;
        }
        
        logger.log(`Found LinkedIn field but URL is invalid: ${linkedInUrl}`);
      }
    }
    
    // Check if there are any other fields that might contain a LinkedIn URL
    for (const key in contactInfo) {
      if (key.toLowerCase().includes('linkedin') && 
          typeof contactInfo[key] === 'string' && 
          contactInfo[key] !== 'null') {
        
        let linkedInUrl = contactInfo[key];
        
        // Check if it's a valid LinkedIn URL
        if (validateLinkedIn(linkedInUrl)) {
          logger.log(`Found valid LinkedIn URL in ${key}: ${linkedInUrl}`);
          return linkedInUrl;
        }
        
        // Try to fix common LinkedIn URL format issues
        if (!linkedInUrl.includes('linkedin.com')) {
          if (linkedInUrl.startsWith('company/') || linkedInUrl.startsWith('in/')) {
            linkedInUrl = `https://www.linkedin.com/${linkedInUrl}`;
          } else {
            linkedInUrl = `https://www.linkedin.com/company/${linkedInUrl}`;
          }
        } else if (!linkedInUrl.startsWith('http')) {
          linkedInUrl = `https://${linkedInUrl}`;
        }
        
        // Re-validate the fixed URL
        if (validateLinkedIn(linkedInUrl)) {
          logger.log(`Fixed LinkedIn URL from ${key}: ${linkedInUrl}`);
          return linkedInUrl;
        }
      }
    }
    
    return '';
  };
  
  // Handle form change with validation feedback
  const handleFormChange = (field, value) => {
    // Update the form data
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // If changing the outreach method, try to fill in the appropriate contact detail
    if (field === 'outreach_method') {
      let contactDetail = '';
      
      // Try to auto-fill the appropriate contact detail based on the method
      switch(value) {
        case 'phone':
          contactDetail = findContactPhone();
          break;
        case 'email':
          contactDetail = findContactEmail();
          break;
        case 'linkedin':
          contactDetail = findContactLinkedIn();
          break;
        default:
          break;
      }
      
      setFormData(prev => ({
        ...prev,
        contact_detail: contactDetail
      }));
      
      setValidationError('');
      return;
    }
    
    // Validate contact detail as user types
    if (field === 'contact_detail' && value) {
      const isValid = validateContactDetail(value, formData.outreach_method);
      if (!isValid) {
        const detailType = getContactDetailLabel(formData.outreach_method);
        setValidationError(`Invalid ${detailType.toLowerCase()} format`);
      } else {
        setValidationError('');
      }
    } else if (field === 'contact_detail' && !value) {
      setValidationError('');
    }
  };
  
  // Handle form reset
  const resetForm = () => {
    // If in edit mode, reset to empty fields when closing the form
    if (editMode) {
      setFormData({
        contact_name: '',
        outreach_method: 'phone',
        contact_detail: '',
        outreach_date: new Date(),
        reached_intended_contact: false,
        followup_date: null,
        meeting_scheduled: false,
        meeting_date: null,
        notes: '',
        completed: false
      });
    } else {
      // Get available phone numbers
      const phones = findContactPhones();
      const defaultPhone = phones.length > 0 ? phones[0].number : '';
      
      // For a new form, try to prefill with contact information
      setFormData({
        contact_name: findContactName() || '',
        outreach_method: 'phone',
        contact_detail: defaultPhone,
        outreach_date: new Date(),
        reached_intended_contact: false,
        followup_date: null,
        meeting_scheduled: false,
        meeting_date: null,
        notes: '',
        completed: false
      });
    }
    
    // Reset form state
    setEditMode(false);
    setFollowUpMode(false);
    setCurrentEditId(null);
    setShowForm(false);
    setValidationError('');
  };

  // Function to open form for adding a new outreach
  const handleAddNewOutreach = () => {
    // Get available phone numbers
    const phones = findContactPhones();
    const defaultPhone = phones.length > 0 ? phones[0].number : '';
    
    // Set up form data for a new outreach
    setFormData({
      contact_name: findContactName() || '',
      outreach_method: 'phone',
      contact_detail: defaultPhone,
      outreach_date: new Date(),
      reached_intended_contact: false,
      followup_date: null,
      meeting_scheduled: false,
      meeting_date: null,
      notes: '',
      completed: false
    });
    
    // Reset form state
    setEditMode(false);
    setFollowUpMode(false);
    setCurrentEditId(null);
    setValidationError('');
    setShowForm(true);
  };
  
  // Helper function to show notifications
  const showNotification = (message, severity = 'info') => {
    setNotification({
      open: true,
      message,
      severity
    });
  };
  
  // Helper function to close notifications
  const handleCloseNotification = () => {
    setNotification(prev => ({
      ...prev,
      open: false
    }));
  };
  
  // Helper function to render method icon
  const renderMethodIcon = (method) => {
    switch (method) {
      case 'phone':
        return <PhoneIcon fontSize="small" color="primary" />;
      case 'email':
        return <EmailIcon fontSize="small" color="primary" />;
      case 'linkedin':
        return <LinkedInIcon fontSize="small" color="primary" />;
      default:
        return null;
    }
  };
  
  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM d, yyyy h:mm a');
    } catch (err) {
      logger.error('Error formatting date:', err);
      return dateString;
    }
  };
  
  // DNS-related utility functions
  // Extract domain from website URL
  const extractDomain = (url) => {
    if (!url) return null;
    
    try {
      // Remove protocol, www, and path
      let domain;
      
      // Try to use URL parsing if possible
      try {
        const urlObj = new URL(url);
        domain = urlObj.hostname;
      } catch (e) {
        // Fallback to manual parsing if URL is invalid
        domain = url.replace(/^https?:\/\//, '').replace(/^www\./, '');
        domain = domain.split('/')[0];
      }
      
      // Clean up domain: remove www. prefix and any trailing dots
      domain = domain.replace(/^www\./, '').replace(/\.$/, '');
      
      // Make sure it's a valid domain (contains at least one dot)
      if (!domain.includes('.')) {
        logger.log(`Invalid domain: ${domain}`);
        return null;
      }
      
      logger.log(`Extracted domain: ${domain} from URL: ${url}`);
      return domain;
    } catch (e) {
      logger.error(`Error extracting domain from ${url}:`, e);
      return null;
    }
  };
  
  // Get email provider icon and tooltip based on DNS data
  const getEmailProviderInfo = (dnsData) => {
    // Default values if no DNS data is available
    const defaultInfo = {
      iconType: 'question',
      tooltip: 'Unknown email provider',
      color: '#888'
    };
    
    // If no DNS data or no MX records, return default
    if (!dnsData || !dnsData.email || !dnsData.email.provider) {
      return defaultInfo;
    }
    
    // Format the MX records for the tooltip
    const mxRecordsText = dnsData.email.records && dnsData.email.records.length > 0
      ? `MX Records:\n${dnsData.email.records.join('\n')}`
      : 'No MX records found';
    
    // Get info based on provider
    switch (dnsData.email.provider) {
      case 'M365':
        return {
          iconType: 'microsoft',
          tooltip: `Microsoft 365\n${mxRecordsText}`,
          color: '#0078d4'
        };
      
      case 'M365_Filtered':
        return {
          iconType: 'microsoftFiltered',
          tooltip: `Microsoft 365 (Through Mail Filter)\nDetected by SPF record but MX does not point to Microsoft\n${mxRecordsText}`,
          color: '#8661C5',
          isFiltered: true
        };
        
      case 'Google':
        return {
          iconType: 'google',
          tooltip: `Google Workspace\n${mxRecordsText}`,
          color: '#4285F4'
        };
        
      case 'Hybrid':
        return {
          iconType: 'hybrid',
          tooltip: `Hybrid (Microsoft & Google)\n${mxRecordsText}`,
          color: 'purple'
        };
        
      case 'Other':
        return {
          iconType: 'email',
          tooltip: `Other Email Provider\n${mxRecordsText}`,
          color: '#FF9800'
        };
        
      default:
        return {
          iconType: 'question',
          tooltip: `Unknown (${dnsData.email.provider})\n${mxRecordsText}`,
          color: '#888'
        };
    }
  };
  
  // Render email provider icon based on iconType
  const renderEmailProviderIcon = (info) => {
    switch (info.iconType) {
      case 'microsoft':
        return <MicrosoftIcon fontSize="small" sx={{ color: info.color }} />;
      case 'microsoftFiltered':
        return <MicrosoftIcon fontSize="small" sx={{ color: info.color }} />;
      case 'google':
        return <GoogleIcon fontSize="small" sx={{ color: info.color }} />;
      case 'hybrid':
        return (
          <>
            <MicrosoftIcon fontSize="small" sx={{ color: '#0078d4', mr: 0.5 }} />
            <GoogleIcon fontSize="small" sx={{ color: '#4285F4' }} />
          </>
        );
      case 'email':
        return <EmailIcon fontSize="small" sx={{ color: info.color }} />;
      case 'question':
      default:
        return <QuestionMarkIcon fontSize="small" sx={{ color: info.color }} />;
    }
  };

  // Get enterprise/MDM info based on DNS data
  const getEnterpriseInfo = (dnsData) => {
    if (!dnsData || !dnsData.enterprise) {
      return {
        found: false,
        iconType: null,
        tooltip: 'No Enterprise enrollment information',
        records: []
      };
    }
    
    const hasEnterprise = dnsData.enterprise.found;
    const records = dnsData.enterprise.records || [];
    
    // Format records for the tooltip
    const recordText = records.length > 0
      ? `Enterprise Records:\n${records.join('\n')}`
      : 'No enterprise records found';
    
    return {
      found: hasEnterprise,
      iconType: hasEnterprise ? 'devices' : null,
      tooltip: hasEnterprise ? 
        `Microsoft Intune (MDM) Configured\n${recordText}` : 
        'No Microsoft Intune (MDM) detected',
      records: records
    };
  };

  // Get DMARC security status and icon
  const getDmarcSecurityInfo = (dnsData) => {
    if (!dnsData || !dnsData.dmarc) {
      return {
        found: false,
        iconType: 'bad',
        tooltip: 'DMARC not configured',
        color: '#d32f2f',
        status: 'missing'
      };
    }
    
    const hasDmarc = dnsData.dmarc.found;
    const policy = dnsData.dmarc.policy || 'none';
    const record = dnsData.dmarc.record || '';
    
    // If DMARC is not configured
    if (!hasDmarc) {
      return {
        found: false,
        iconType: 'bad',
        tooltip: 'DMARC not configured - emails not protected',
        color: '#d32f2f',
        status: 'missing'
      };
    }
    
    // Based on policy strength
    switch (policy.toLowerCase()) {
      case 'reject':
        return {
          found: true,
          iconType: 'good',
          tooltip: `DMARC Policy: reject\n${record}\n\nHighest protection: Unauthorized emails will be rejected`,
          color: '#4caf50',
          status: 'strong'
        };
        
      case 'quarantine':
        return {
          found: true,
          iconType: 'maybe',
          tooltip: `DMARC Policy: quarantine\n${record}\n\nMedium protection: Unauthorized emails will be quarantined`,
          color: '#ff9800',
          status: 'medium'
        };
        
      case 'none':
      default:
        return {
          found: true,
          iconType: 'bad',
          tooltip: `DMARC Policy: none\n${record}\n\nWeak protection: Only monitoring, no action taken on unauthorized emails`,
          color: '#d32f2f',
          status: 'weak'
        };
    }
  };

  // Get SPF security status and icon
  const getSpfSecurityInfo = (dnsData) => {
    if (!dnsData || !dnsData.spf) {
      return {
        found: false,
        iconType: 'shield',
        tooltip: 'SPF not configured',
        color: '#d32f2f'
      };
    }
    
    const hasSpf = dnsData.spf.found;
    const record = dnsData.spf.record || '';
    
    if (!hasSpf) {
      return {
        found: false,
        iconType: 'shield',
        tooltip: 'SPF not configured - susceptible to email spoofing',
        color: '#d32f2f'
      };
    }
    
    return {
      found: true,
      iconType: 'shield',
      tooltip: `SPF configured\n${record}`,
      color: '#4caf50'
    };
  };

  // Get DKIM security status and icon
  const getDkimSecurityInfo = (dnsData) => {
    if (!dnsData || !dnsData.dkim) {
      return {
        found: false,
        iconType: 'markEmail',
        tooltip: 'DKIM not configured',
        color: '#d32f2f'
      };
    }
    
    const hasDkim = dnsData.dkim.found;
    const selectors = dnsData.dkim.selectors || [];
    
    if (!hasDkim) {
      return {
        found: false,
        iconType: 'markEmail',
        tooltip: 'DKIM not configured - email integrity not verified',
        color: '#d32f2f'
      };
    }
    
    return {
      found: true,
      iconType: 'markEmail',
      tooltip: `DKIM configured\nSelectors: ${selectors.join(', ')}`,
      color: '#4caf50'
    };
  };
  
  // Render security icon based on iconType
  const renderSecurityIcon = (info) => {
    if (!info) return null;
    
    switch (info.iconType) {
      case 'good':
        return <GppGoodIcon fontSize="small" sx={{ color: info.color }} />;
      case 'maybe':
        return <GppMaybeIcon fontSize="small" sx={{ color: info.color }} />;
      case 'bad':
        return <GppBadIcon fontSize="small" sx={{ color: info.color }} />;
      case 'shield':
        return <ShieldIcon fontSize="small" sx={{ color: info.color }} />;
      case 'markEmail':
        return <MarkEmailReadIcon fontSize="small" sx={{ color: info.color }} />;
      case 'devices':
        return <DevicesIcon fontSize="small" sx={{ color: info.color || '#107C10' }} />;
      default:
        return null;
    }
  };
  
  // Helper function to check if there are recent changes for a security type
  const hasRecentSecurityChanges = (securityType) => {
    if (!securityChanges || !securityChanges.changes_by_type) return false;
    return securityChanges.changes_by_type[securityType]?.has_recent_changes || false;
  };
  
  // Helper function to get the most recent change for a security type
  const getRecentSecurityChange = (securityType) => {
    if (!securityChanges || !securityChanges.changes_by_type) return null;
    return securityChanges.changes_by_type[securityType]?.latest_change || null;
  };
  
  // Helper function to determine if a change is positive, negative, or neutral
  const getChangeDirection = (securityType) => {
    const change = getRecentSecurityChange(securityType);
    if (!change) return 'neutral';
    
    const summary = change.change_summary?.toLowerCase() || '';
    const description = change.change_description?.toLowerCase() || '';
    
    // Analyze change for direction based on type and content
    switch (securityType) {
      case 'spf':
        if (summary.includes('added') || summary.includes('configured') || description.includes('spf record added')) {
          return 'positive'; // SPF record added is good
        }
        if (summary.includes('removed') || summary.includes('deleted') || description.includes('spf record removed')) {
          return 'negative'; // SPF record removed is bad
        }
        return 'neutral'; // SPF record modified
        
      case 'dkim':
        if (summary.includes('added') || summary.includes('configured')) {
          return 'positive'; // DKIM added is good
        }
        if (summary.includes('removed') || summary.includes('deleted')) {
          return 'negative'; // DKIM removed is bad
        }
        // Check selector count changes
        const selectorMatch = summary.match(/(\d+)\s*→\s*(\d+)\s*selectors?/);
        if (selectorMatch) {
          const oldCount = parseInt(selectorMatch[1]);
          const newCount = parseInt(selectorMatch[2]);
          if (newCount > oldCount) return 'positive'; // More selectors is good
          if (newCount < oldCount) return 'negative'; // Fewer selectors is bad
        }
        return 'neutral';
        
      case 'dmarc':
        if (summary.includes('added') || summary.includes('configured') || description.includes('dmarc record added')) {
          return 'positive'; // DMARC record added is good
        }
        if (summary.includes('removed') || summary.includes('deleted') || description.includes('dmarc record removed')) {
          return 'negative'; // DMARC record removed is bad
        }
        
        // Check policy changes
        if (summary.includes('policy changed')) {
          // Look for policy direction in summary
          if (summary.includes('none to reject') || summary.includes('none to quarantine') || 
              summary.includes('quarantine to reject')) {
            return 'positive'; // Stronger policy is good
          }
          if (summary.includes('reject to none') || summary.includes('quarantine to none') ||
              summary.includes('reject to quarantine')) {
            return 'negative'; // Weaker policy is bad
          }
        }
        return 'neutral';
        
      default:
        return 'neutral';
    }
  };
  
  // Helper function to render change direction icon
  const renderChangeDirectionIcon = (securityType) => {
    if (!hasRecentSecurityChanges(securityType)) return null;
    
    const direction = getChangeDirection(securityType);
    const iconStyle = { 
      fontSize: '12px', 
      fontWeight: 'bold',
      ml: 0.5
    };
    
    switch (direction) {
      case 'positive':
        return <ArrowUpwardIcon sx={iconStyle} />;
      case 'negative':
        return <ArrowDownwardIcon sx={iconStyle} />;
      case 'neutral':
      default:
        return <CircleIcon sx={iconStyle} />;
    }
  };
  
  // Helper function to format time ago
  const formatTimeAgo = (dateString) => {
    if (!dateString) return '';
    
    const now = new Date();
    const changeDate = new Date(dateString);
    const diffMs = now - changeDate;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffHours < 1) {
      return 'Less than 1 hour ago';
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    } else {
      return changeDate.toLocaleDateString();
    }
  };
  
  // Function to get contact detail label based on method
  const getContactDetailLabel = (method) => {
    switch(method) {
      case 'phone':
        return 'Phone Number';
      case 'email':
        return 'Email Address';
      case 'linkedin':
        return 'LinkedIn URL';
      default:
        return 'Contact Detail';
    }
  };
  
  // Function to get the appropriate input type based on method
  const getContactDetailInputType = (method) => {
    switch(method) {
      case 'phone':
        return 'tel';
      case 'email':
        return 'email';
      case 'linkedin':
        return 'url';
      default:
        return 'text';
    }
  };
  
  // Function to extract contact detail from notes if stored there
  const extractContactDetailFromNotes = (notes, method) => {
    if (!notes) return null;
    
    const detailType = getContactDetailLabel(method);
    const regex = new RegExp(`${detailType}:\\s*([^\\n]+)`);
    const match = notes.match(regex);
    
    return match ? match[1].trim() : null;
  };
  
  // Helper function to get contact detail from either field or notes (for backward compatibility)
  const getContactDetail = (outreach) => {
    // First try to get from contact_detail field
    if (outreach.contact_detail) {
      return outreach.contact_detail;
    }
    
    // For backward compatibility with old records, try to extract from notes
    return extractContactDetailFromNotes(outreach.notes, outreach.outreach_method);
  };

  const handleFollowUp = (outreach) => {
    setFormData({
      contact_name: outreach.contact_name || '',
      outreach_method: outreach.outreach_method || 'phone',
      contact_detail: getContactDetail(outreach) || '',
      outreach_date: new Date(),
      reached_intended_contact: false,
      followup_date: null,
      meeting_scheduled: false,
      meeting_date: null,
      notes: '',
      completed: false
    });
    setEditMode(false);
    setFollowUpMode(true);
    setCurrentEditId(null);
    setValidationError('');
    setShowForm(true);
  };

  // Function to get clean notes (no special logic needed anymore for new records)
  const getCleanNotes = (outreach) => {
    return outreach.notes || '';
  };
  
  // Handle opening the full research dialog
  const handleOpenResearch = () => {
    setResearchDialogOpen(true);
    setIsDialogOpen(true); // Mark dialog as open to pause reverse lookups
  };

  // Handle closing the research dialog
  const handleCloseResearch = () => {
    setResearchDialogOpen(false);
    setIsDialogOpen(false); // Mark dialog as closed to resume reverse lookups
    
    // Resume IP lookups if they were paused
    if (totalIpsToProcessRef.current > 0 && lookupProgress.current < totalIpsToProcessRef.current) {
      logger.log("Resuming paused IP lookups after dialog closed");
      setTimeout(() => {
        const remainingIps = pendingIps;
        if (remainingIps.length > 0 && !isProcessingQueueRef.current) {
          processBatchOfIps(remainingIps);
        }
      }, 300);
    }
  };
  
  // Handle opening the provider info dialog
  const handleOpenProviderInfo = (entry) => {
    setSelectedProvider(entry);
    setProviderInfoOpen(true);
    setIsDialogOpen(true); // Mark dialog as open to pause reverse lookups
    setProviderSearchResult(null); // Clear previous results
    searchProviderInfo(entry);
  };
  
  // Handle closing the provider info dialog
  const handleCloseProviderInfo = () => {
    setProviderInfoOpen(false);
    setIsDialogOpen(false); // Mark dialog as closed to resume reverse lookups
    setSelectedProvider(null);
    setProviderSearchResult(null);
    
    // Resume IP lookups if they were paused
    if (totalIpsToProcessRef.current > 0 && lookupProgress.current < totalIpsToProcessRef.current) {
      logger.log("Resuming paused IP lookups after dialog closed");
      setTimeout(() => {
        const remainingIps = pendingIps;
        if (remainingIps.length > 0 && !isProcessingQueueRef.current) {
          processBatchOfIps(remainingIps);
        }
      }, 300);
    }
  };
  
  // CompanyResearchDialog component updated to use the imported CompanyResearch component
  const CompanyResearchDialog = ({ open, onClose, record }) => {
    if (!open || !record) return null;
    
    // Format the record for CompanyResearch component
    const formattedRecord = {
      ...record,
      record_id: recordId,
      company_name: companyName || record.company_name || record.Instellingsnaam
    };
    
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: "90vh" }
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              <BusinessIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Company Research: {formattedRecord.company_name}
            </Typography>
            <IconButton edge="end" color="inherit" onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <CompanyResearch 
            selectedRecords={[formattedRecord]} 
            onClose={onClose}
          />
        </DialogContent>
      </Dialog>
    );
  };
  
  // ProviderInfoDialog component for displaying information about email providers

  // Memoize the ProviderInfoDialog component to prevent re-renders when parent state changes
  const ProviderInfoDialog = React.memo(({ open, onClose, provider, result, loading }) => {
    if (!open || !provider) return null;
    
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: 'primary.main', 
          color: 'white',
          pb: 1
        }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" sx={{ 
              display: 'flex', 
              alignItems: 'center',
              fontWeight: 'medium'
            }}>
              {provider.type === 'hostname' ? (
                <DnsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              ) : provider.type === 'verification' ? (
                <VerifiedUserIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              ) : (
                <NetworkCheckIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              )}
              {provider.provider || provider.displayName || provider.value}
              
              {/* Show category badge if available */}
              {provider.category && (
                <Chip 
                  size="small" 
                  label={provider.category} 
                  sx={{ 
                    ml: 2, 
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    height: 24
                  }} 
                />
              )}
            </Typography>
            <IconButton edge="end" color="inherit" onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          {loading ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              justifyContent: 'center', 
              p: 5,
              minHeight: '200px'
            }}>
              <CircularProgress size={40} sx={{ mb: 2 }} />
              <Typography variant="body1">
                Searching for information about {provider.displayName || provider.value}...
              </Typography>
            </Box>
          ) : result ? (
            <Box>
              <Typography variant="subtitle1" fontWeight="medium" gutterBottom sx={{ color: 'primary.main' }}>
                About {provider.provider || provider.displayName || provider.value}
              </Typography>
              
              {/* Display description from our backend if available */}
              {provider.description && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    {provider.description}
                  </Typography>
                </Alert>
              )}
              
              <Divider sx={{ mb: 2 }} />
              
              {/* Render markdown with react-markdown */}
              <Box sx={{ 
                '& a': { color: 'primary.main' },
                '& img': { maxWidth: '100%', height: 'auto' },
                '& pre': { 
                  bgcolor: 'background.paper', 
                  p: 2, 
                  borderRadius: 1,
                  overflowX: 'auto',
                  fontFamily: 'monospace'
                },
                '& code': { 
                  bgcolor: 'background.paper', 
                  px: 1, 
                  py: 0.5, 
                  borderRadius: 1,
                  fontFamily: 'monospace'
                },
                '& table': { 
                  borderCollapse: 'collapse',
                  width: '100%',
                  my: 2 
                },
                '& th, & td': { 
                  border: '1px solid #ddd',
                  p: 1
                },
                '& th': { 
                  bgcolor: 'background.paper',
                  fontWeight: 'bold' 
                },
                '& blockquote': {
                  borderLeft: '4px solid #ccc',
                  pl: 2,
                  ml: 0,
                  fontStyle: 'italic',
                  color: 'text.secondary',
                },
                '& h1, & h2, & h3, & h4, & h5, & h6': {
                  color: 'primary.main',
                  fontWeight: 'medium'
                }
              }}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {result}
                </ReactMarkdown>
              </Box>
              
              {provider.type === 'hostname' && provider.value && (
                <Box sx={{ 
                  mt: 4, 
                  pt: 2, 
                  px: 2,
                  pb: 2,
                  borderTop: '1px solid', 
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                  borderRadius: 1
                }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ color: 'primary.main' }}>
                    Domain Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Full Domain:</strong> {provider.value}
                      </Typography>
                    </Grid>
                    {provider.provider && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Service Provider:</strong> {provider.provider}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}
              
              {/* Show record info for verification records */}
              {provider.type === 'verification' && provider.value && (
                <Box sx={{ 
                  mt: 4, 
                  pt: 2, 
                  px: 2,
                  pb: 2,
                  borderTop: '1px solid', 
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                  borderRadius: 1
                }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ color: 'primary.main' }}>
                    Verification Record Details
                  </Typography>
                  <Grid container spacing={2}>
                    {provider.category && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Category:</strong> {provider.category}
                        </Typography>
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Record:</strong> {provider.value}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{ 
              p: 3, 
              textAlign: 'center',
              bgcolor: 'background.paper',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider'
            }}>
              <InfoIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
              <Typography variant="body1" color="text.secondary">
                No information available for this provider.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Try searching for it online or click the search button below.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {provider.value && (
            <Button 
              variant="outlined" 
              startIcon={<SearchIcon />} 
              onClick={() => window.open(`https://www.google.com/search?q=${encodeURIComponent(provider.value)}`, '_blank')}
            >
              Search on Google
            </Button>
          )}
          {provider.provider && provider.provider !== provider.value && (
            <Button 
              variant="outlined" 
              startIcon={<SearchIcon />} 
              onClick={() => window.open(`https://www.google.com/search?q=${encodeURIComponent(provider.provider)}+${provider.type === 'verification' ? 'domain+verification' : 'email+provider'}`, '_blank')}
            >
              More about {provider.provider}
            </Button>
          )}
          <Button variant="contained" onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  });
  
  // Effect to log DNS data when it changes
  useEffect(() => {
    if (dnsData) {
      logger.log("DNS data updated in state:", dnsData);
    }
  }, [dnsData]);
  
  // Effect to process any pending DNS lookups when forceUpdate changes
  useEffect(() => {
    if (forceUpdate > 0) {
      logger.log(`ForceUpdate triggered (${forceUpdate}), refreshing IP lookup data`);
      // The component will re-render with the latest lookup results
    }
  }, [forceUpdate]);
  
  // Function to get provider name for known domains
  const getProviderNameFromDomain = (domain) => {
    const lowerDomain = domain.toLowerCase();
    
    // Extract the core domain from a subdomain
    const extractCoreDomain = (domainStr) => {
      // Remove common prefixes
      let cleaned = domainStr;
      
      // Remove known email subdomain prefixes
      const knownPrefixes = ['_spf.', 'spf.', 'mail.', 'email.', 'mx.', 'smtp.', 'outbound.'];
      for (const prefix of knownPrefixes) {
        if (cleaned.startsWith(prefix)) {
          cleaned = cleaned.substring(prefix.length);
          break;
        }
      }
      
      return cleaned;
    };
    
    // Get the core domain without email-related prefixes
    const coreDomain = extractCoreDomain(lowerDomain);
    
    // Domain to company name mapping
    const domainToCompany = {
      // Microsoft domains
      'protection.outlook.com': 'Microsoft 365',
      'mail.protection.outlook.com': 'Microsoft 365',
      'spf.protection.outlook.com': 'Microsoft 365',
      'outlook.com': 'Microsoft 365',
      'office365.com': 'Microsoft 365',
      'microsoft.com': 'Microsoft 365',
      
      // Google domains
      'google.com': 'Google Workspace',
      '_spf.google.com': 'Google Workspace',
      'gmail.com': 'Google',
      'googlemail.com': 'Google',
      
      // Email security and filtering services
      'pphosted.com': 'Proofpoint',
      'proofpoint.com': 'Proofpoint',
      'mimecast.com': 'Mimecast',
      'barracuda.com': 'Barracuda',
      'barracudanetworks.com': 'Barracuda',
      'antispamcloud.com': 'AntiSpamCloud',
      'spamexperts.com': 'SpamExperts',
      'spamhero.com': 'SpamHero',
      'mailchannels.net': 'MailChannels',
      'messagelabs.com': 'Symantec',
      'trendmicro.com': 'Trend Micro',
      'forcepoint.com': 'Forcepoint',
      'appriver.com': 'AppRiver',
      'reflexion.net': 'Reflexion',
      'spamtitan.com': 'SpamTitan',
      'sophos.com': 'Sophos',
      'spamsolutions.net': 'SpamSolutions',
      'fireeyecloud.com': 'FireEye',
      'cloudfilter.net': 'CloudFilter',
      'hornetsecurity.com': 'Hornetsecurity',
      'spf2.email': 'SPF2',
      
      // Email service providers
      'sendgrid.net': 'SendGrid',
      'sendgrid.com': 'SendGrid',
      'mx.sendgrid.net': 'SendGrid',
      'amazonses.com': 'Amazon SES',
      'aws.amazon.com': 'Amazon SES',
      'zoho.com': 'Zoho Mail',
      'zoho.eu': 'Zoho Mail',
      'mandrillapp.com': 'Mandrill',
      'mandrill.com': 'Mandrill',
      'postmarkapp.com': 'Postmark',
      'sparkpost.com': 'SparkPost',
      'rpost.net': 'Rpost',
      'afas.online': 'AFAS',
      'evolveip.eu': 'Evolve IP',
      'mailgun.com': 'Mailgun',
      'mailgun.net': 'Mailgun',
      'exclaimer.net': 'Exclaimer',
      'exclaimer.com': 'Exclaimer',
      'emailsignatures365.com': 'CodeTwo',
      'yukiworks.nl': 'Yukiworks',
      'routit.net': 'RoutiT',
      'mailplatform.eu': 'MailPlatform',
      'mailplatform.nl': 'MailPlatform',
      'mailplatform.com': 'MailPlatform',
      'messagingengine.com': 'FastMail',
      'fastmail.com': 'FastMail',
      'one.com': 'One.com',
      'hetzner.com': 'Hetzner',
      'hostinger.com': 'Hostinger',
      'transip.email': 'TransIP',
      'mailbox.org': 'mailbox.org',
      'hostnet.nl': 'Hostnet',
      'strato.de': 'Strato',
      'strato.com': 'Strato',
      'hosterby.com': 'HosterBY',
      'yourhosting.nl': 'YourHosting',
      'pcextreme.nl': 'PCextreme',
      'webreus.nl': 'Webreus',
      'antagonist.nl': 'Antagonist',
      'openprovider.nl': 'Openprovider',
      'vevida.com': 'Vevida',
      'mijndomein.nl': 'MijnDomein',
      'hosting2go.nl': 'Hosting2GO',
      'webhostingserver.nl': 'WebhostingServer',
      'mijn.host': 'Mijn.Host',
      'easyhosting.nl': 'EasyHosting',
      'basenet.nl': 'Basenet',


      
      // Marketing and CRM platforms
      'servers.mcsv.net': 'Mailchimp',
      'mailchimp.com': 'Mailchimp',
      '_spf.salesforce.com': 'Salesforce',
      'salesforce.com': 'Salesforce',
      'zendesk.com': 'Zendesk',
      'hubspot.com': 'HubSpot',
      'hubspotemail.net': 'HubSpot',
      'marketo.com': 'Marketo',
      'mktdns.com': 'Marketo',
      'exacttarget.com': 'Salesforce Marketing Cloud',
      'et.e.sparkpost.com': 'Salesforce Marketing Cloud',
      'freshworks.com': 'Freshworks',
      'freshdesk.com': 'Freshdesk',
      'intercom.com': 'Intercom',
      'intercom.io': 'Intercom',
      'mailersend.com': 'MailerSend',
      'klaviyo.com': 'Klaviyo',
      'activecampaign.com': 'ActiveCampaign',
      'constantcontact.com': 'Constant Contact',
      'mailerlite.com': 'MailerLite',
      'ontramail.com': 'ONTRAPORT',
      'getresponse.com': 'GetResponse',
      'convertkit.com': 'ConvertKit',
      'autopilothq.com': 'Autopilot',
      'campaign-archive.com': 'Mailchimp'
    };
    
    // Check direct match for the core domain
    if (domainToCompany[coreDomain]) {
      return domainToCompany[coreDomain];
    }
    
    // Check if the core domain contains any of the known provider domains
    for (const [domainKey, providerName] of Object.entries(domainToCompany)) {
      if (coreDomain.includes(domainKey)) {
        return providerName;
      }
    }
    
    // If no match found for the specific domain, check if it might be a subdomain
    // Example: mail.zendesk.com -> Zendesk
    const parts = coreDomain.split('.');
    if (parts.length >= 3) {
      const baseDomain = `${parts[parts.length - 2]}.${parts[parts.length - 1]}`;
      const orgName = parts[parts.length - 2].charAt(0).toUpperCase() + parts[parts.length - 2].slice(1);
      
      // Check if the base domain is in our known domains mapping
      if (domainToCompany[baseDomain]) {
        return domainToCompany[baseDomain];
      }
      
      // Common email service providers that might be used with subdomains
      const commonEmailProviders = {
        'zendesk.com': 'Zendesk',
        'hubspot.com': 'HubSpot',
        'mailchimp.com': 'Mailchimp',
        'salesforce.com': 'Salesforce',
        'freshdesk.com': 'Freshdesk',
        'mailjet.com': 'Mailjet',
        'sendgrid.com': 'SendGrid'
      };
      
      if (commonEmailProviders[baseDomain]) {
        return commonEmailProviders[baseDomain];
      }
      
      // If not a known provider but still looks like a service
      // Return the capitalized name of the main domain
      return orgName;
    }
    
    // Return null if no match found
    return null;
  };
  
  // Well-known IP ranges for common services
  const ipRanges = {
    // Microsoft IP ranges (sample subset)
    "40.96.0.0/12": "Microsoft 365",
    "40.112.0.0/13": "Microsoft Azure",
    "40.120.0.0/14": "Microsoft",
    "40.124.0.0/16": "Microsoft",
    "40.125.0.0/17": "Microsoft",
    "40.80.0.0/12": "Microsoft",
    "40.64.0.0/10": "Microsoft",
    "52.96.0.0/12": "Microsoft",
    "52.112.0.0/14": "Microsoft",
    "104.40.0.0/13": "Microsoft Azure",
    "104.146.0.0/15": "Microsoft",
    "134.170.0.0/16": "Microsoft",
    "157.55.0.0/16": "Microsoft",
    "157.56.0.0/15": "Microsoft",
    
    // Google IP ranges (sample subset)
    "35.184.0.0/13": "Google Cloud",
    "35.192.0.0/13": "Google Cloud",
    "35.200.0.0/13": "Google Cloud",
    "35.208.0.0/12": "Google Cloud",
    "35.224.0.0/12": "Google Cloud",
    "35.240.0.0/13": "Google Cloud",
    "64.18.0.0/20": "Google",
    "64.233.160.0/19": "Google",
    "66.102.0.0/20": "Google",
    "66.249.80.0/20": "Google",
    "72.14.192.0/18": "Google",
    "74.125.0.0/16": "Google",
    "108.177.0.0/17": "Google",
    "173.194.0.0/16": "Google",
    "209.85.128.0/17": "Google",
    "216.58.192.0/19": "Google",
    "216.239.32.0/19": "Google",
    
    // Amazon SES and AWS IP ranges (sample subset)
    "3.208.0.0/12": "Amazon AWS",
    "3.224.0.0/12": "Amazon AWS",
    "23.20.0.0/14": "Amazon AWS",
    "35.168.0.0/13": "Amazon AWS",
    "35.176.0.0/13": "Amazon AWS",
    "52.0.0.0/12": "Amazon AWS",
    "52.16.0.0/13": "Amazon AWS",
    "52.32.0.0/13": "Amazon AWS",
    "52.48.0.0/13": "Amazon AWS",
    "52.64.0.0/12": "Amazon AWS",
    "52.95.0.0/16": "Amazon AWS",
    "54.144.0.0/12": "Amazon AWS",
    "54.160.0.0/12": "Amazon AWS",
    "54.192.0.0/12": "Amazon AWS",
    "54.208.0.0/13": "Amazon AWS",
    "54.216.0.0/14": "Amazon AWS",
    "54.220.0.0/15": "Amazon AWS",
    "54.239.0.0/17": "Amazon AWS",
    
    // ProofPoint
    "148.163.0.0/16": "ProofPoint",
    "159.183.0.0/16": "ProofPoint",
    "68.232.128.0/17": "ProofPoint",
    
    // Mimecast
    "205.139.110.0/24": "Mimecast",
    "91.220.42.0/24": "Mimecast",
    "170.10.0.0/17": "Mimecast",
    "207.82.80.0/24": "Mimecast",
    
    // SendGrid
    "167.89.0.0/17": "SendGrid",
    "208.117.48.0/20": "SendGrid",
    "50.31.32.0/19": "SendGrid",
    
    // Mailchimp/Mandrill
    "205.201.128.0/20": "Mailchimp",
    "198.2.128.0/18": "Mailchimp",
    "148.105.0.0/16": "Mailchimp",
    
    // Constant Contact
    "208.75.120.0/21": "Constant Contact",
    "63.251.135.0/24": "Constant Contact"
  };
  
  // Function to check if an IP belongs to a known range
  const getProviderForIp = (ip) => {
    // Helper function to check if IP is in range (simple implementation for common CIDR formats)
    const isIpInRange = (ip, cidr) => {
      const [rangeIp, bits] = cidr.split('/');
      const mask = bits ? ~(2 ** (32 - parseInt(bits)) - 1) : -1;
      
      // Convert IP to number
      const ipToNum = (ip) => {
        const parts = ip.split('.');
        return ((parseInt(parts[0], 10) << 24) |
                (parseInt(parts[1], 10) << 16) |
                (parseInt(parts[2], 10) << 8) |
                parseInt(parts[3], 10)) >>> 0;
      };
      
      const ipNum = ipToNum(ip);
      const rangeIpNum = ipToNum(rangeIp);
      
      return (ipNum & mask) === (rangeIpNum & mask);
    };
    
    // Check against our known ranges
    for (const [cidr, provider] of Object.entries(ipRanges)) {
      if (isIpInRange(ip, cidr)) {
        return provider;
      }
    }
    
    return null; // Not in a known range
  };
  
  // First, update the performReverseLookup function
  const performReverseLookup = async (ip) => {
    try {
      // First check if the IP is in a known range to avoid unnecessary lookups
      const knownProvider = getProviderForIp(ip);
      if (knownProvider) {
        logger.log(`Using known provider for ${ip}: ${knownProvider}`);
        const result = {
          hostname: `${knownProvider} (${ip})`,
          provider: knownProvider,
          reverseLookup: true
        };
        
        // Cache the result immediately to prevent future lookups for this IP
        reverseLookupResultsRef.current = { 
          ...reverseLookupResultsRef.current, 
          [ip]: result 
        };
        
        return result;
      }
      
      // Improved caching - Check if we have any result for this IP, including failed ones
      if (reverseLookupResultsRef.current[ip]) {
        logger.log(`Using cached result for ${ip}`);
        return reverseLookupResultsRef.current[ip];
      }
      
      logger.log(`Performing reverse DNS lookup for ${ip} using API client`);
      
      try {
        // Use the centralized API client instead of direct axios call with hardcoded URL
        const response = await api.reverseDnsLookup(ip);
        
        // Process successful response
        if (response && response.success && response.hostname) {
          const hostname = response.hostname;
          logger.log(`Found hostname for ${ip}: ${hostname}`);
          
          // Extract potential provider name from hostname
          const hostnameParts = hostname.split('.');
          let providerName = null;
          
          if (hostnameParts.length >= 2) {
            // Try to get the second level domain as provider
            const domain = hostnameParts.slice(-2).join('.');
            providerName = getProviderNameFromDomain(domain);
          }
          
          return {
            hostname: hostname,
            provider: providerName || hostname,
            reverseLookup: true
          };
        } else {
          logger.log(`No hostname found for ${ip} in API response`);
          return {
            hostname: null,
            provider: null,
            noHostname: true,
            reverseLookup: false
          };
        }
      } catch (apiError) {
        // Handle errors
        logger.error(`API error for ${ip}:`, apiError);
        
        // Mark as processed but failed
        return {
          hostname: null,
          provider: null,
          error: apiError.message || "Request failed",
          noHostname: true,
          reverseLookup: false
        };
      }
    } catch (error) {
      logger.error(`General error in lookup for ${ip}:`, error);
      return {
        hostname: null,
        provider: null,
        error: error.message,
        noHostname: true,
        reverseLookup: false
      };
    }
  };
  
  // Update the processBatchOfIps function to prevent infinite loops
  const processBatchOfIps = async (ips) => {
    if (ips.length === 0) return;
    
    // Don't process if a dialog is open to prevent UI flickering
    if (isDialogOpen) {
      logger.log('Skipping reverse lookup processing while dialog is open');
      isProcessingQueueRef.current = false;
      isProcessingQueue = false;
      return;
    }
    
    // Set processing flag to prevent multiple concurrent batches
    isProcessingQueueRef.current = true;
    isProcessingQueue = true; // Update local var too
    
    // Determine if this is a large batch (more than 5 IPs)
    const isLargeBatch = ips.length > 5;
    
    // Adjust parameters based on batch size - use smaller batches with increased delay to prevent proxy errors
    const BATCH_SIZE = isLargeBatch ? 2 : 1; // Process max 2 IPs at once for larger batches (reduced from 3)
    const MAX_ATTEMPTS = 1; // Single attempt for all lookups
    const DELAY_BETWEEN_REQUESTS = isLargeBatch ? 600 : 800; // Longer delay for large batches (increased from 300ms)
    
    logger.log(`Processing batch of ${ips.length} IPs (max ${BATCH_SIZE} at once)`);
    
    // De-duplicate IPs
    const uniqueIps = [...new Set(ips)];
    logger.log(`After deduplication: ${uniqueIps.length} unique IPs`);
    
    // Filter out IPs that have already been processed or have failed
    const filteredBatch = uniqueIps.filter(ip => {
      // Skip if this IP already has a result (successful or failed)
      if (reverseLookupResultsRef.current[ip]) {
        logger.log(`Skipping already processed IP: ${ip}`);
        return false;
      }
      
      // Skip if we've already tried this IP MAX_ATTEMPTS times
      if (failedLookupsRef.current[ip] && failedLookupsRef.current[ip] >= MAX_ATTEMPTS) {
        logger.log(`Skipping IP with too many failed attempts: ${ip}`);
        return false;
      }
      
      return true;
    });
    
    // Get the current progress
    const currentProgress = lookupProgress.current;
    
    // Calculate total IPs for progress bar if this is the first batch
    if (totalIpsToProcessRef.current === 0 || currentProgress === 0) {
      // This counts all IPs across all batches for accurate progress reporting
      totalIpsToProcessRef.current = filteredBatch.length;
      
      // If we're processing a partial batch of a larger set, add the expected total
      if (ips.length < totalIpsToProcessRef.current) {
        totalIpsToProcessRef.current = Math.max(totalIpsToProcessRef.current, ips.length);
      }
      
      logger.log(`Total IPs to process: ${totalIpsToProcessRef.current}`);
    }
    
    // Update state once at beginning to show progress UI - avoid state updates during render
    if (filteredBatch.length > 0) {
      setTimeout(() => {
        setShowLookupProgress(true);
        setLookupProgress({ 
          current: currentProgress, 
          total: totalIpsToProcessRef.current
        });
        
        // Update pending IPs in state to show as "looking up..." in the UI - ensure it's outside render
        setPendingIps(current => {
          // Only add IPs that aren't already pending
          const newPending = filteredBatch.filter(ip => !current.includes(ip));
          return [...current, ...newPending];
        });
      }, 0);
    } else {
      // If there's nothing to process, hide the progress bar
      if (currentProgress >= totalIpsToProcessRef.current) {
        setTimeout(() => {
          setShowLookupProgress(false);
          totalIpsToProcessRef.current = 0; // Reset for next time
        }, 0);
      }
      return; // Exit early if nothing to process
    }
    
    try {
      let processed = 0;
      
      // Process in larger chunks for efficiency with large batches
      for (let i = 0; i < filteredBatch.length; i += BATCH_SIZE) {
        const currentBatch = filteredBatch.slice(i, i + BATCH_SIZE);
        
              // Process concurrent requests in this small batch
      const processResults = await Promise.all(currentBatch.map(async (ip) => {
        try {
          logger.log(`Looking up IP: ${ip}`);
          
          // Perform the lookup
          const result = await performReverseLookup(ip);
          
          // Return the result to be handled outside the async function
          return { ip, result, success: !!result.hostname };
        } catch (err) {
          logger.error(`Error processing IP ${ip}:`, err);
          return { ip, error: err, success: false };
        }
      }));
      
      // Now handle all the results outside the function passed to map
      for (const item of processResults) {
        const { ip, result, success, error } = item;
        
        if (!error && result) {
          // Update cache reference for future use
          reverseLookupResultsRef.current = { 
            ...reverseLookupResultsRef.current, 
            [ip]: result 
          };
          
          // Copy to the variable at this scope
          const currentResults = reverseLookupResultsRef.current;
          
          // If the lookup was successful, update the state for UI rendering
          if (success) {
            // Batch state updates
            setTimeout(() => {
              setReverseLookups(prev => ({
                ...prev,
                [ip]: result
              }));
            }, 0);
          } else {
            // Mark as failed - we won't retry
            failedLookupsRef.current = { 
              ...failedLookupsRef.current, 
              [ip]: MAX_ATTEMPTS 
            };
            
            // Create a local variable that's not referenced in a closure
            const currentFailedCache = { ...failedLookupsRef.current };
            
            // Update state for UI rendering - batched in setTimeout
            setTimeout(() => {
              setFailedLookups(prev => ({
                ...prev,
                [ip]: MAX_ATTEMPTS
              }));
            }, 0);
            
            logger.log(`Lookup failed for IP ${ip}, marking as permanently failed`);
          }
          
          // Remove from pending IPs - batch in setTimeout
          setTimeout(() => {
            setPendingIps(current => current.filter(pendingIp => pendingIp !== ip));
          }, 0);
          
          // Update progress counter locally
          const currentProcessed = processed + 1;
          processed = currentProcessed;
          
          // Update global progress, not just batch progress - batch updates
          setTimeout(() => {
            setLookupProgress(prev => ({ 
              current: prev.current + 1, 
              total: totalIpsToProcessRef.current 
            }));
          }, 0);
        }
      }
        
        // Add delay between batches, but not after the last batch
        if (i + BATCH_SIZE < filteredBatch.length) {
          await new Promise(resolve => setTimeout(resolve, DELAY_BETWEEN_REQUESTS));
        }
      }
      
      logger.log(`Completed batch of ${filteredBatch.length} IPs`);
      
      // Check if ALL IPs have been processed (including ones from previous batches)
      const globalProgressComplete = lookupProgress.current >= totalIpsToProcessRef.current;
      
      // Hide progress indicator after ALL batches complete
      if (globalProgressComplete) {
        setTimeout(() => {
          setShowLookupProgress(false);
          totalIpsToProcessRef.current = 0; // Reset for next time
        }, 1000); // Keep visible briefly after completion for user feedback
      }
      
      // Process next batch if there are remaining IPs
      const remainingIps = ips.slice(filteredBatch.length);
      if (remainingIps.length > 0) {
        // Schedule next batch with a delay
        setTimeout(() => {
          processBatchOfIps(remainingIps);
        }, DELAY_BETWEEN_REQUESTS);
      }
    } catch (error) {
      logger.error('Error processing IP batch:', error);
      
      // Remove all pending IPs from this batch to avoid hanging UI
      setTimeout(() => {
        setPendingIps(current => current.filter(ip => !filteredBatch.includes(ip)));
      }, 0);
      
      // Hide progress indicator in case of error
      if (lookupProgress.current >= totalIpsToProcessRef.current) {
        setTimeout(() => {
          setShowLookupProgress(false);
          totalIpsToProcessRef.current = 0; // Reset for next time
        }, 0);
      }
    } finally {
      // Only clear processing flag if we're done with all batches
      if (ips.length <= filteredBatch.length) {
        isProcessingQueueRef.current = false;
        isProcessingQueue = false; // Update local var too
      }
    }
  };
  
  // Function to extract all hostnames and IP addresses from SPF record
  const extractSpfEntries = (spfRecord) => {
    if (!spfRecord) return [];
    
    // Match include:, a:, mx:, ptr:, exists:, redirect= followed by a domain name or hostname
    // This regex allows for underscores, which are valid in SPF records
    const hostnameRegex = /(?:include:|a:|mx:|ptr:|exists:|redirect=)([a-zA-Z0-9_][-a-zA-Z0-9_.*]*[.][a-zA-Z0-9][-a-zA-Z0-9.*]*)/g;
    
    // Extract all IP addresses from the SPF record (ip4: mechanism)
    const ipv4Regex = /ip4:(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}(?:\/\d{1,2})?)/g;
    
    // Get hostnames
    const matches = [...spfRecord.matchAll(hostnameRegex)];
    const hostnames = matches.map(match => match[1]);
    
    // Get IPs
    const ipMatches = [...spfRecord.matchAll(ipv4Regex)];
    const ips = ipMatches.map(match => {
      // Extract the IP address part (removing CIDR notation if present)
      const ipWithCidr = match[1];
      return ipWithCidr.split('/')[0]; // Remove /27 or other CIDR notation
    });
    
    // Extract main domain from a subdomain (e.g. mail.zendesk.com -> zendesk.com -> Zendesk)
    const extractMainDomain = (hostname) => {
      // Remove common SPF prefixes
      let domain = hostname;
      if (domain.startsWith('_spf.')) {
        domain = domain.substring(5);
      } else if (domain.startsWith('spf.')) {
        domain = domain.substring(4);
      }
      
      // Split by dots to get domain parts
      const parts = domain.split('.');
      
      // If domain has at least 3 parts (like mail.zendesk.com), extract the main part
      if (parts.length >= 3) {
        // Get the organization domain part (typically the second-level domain)
        // For mail.zendesk.com, this would be "zendesk"
        const orgDomain = parts[parts.length - 2];
        
        // Capitalize the first letter
        return orgDomain.charAt(0).toUpperCase() + orgDomain.slice(1);
      }
      
      return null;
    };
    
    // Process hostnames for display
    const rawProcessedHostnames = hostnames.map(hostname => {
      // Process hostname for display
      let displayName = hostname;
      
      // Remove _spf. or spf. prefix if present (case insensitive)
      const lowerHostname = displayName.toLowerCase();
      if (lowerHostname.startsWith('_spf.')) {
        displayName = displayName.substring(5);
      } else if (lowerHostname.startsWith('spf.')) {
        displayName = displayName.substring(4);
      }
      
      // Get provider name if known (after removing prefix)
      const providerName = getProviderNameFromDomain(displayName);
      
      // If we have a provider name, use it
      if (providerName) {
        displayName = providerName;
      } 
      // Otherwise try to extract the main domain name from subdomains
      else {
        const mainDomain = extractMainDomain(hostname);
        if (mainDomain) {
          displayName = mainDomain;
        }
      }
      
      return {
        type: 'hostname',
        value: hostname,
        displayName: displayName,
        color: getHostnameColor(hostname),
        provider: providerName,
        baseDomain: getBaseDomain(displayName)
      };
    });
    
    // Group hostnames by base domain similar to how we group IPs
    const groupedHostnames = {};
    rawProcessedHostnames.forEach(entry => {
      const baseDomain = entry.baseDomain;
      
      if (!groupedHostnames[baseDomain]) {
        groupedHostnames[baseDomain] = {
          count: 1,
          entries: [entry],
          displayName: baseDomain,
          type: 'hostname-group',
          color: entry.color,
          provider: entry.provider
        };
      } else {
        groupedHostnames[baseDomain].count += 1;
        groupedHostnames[baseDomain].entries.push(entry);
      }
    });
    
    // Convert to array and add count to display name for groups
    const processedHostnames = Object.values(groupedHostnames).map(group => {
      if (group.count > 1) {
        group.displayName = `${group.displayName} (${group.count})`;
      }
      return group;
    });
    
    // Process IPs for display
    const processedIps = ips.map(ip => {
      // Check if we have a result for this IP from our known ranges
      const knownProvider = getProviderForIp(ip);
      
      // If we have a known provider, use it directly without reverse lookup
      if (knownProvider) {
        return {
          type: 'ip',
          value: ip,
          displayName: knownProvider,
          color: getServiceColor(knownProvider),
          provider: knownProvider,
          reverseLookup: true
        };
      }
      
      // Check for result in state first (for real-time updates)
      if (reverseLookups[ip] && reverseLookups[ip].hostname) {
        const lookupResult = reverseLookups[ip];
        return {
          type: 'ip',
          value: ip,
          displayName: lookupResult.provider || lookupResult.hostname,
          color: getServiceColor(lookupResult.hostname),
          hostname: lookupResult.hostname,
          provider: lookupResult.provider,
          reverseLookup: true
        };
      }
      
      // Fall back to ref for backwards compatibility
      const lookupResult = reverseLookupResults[ip];
      if (lookupResult && lookupResult.hostname) {
        return {
          type: 'ip',
          value: ip,
          displayName: lookupResult.provider || lookupResult.hostname,
          color: getServiceColor(lookupResult.hostname),
          hostname: lookupResult.hostname,
          provider: lookupResult.provider,
          reverseLookup: true
        };
      }
      
      // Check if this IP is currently being looked up
      const isPending = pendingIps.includes(ip);
      
      // Check if this IP has failed too many times - use ref for consistent access
      const hasFailed = failedLookupsRef.current[ip] && failedLookupsRef.current[ip] >= 2;
      
      // Determine display name based on status
      let displayName = ip;
      if (isPending) {
        displayName = `${ip} (looking up...)`;
      } else if (hasFailed) {
        displayName = `${ip} (lookup failed)`;
      }
      
      // If no result yet, just show the IP
      return {
        type: 'ip',
        value: ip,
        displayName: displayName,
        color: isPending ? '#e3f2fd' : hasFailed ? '#ffebee' : '#607d8b', // Light blue for pending, light red for failed, blue-gray for normal
        borderColor: isPending ? '#2196f3' : hasFailed ? '#f44336' : null, // Blue border for pending, red for failed
        fontStyle: (isPending || hasFailed) ? 'italic' : 'normal',
        reverseLookup: false,
        isPending,
        hasFailed
      };
    });
    
    // Process reverse lookups without using the queue system
    // This avoids the infinite re-render loop by not triggering state updates during render
    const neededLookups = ips.filter(ip => {
      // Skip IPs with known providers
      if (getProviderForIp(ip)) return false;
      
      // Skip if we already have ANY result (success or failure) in cache
      if (reverseLookupResultsRef.current[ip]) return false;
      
      // Skip if already pending
      if (pendingIps.includes(ip)) return false;
      
      // Skip if it has any failed attempts logged
      if (failedLookupsRef.current[ip]) return false;
      
      // Otherwise, this IP needs lookup
      return true;
    });
    
    // Update our ref with the new IPs that need lookup instead of using a local variable
    // This ensures the component-level useEffect can access the latest data
    if (JSON.stringify(ipsNeedingLookupRef.current) !== JSON.stringify(neededLookups)) {
      ipsNeedingLookupRef.current = neededLookups;
      
      // Log for debugging but don't directly process during render
      if (neededLookups.length > 0) {
        logger.log(`Found ${neededLookups.length} IPs needing lookup (will be processed through effect)`);
        
        // Use a small timeout to trigger the effect after rendering completes
        // This helps auto-start the lookup process without waiting for user interaction
        setTimeout(() => {
          // Create a "dummy state update" to force a re-render and trigger effects
          setForceUpdate(prev => prev + 1);
        }, 300);
      }
    }
    
    // Separate the different types of IP entries
    const successfulLookups = processedIps.filter(ip => ip.reverseLookup === true);
    const pendingLookups = processedIps.filter(ip => ip.isPending === true);
    const failedLookups = processedIps.filter(ip => ip.hasFailed === true);
    
    // Group successful lookups by base domain
    const groupedLookups = {};
    successfulLookups.forEach(lookup => {
      const hostname = lookup.hostname || lookup.provider || lookup.displayName;
      const baseDomain = getBaseDomain(hostname);
      
      if (!groupedLookups[baseDomain]) {
        groupedLookups[baseDomain] = {
          count: 1,
          entries: [lookup],
          displayName: baseDomain,
          type: 'grouped',
          color: lookup.color,
          reverseLookup: true
        };
      } else {
        groupedLookups[baseDomain].count += 1;
        groupedLookups[baseDomain].entries.push(lookup);
      }
    });
    
    // Convert grouped lookups to array
    const groupedLookupEntries = Object.values(groupedLookups).map(group => {
      // If there's more than one entry, add count to display name
      if (group.count > 1) {
        group.displayName = `${group.displayName} (${group.count})`;
      }
      return group;
    });
    
    // Create a single chip for all failed lookups if there are any
    let failedLookupEntry = null;
    if (failedLookups.length > 0) {
      failedLookupEntry = {
        type: 'failed-group',
        value: 'failed-lookups',
        displayName: `${failedLookups.length} failed lookup${failedLookups.length > 1 ? 's' : ''}`,
        color: '#ffebee',
        borderColor: '#f44336',
        fontStyle: 'italic',
        reverseLookup: false,
        hasFailed: true,
        entries: failedLookups // Store all failed entries for potential tooltip
      };
    }
    
    // Combine all entries
    const entries = [
      ...processedHostnames, 
      ...groupedLookupEntries, 
      ...pendingLookups, 
      ...(failedLookupEntry ? [failedLookupEntry] : [])
    ];
    
    // Filter out duplicates by displayName
    const uniqueEntries = entries.filter((entry, index, self) => 
      index === self.findIndex(e => e.displayName === entry.displayName)
    );
    
    // Return all entries, limited to 20 for display purposes
    return uniqueEntries.slice(0, 20);
  };
  
  // Function to get a color for a hostname based on known services
  const getHostnameColor = (hostname) => {
    const lowerHostname = hostname.toLowerCase();
    
    // Microsoft services
    if (lowerHostname.includes('microsoft') || 
        lowerHostname.includes('outlook') || 
        lowerHostname.includes('office365') || 
        lowerHostname.includes('protection.outlook.com')) {
      return '#0078d4'; // Microsoft blue
    }
    
    // Google services
    if (lowerHostname.includes('google') || 
        lowerHostname.includes('googlemail') || 
        lowerHostname.includes('gmail')) {
      return '#4285F4'; // Google blue
    }
    
    // Proofpoint
    if (lowerHostname.includes('pphosted') || 
        lowerHostname.includes('proofpoint')) {
      return '#de5917'; // Proofpoint orange
    }
    
    // Mimecast
    if (lowerHostname.includes('mimecast')) {
      return '#f4813e'; // Mimecast orange
    }
    
    // Barracuda
    if (lowerHostname.includes('barracuda')) {
      return '#008BD3'; // Barracuda blue
    }
    
    // SendGrid
    if (lowerHostname.includes('sendgrid')) {
      return '#1A82E2'; // SendGrid blue
    }
    
    // Mailchimp/Mandrill
    if (lowerHostname.includes('mailchimp') || 
        lowerHostname.includes('mandrill')) {
      return '#FFE01B'; // Mailchimp yellow
    }
    
    // Amazon SES
    if (lowerHostname.includes('amazonses') || 
        lowerHostname.includes('amazon.com')) {
      return '#FF9900'; // AWS orange
    }
    
    // Zoho
    if (lowerHostname.includes('zoho')) {
      return '#f0483c'; // Zoho red
    }
    
    // Zendesk
    if (lowerHostname.includes('zendesk')) {
      return '#03363D'; // Zendesk dark teal
    }
    
    // Salesforce
    if (lowerHostname.includes('salesforce') ||
        lowerHostname.includes('exacttarget')) {
      return '#00A1E0'; // Salesforce blue
    }
    
    // HubSpot
    if (lowerHostname.includes('hubspot')) {
      return '#FF7A59'; // HubSpot orange
    }
    
    // Marketo
    if (lowerHostname.includes('marketo')) {
      return '#5C4C9F'; // Marketo purple
    }
    
    // Mailgun
    if (lowerHostname.includes('mailgun')) {
      return '#F0423B'; // Mailgun red
    }
    
    // Postmark
    if (lowerHostname.includes('postmarkapp') ||
        lowerHostname.includes('postmark')) {
      return '#FFDA7A'; // Postmark yellow
    }
    
    // SparkPost
    if (lowerHostname.includes('sparkpost')) {
      return '#FA6423'; // SparkPost orange
    }
    
    // RoutiT (from example)
    if (lowerHostname.includes('routit')) {
      return '#FF2A00'; // RoutiT red
    }
    
    // MailPlatform (from example)
    if (lowerHostname.includes('mailplatform')) {
      return '#0054a6'; // MailPlatform blue
    }
    
    // AntiSpamCloud (from example)
    if (lowerHostname.includes('antispamcloud')) {
      return '#2C3082'; // AntiSpamCloud navy blue
    }
    
    // Other spam/security providers
    if (lowerHostname.includes('spam') || 
        lowerHostname.includes('security') ||
        lowerHostname.includes('filter') ||
        lowerHostname.includes('protect')) {
      return '#56575E'; // Security gray
    }
    
    // Dutch hosting providers 
    if (lowerHostname.includes('hostnet') ||
        lowerHostname.includes('transip') ||
        lowerHostname.includes('pcextreme') ||
        lowerHostname.includes('antagonist') ||
        lowerHostname.includes('mijndomein') ||
        lowerHostname.includes('hosting2go') ||
        lowerHostname.includes('vevida')) {
      return '#1B76D2'; // Hosting blue
    }
    
    // Default color for other services
    return '#555555'; // Darker gray for better contrast
  };
  
  // New function to update website URL in the database
  const updateWebsiteUrl = async () => {
    try {
      // Check if we have a record ID
      if (!recordId) {
        showNotification('Cannot update URL: Missing record ID', 'error');
        return;
      }
      
      logger.log(`Updating website URL from "${websiteUrl}" to "${newUrl}"`);
      
      // Call API to update the URL - allow empty newUrl value
      const response = await api.updateRecordField(recordId, 'URL', newUrl);
      
      if (response.success) {
        // Update both the displayed URL and form value
        setWebsiteUrl(newUrl);
        setEditingUrl(false);
        
        showNotification(newUrl 
          ? 'Website URL updated successfully' 
          : 'Website URL cleared successfully', 'success');
        
        // Update domain check with new URL if not empty
        if (newUrl) {
          await checkDNS();
        } else {
          // Clear DNS data if URL was cleared
          setDnsData(null);
          setDnsLoading(false);
          setDnsError(null);
        }
      } else {
        showNotification(response.message || 'Failed to update website URL', 'error');
        // Reset newUrl to current websiteUrl since update failed
        setNewUrl(websiteUrl);
      }
    } catch (err) {
      logger.error('Error updating website URL:', err);
      showNotification('Failed to update website URL', 'error');
      // Reset newUrl to current websiteUrl since update failed
      setNewUrl(websiteUrl);
    }
  };
  
  // Function to format date and time for display
  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return '';
    
    try {
      const date = new Date(dateTimeString);
      return date.toLocaleString('nl-NL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      logger.error('Error formatting date:', e);
      return dateTimeString;
    }
  };
  
  // Function to search for provider info using Perplexity
  const searchProviderInfo = async (provider) => {
    if (!provider) return;
    
    try {
      setProviderSearchLoading(true);
      
      // First, check if we have predefined information about this provider
      const predefinedInfo = getProviderPredefinedInfo(provider);
      if (predefinedInfo) {
        logger.log("Using predefined provider info for:", provider.displayName || provider.value);
        setTimeout(() => {
          setProviderSearchResult(predefinedInfo);
          setProviderSearchLoading(false);
        }, 300); // Small delay to show loading indicator for better UX
        return;
      }
      
      // Construct a clear search query
      let query = '';
      if (provider.type === 'verification') {
        // For verification records, use the service name directly
        const serviceName = provider.displayName || provider.provider || '';
        if (serviceName && serviceName !== 'Unknown') {
          query = `What is ${serviceName}? Provide a brief overview of their services and how they use domain verification.`;
        } else if (provider.value && provider.value.includes('=')) {
          // Try to extract service name from key-value format
          const parts = provider.value.split('=');
          if (parts.length >= 2) {
            const keyName = parts[0].trim();
            // Format the key for better querying
            const formattedKey = keyName
              .replace(/[-_]/g, ' ')
              .replace(/key$/i, '')
              .trim();
            
            if (formattedKey) {
              query = `What is ${formattedKey}? Provide information about this service and how they use domain verification.`;
            }
          }
        }
      } else if (provider.provider) {
        // If we have a known provider name, use that
        query = `What is ${provider.provider}? Provide a brief overview of their email services and reputation in email security.`;
      } else if (provider.value) {
        // Otherwise use the domain
        // Extract the base domain by removing spf prefixes and other common patterns
        let domainValue = provider.value;
        
        // Remove common SPF prefixes from domain before querying
        domainValue = domainValue.replace(/^spf[a-z]?\./i, '');
        domainValue = domainValue.replace(/^_spf\./i, '');
        
        // Remove common SPF mechanisms and qualifiers if the value is an SPF record fragment
        domainValue = domainValue.replace(/^[+\-~?]?(?:a|mx|ptr|ip4|ip6|exists|include|redirect|exp):/i, '');
        
        // Clean up domains that may have trailing info
        if (domainValue.includes('/')) {
          domainValue = domainValue.split('/')[0];
        }
        
        logger.log(`Original domain: ${provider.value}, Cleaned domain for search: ${domainValue}`);
        query = `What is the domain ${domainValue} used for? Is it a known email provider or service?`;
      } else if (provider.displayName) {
        // Fallback to display name
        query = `What is ${provider.displayName}? Provide information about this email service or domain.`;
      }
      
      if (!query) {
        setProviderSearchLoading(false);
        setProviderSearchResult(getProviderFallbackInfo(provider));
        return;
      }
      
      logger.log(`Searching for information about: ${query}`);
      
      // Call the Perplexity API
      const messages = [
        { role: "system", content: "You are a helpful assistant providing concise information about email providers and domains. Focus on describing the company, their email services, reputation, and general information. Ignore technical details like SPF, DKIM, or DMARC unless specifically asked." },
        { role: "user", content: query }
      ];
      
      // Adjust system prompt for verification records
      if (provider.type === 'verification') {
        messages[0].content = "You are a helpful assistant providing concise information about services that use domain verification. Focus on describing the company, their services, and how domain verification is used in their products.";
      }
      
      // Attempt only once since we have a fallback
      try {
        logger.log("Making Perplexity API request...");
        const response = await api.perplexityAsk(messages);
        
        if (response && response.success) {
          logger.log("Perplexity API response successful");
          setProviderSearchResult(response.content);
          return;
        } else {
          logger.warn(`Perplexity API error:`, response);
          // Fall back to our predefined information
          setProviderSearchResult(getProviderFallbackInfo(provider));
        }
      } catch (apiError) {
        logger.error("Perplexity API error:", apiError);
        // Use fallback information
        setProviderSearchResult(getProviderFallbackInfo(provider));
      }
    } catch (error) {
      logger.error("Error in searchProviderInfo:", error);
      setProviderSearchResult(getProviderFallbackInfo(provider));
    } finally {
      setProviderSearchLoading(false);
    }
  };
  
  // Helper function to get predefined information about common email providers
  const getProviderPredefinedInfo = (provider) => {
    if (!provider) return null;
    
    // For verification records (the new domain verification chips)
    if (provider.type === 'verification') {
      const serviceName = provider.displayName || '';
      
      // Specific cases for services with non-standard verification formats
      if (serviceName === 'GlobalSign') {
        return `GlobalSign domain verification is used to validate domain ownership for SSL/TLS certificate issuance. GlobalSign is a Certificate Authority (CA) that provides digital certificates for securing websites with HTTPS, digital signatures, and other security services. This verification confirms that the organization has legitimate control over the domain before issuing security certificates.`;
      }
      
      if (serviceName === 'Have I Been Pwned') {
        return `Have I Been Pwned (HIBP) domain verification allows an organization to receive notifications when email addresses from their domain appear in data breaches. This service, operated by security researcher Troy Hunt, helps organizations monitor when their users' credentials have been compromised in security breaches across the internet.`;
      }
      
      // Handle specific key-based verification patterns (like d365mktkey, msfpkey)
      if (provider.value && provider.value.includes('=')) {
        const parts = provider.value.split('=');
        if (parts.length >= 2) {
          const keyName = parts[0].trim().toLowerCase();
          
          // Specific information for known key patterns
          if (keyName === 'd365mktkey') {
            return `Dynamics 365 Marketing key verification is used to connect a custom domain to Microsoft Dynamics 365 Marketing services. This verification allows the organization to use Dynamics 365 Marketing to send authenticated emails and track marketing campaigns associated with this domain.`;
          }
          
          if (keyName === 'msfpkey') {
            return `Microsoft Forms Pro key verification is used to connect a domain to Microsoft Forms Pro (now part of Dynamics 365 Customer Voice). This verification allows the organization to conduct surveys and collect customer feedback using Microsoft's survey platform.`;
          }
          
          if (keyName === 'bw') {
            return `BitWarden domain verification key is used to verify domain ownership for BitWarden password management services. This verification allows the organization to use BitWarden's enterprise password management features, including organization-wide password policies and secure sharing with users in the domain.`;
          }
          
          if (keyName.includes('d365')) {
            return `Microsoft Dynamics 365 verification key for connecting this domain to Dynamics 365 services. This verification record confirms that the organization has administrative control over the domain and is authorized to use various Dynamics 365 features.`;
          }
          
          if (keyName.includes('msf') || keyName.includes('microsoft forms')) {
            return `Microsoft Forms verification key used to connect this domain to Microsoft Forms services. This allows the organization to create branded forms, surveys, and questionnaires that are authenticated with their domain.`;
          }
          
          if (keyName.includes('hubspot')) {
            return `HubSpot domain verification key used to connect this domain to HubSpot's marketing, sales, and CRM platform. This verification confirms ownership of the domain for use with HubSpot's various marketing and customer relationship management tools.`;
          }
          
          if (keyName.includes('adobe') || keyName.includes('acrobat')) {
            return `Adobe domain verification key used to connect this domain to Adobe's document and marketing services. This verification confirms domain ownership for using Adobe services like Document Cloud, Sign, or Marketing Cloud.`;
          }
          
          if (keyName.includes('salesforce') || keyName.includes('sfdc')) {
            return `Salesforce domain verification key used to verify domain ownership for Salesforce CRM and marketing services. This verification allows the organization to use their domain with various Salesforce cloud services.`;
          }
          
          if (keyName.includes('mailchimp')) {
            return `Mailchimp domain verification key used to connect this domain to Mailchimp's email marketing platform. This verification allows the organization to send authenticated emails from their domain using Mailchimp's services.`;
          }

          // Try to infer service info from the key name
          const cleanKeyName = keyName
            .replace(/key$/, '') // Remove 'key' suffix if present
            .replace(/^[a-z0-9]{2,4}[-_]?/, '') // Remove common prefixes like 'ms-', 'sf-', etc.
            .replace(/[-_]/g, ' ') // Replace dashes/underscores with spaces
            .trim();
            
          if (cleanKeyName && cleanKeyName !== keyName) {
            const formattedName = cleanKeyName
              .split(' ')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ');
              
            if (formattedName.length > 1) {
              return `${formattedName} domain verification key used to connect this domain to ${formattedName} services. This verification record confirms domain ownership and authorizes the use of ${formattedName} features and services with this domain.`;
            }
          }
          
          // If we have a non-generic key name but no specific information
          if (keyName && keyName !== 'unknown' && !keyName.includes('verification')) {
            return `Domain verification key "${keyName}" is used to verify ownership of this domain for a specific service or platform. This verification record confirms that the organization has administrative control over the domain and is authorized to use the associated services.`;
          }
        }
      }
      
      // Clean up service name to improve matching (remove common verification terms)
      const cleanedServiceName = serviceName
        .toLowerCase()
        .replace(/\s?verification\b/i, '')
        .replace(/\bdomain\b/i, '')
        .replace(/\bsite\b/i, '')
        .trim();
      
      // Predefined information for common verification services
      const verificationInfo = {
        'google': `Google site verification is used to verify ownership of a domain for Google services like Google Workspace, Google Search Console, Google Analytics, and other Google products. It confirms that you have administrative control over the domain.`,
        
        'microsoft': `Microsoft domain verification is used to verify ownership of a domain for Microsoft services including Microsoft 365, Azure, and other Microsoft products. This verification record confirms that you have administrative control over the domain.`,
        
        'apple': `Apple domain verification is used to verify ownership of a domain for Apple services such as Apple Business Manager or Apple School Manager. It allows organizations to set up and manage Apple devices and services for their domain.`,
        
        'facebook': `Facebook domain verification confirms ownership of a domain for Facebook Business tools and services, including the Facebook Pixel and Dynamic Ads. It ensures that your website is officially linked to your Facebook business account.`,
        
        'openai': `OpenAI domain verification is used to confirm ownership of a domain for OpenAI's services, such as custom GPT access and potentially for organization-level API access. It verifies that you have administrative control of the domain for OpenAI's verification purposes.`,
        
        'slack': `Slack domain verification is used to confirm ownership of a domain when setting up a Slack workspace for your organization. It allows you to claim your organization's domain and enables features like automatic user provisioning from email addresses at your domain.`,
        
        'atlassian': `Atlassian domain verification confirms ownership of a domain for Atlassian products such as Jira, Confluence, and Bitbucket. It allows administrators to manage access to Atlassian services for all users with email addresses on their domain.`,
        
        'zoom': `Zoom domain verification is used to verify ownership of a domain for Zoom's enterprise services. It enables features like SSO (Single Sign-On) and allows account admins to manage users with email addresses from the verified domain.`,
        
        'stripe': `Stripe domain verification validates ownership of a domain for Stripe payment processing services. This is typically used for Stripe Connect accounts or for custom payment pages to ensure the domain is authorized to process payments through Stripe.`,
        
        'shopify': `Shopify domain verification confirms ownership of a domain when connecting a custom domain to a Shopify store. It ensures that you have the right to use the domain for your e-commerce website.`,
        
        'airtable': `Airtable domain verification is used to verify ownership of a domain for Airtable's enterprise services. It allows administrators to manage access to Airtable for all users with email addresses on their domain.`,
        
        'canva': `Canva domain verification confirms ownership of a domain for Canva's business and education plans. It enables features like team management and brand controls for all users with email addresses on the verified domain.`,
        
        'mentimeter': `Mentimeter domain verification is used to confirm ownership of a domain for Mentimeter's enterprise services. This allows organizations to manage access and content for users with email addresses on their domain.`,
        
        'smartsheet': `Smartsheet domain verification confirms ownership of a domain for Smartsheet's enterprise services. It enables domain-based user management and Single Sign-On (SSO) for all users with email addresses on the verified domain.`,
        
        'intercom': `Intercom domain verification validates ownership of a domain for Intercom's customer messaging platform. This verification is typically used when setting up Intercom's business messaging features on your website.`,
        
        'docusign': `DocuSign domain verification confirms ownership of a domain for DocuSign's electronic signature and document management services. It's used to enable enterprise features and domain-based user management.`,
        
        'flexera': `Flexera domain verification is used to verify ownership of a domain for Flexera's IT management and software asset management services. This verification allows enterprise-level control over Flexera services for your organization.`,
        
        'wiz': `Wiz domain verification confirms ownership of a domain for Wiz security services. Wiz is a cloud security platform, and domain verification enables various security integrations and domain-based access controls.`,
        
        'cursor': `Cursor domain verification validates ownership of a domain for Cursor's development tools and services. Cursor is an AI-powered code editor, and domain verification may be used for team licensing and management features.`,
        
        'unknown': `This appears to be a domain verification record used to confirm domain ownership for a third-party service. Domain verification records are typically added to DNS when an organization needs to prove they control a domain in order to use various online services or platforms.`
      };
      
      // Check for direct match against the cleaned service name
      for (const [key, info] of Object.entries(verificationInfo)) {
        if (cleanedServiceName.includes(key)) {
          return info;
        }
      }
      
      // If there's a name but no match in our database, provide specific information
      if (cleanedServiceName && cleanedServiceName !== 'unknown') {
        return `${serviceName} verification is used to confirm ownership of a domain for ${serviceName} services. This verification record proves the organization has administrative control of this domain and is authorized to use ${serviceName} services.`;
      }
      
      // If no specific match, return the generic 'unknown' entry
      return verificationInfo['unknown'];
    }
    
    // Extract the core domain (without subdomains) for matching
    const extractCoreDomain = (domain) => {
      if (!domain) return '';
      try {
        // Split by dots and take the last two parts (e.g., example.com from sub.example.com)
        const parts = domain.toLowerCase().split('.');
        if (parts.length >= 2) {
          return `${parts[parts.length - 2]}.${parts[parts.length - 1]}`;
        }
        return domain.toLowerCase();
      } catch(e) {
        return domain.toLowerCase();
      }
    };

    // Get the value to match against (either provider name or domain)
    const providerName = provider.provider ? provider.provider.toLowerCase() : '';
    const providerValue = provider.value ? provider.value.toLowerCase() : '';
    const coreDomain = extractCoreDomain(providerValue);
    
    // Predefined information for common email providers and services
    const providers = {
      'microsoft': `Microsoft 365 (formerly Office 365) is Microsoft's cloud-based productivity suite that includes email hosting with Exchange Online. It offers enterprise-grade email security features including anti-spam, anti-malware, and advanced threat protection. Microsoft's email infrastructure is widely used by businesses worldwide and is considered highly reliable with strong security measures.`,
      
      'microsoft 365': `Microsoft 365 (formerly Office 365) is Microsoft's cloud-based productivity suite that includes email hosting with Exchange Online. It offers enterprise-grade email security features including anti-spam, anti-malware, and advanced threat protection. Microsoft's email infrastructure is widely used by businesses worldwide and is considered highly reliable with strong security measures.`,
      
      'm365': `Microsoft 365 (M365) is Microsoft's cloud-based productivity suite that includes email hosting with Exchange Online. It provides business email, collaboration tools, and Office applications with enterprise-grade security features including anti-spam and anti-malware protection.`,
      
      'microsoft exchange online': `Exchange Online is Microsoft's cloud-based email service that's part of Microsoft 365. It provides business-class email, calendar, and contacts on phones, tablets, desktops, and the web. Exchange Online offers robust security features including anti-malware protection, anti-spam filtering, and encrypted email capabilities.`,
      
      'google': `Google's email services include both Gmail for personal use and Google Workspace (formerly G Suite) for business use. Google provides robust email infrastructure with industry-leading spam and malware protection. Google's email servers are widely trusted and have excellent deliverability rates across the internet.`,
      
      'google workspace': `Google Workspace (formerly G Suite) is Google's business productivity suite that includes professional email hosting with Gmail. It offers strong security features, spam protection, and seamless integration with other Google services. Google's email infrastructure is highly regarded for its reliability and security.`,

      'proofpoint': `Proofpoint is a leading cybersecurity company specializing in email security, providing cloud-based solutions to protect organizations from threats delivered via email. Their services include advanced threat protection, email fraud defense, and email security gateways. Many organizations route their email through Proofpoint for additional security filtering before delivery.`,
      
      'mimecast': `Mimecast is a leading email security and cyber resilience company. Their cloud-based service provides comprehensive email security, archiving, and continuity services. Many organizations use Mimecast as an email security gateway to filter threats before emails reach their primary mail servers.`,
      
      'barracuda': `Barracuda Networks provides email security products including email security gateways, spam and virus filtering, archiving, and encryption. Their email security services help protect organizations from phishing, malware, and other email-based threats.`,
      
      'sendgrid': `SendGrid (now part of Twilio) is a cloud-based email delivery platform that helps businesses send transactional and marketing emails. It's primarily used for sending automated emails, newsletters, and notifications rather than as a primary business email service. Many applications and websites use SendGrid to reliably deliver their outgoing messages.`,
      
      'mailchimp': `Mailchimp is primarily a marketing automation platform and email marketing service. Companies use Mailchimp to design and send marketing emails, newsletters, and automated messages. It's not typically used for regular business email communications but rather for bulk marketing emails and campaigns.`,
      
      'amazon ses': `Amazon SES (Simple Email Service) is a cloud-based email sending service designed to help digital marketers and application developers send marketing, notification, and transactional emails. It's commonly used by applications and websites to send automated emails rather than as a primary business email hosting solution.`,
      
      'zoho mail': `Zoho Mail is a secure email service with strong privacy features, provided as part of Zoho's suite of business applications. It offers business email hosting with collaboration tools and integrates with Zoho's other business applications. Zoho provides a comprehensive alternative to Microsoft 365 and Google Workspace.`,
      
      'zendesk': `Zendesk is a customer service platform that provides support ticketing, help desk functionality, and customer communication tools. Organizations use Zendesk's email infrastructure for customer support communications, automated responses, and ticket management. When you see mail.zendesk.com in an SPF record, it indicates the organization is using Zendesk for customer support email communications.`,
      
      'hubspot': `HubSpot is an inbound marketing, sales, and customer service platform. Their email infrastructure is used for marketing campaigns, sales outreach, and customer communications. When an organization includes HubSpot in their SPF record, it means they're using HubSpot's email services for marketing automation and customer relationship management.`,
      
      'marketo': `Marketo (now part of Adobe) is a marketing automation platform that helps organizations manage email marketing campaigns, lead generation, and customer engagement. When a domain authorizes Marketo in their SPF record, they're using Marketo's email services for marketing campaigns and automated customer communications.`,
      
      'salesforce marketing cloud': `Salesforce Marketing Cloud (formerly ExactTarget) is a digital marketing platform that provides email, mobile, social, and web marketing capabilities. Organizations use it for large-scale marketing campaigns, customer journey automation, and personalized communications. This SPF entry indicates the organization is using Salesforce for marketing email communications.`,
      
      'freshworks': `Freshworks (including Freshdesk) provides customer engagement software including helpdesk, CRM, marketing automation, and chat tools. Their email infrastructure is used for customer support tickets, marketing campaigns, and automated communications. This SPF entry indicates the organization is using Freshworks products for customer communications.`,
      
      'intercom': `Intercom is a customer messaging platform that enables businesses to communicate with prospective and existing customers across their own websites, mobile apps, and through email. When included in an SPF record, it means the organization is using Intercom for customer communications, support, and engagement.`,
      
      'routit': `RoutiT is a Dutch IT service provider that offers managed services, email hosting, and cloud solutions. Their SPF record indicates this organization is using RoutiT's email infrastructure for corporate email communications. RoutiT provides email services optimized for business reliability and security.`,
      
      'mailplatform': `MailPlatform is an email marketing and automation platform primarily used in Europe. When you see this in an SPF record, it indicates the organization is using MailPlatform to send marketing emails, newsletters, or automated communications. They offer services for sending bulk emails, tracking engagement, and managing email marketing campaigns.`,
      
      'antispamcloud': `AntiSpamCloud is an email security and filtering service that helps organizations protect against spam, phishing, and malware. When included in an SPF record, it indicates that the organization routes their email through AntiSpamCloud's filtering systems before delivery to improve email security and reduce unwanted messages.`,
      
      'spamexperts': `SpamExperts (now part of Cisco) provides cloud-based email security services including spam filtering, virus protection, and email archiving. Their inclusion in an SPF record indicates that the organization's email is filtered through their security systems to prevent malicious emails from reaching recipients.`,
      
      'transip': `TransIP is a Dutch hosting provider offering domain registration, web hosting, and email services. Their presence in an SPF record indicates the organization is using TransIP's infrastructure for email hosting or sending. TransIP provides reliable email services for businesses in the Netherlands and beyond.`,
      
      'hostnet': `Hostnet is a Dutch web hosting and domain registration company that provides email services for businesses. When included in an SPF record, it indicates the organization hosts their email with Hostnet. They offer reliable email hosting services primarily for the Dutch market.`,
      
      'pcextreme': `PCextreme is a Dutch hosting provider offering cloud services, dedicated servers, and email hosting. Their inclusion in an SPF record indicates the organization is using PCextreme's email infrastructure for their corporate communications. They focus on providing stable and secure email services to businesses in the Netherlands.`,
      
      'strato': `Strato is a European web hosting provider offering email hosting, domain registration, and web hosting services. When included in an SPF record, it indicates the organization hosts their email with Strato's servers. They provide reliable and affordable email solutions for businesses across Europe.`
    };
    
    // Check for matches in various ways
    if (providerName && providers[providerName]) {
      return providers[providerName];
    }
    
    // Check provider value against known domains
    if (providerValue) {
      // Check for Microsoft domains
      if (providerValue.includes('protection.outlook.com') || 
          providerValue.includes('mail.protection.outlook.com') ||
          providerValue.includes('spf.protection.outlook.com')) {
        return providers['microsoft'];
      }
      
      // Check for Google domains
      if (providerValue.includes('_spf.google.com') || 
          providerValue.includes('googlemail.com') ||
          providerValue.includes('spf-google.com')) {
        return providers['google'];
      }

      // Check for specific providers in the domain
      if (providerValue.includes('rpost')) return providers['rpost'];
      if (providerValue.includes('afas')) return providers['afas'];
      if (providerValue.includes('evolveip')) return providers['evolveip'];
      
      // Check other common providers by substring matching
      for (const [key, info] of Object.entries(providers)) {
        if (providerValue.includes(key.toLowerCase())) {
          return info;
        }
      }
      
      // Check by core domain
      if (coreDomain === 'outlook.com') return providers['outlook.com'];
      if (coreDomain === 'googlemail.com' || coreDomain === 'gmail.com') return providers['google'];
      if (coreDomain === 'pphosted.com') return providers['proofpoint'];
      if (coreDomain === 'mimecast.com') return providers['mimecast'];
      if (coreDomain === 'sendgrid.net') return providers['sendgrid'];
      if (coreDomain === 'amazonses.com') return providers['amazonses'];
      if (coreDomain === 'zoho.com' || coreDomain === 'zoho.eu') return providers['zoho'];
    }
    
    // No predefined information found
    return null;
  };
  
  // Helper function to get fallback information for a provider
  const getProviderFallbackInfo = (provider) => {
    if (!provider) {
      return "No information available about this provider.";
    }
    
    // Try to get predefined info first
    const predefinedInfo = getProviderPredefinedInfo(provider);
    if (predefinedInfo) {
      return predefinedInfo;
    }
    
    // Special handling for verification records
    if (provider.type === 'verification') {
      const service = provider.displayName || 'Unknown';
      
      // If service name contains "Unknown", try to extract from record
      if (service === 'Unknown' && provider.value) {
        const record = provider.value;
        
        // Try to extract service name from record format "service=value"
        if (record.includes('=')) {
          const parts = record.split('=');
          if (parts.length >= 2 && parts[0].trim().length > 0) {
            // Clean up and format the extracted service name
            const extractedService = parts[0].trim()
              .replace(/[-_]/g, ' ') // Replace dashes and underscores with spaces
              .replace(/\bdomain\b|\bsite\b|\bverification\b/gi, '') // Remove common words
              .trim();
            
            // Only use the extracted name if it's not empty after cleaning
            if (extractedService) {
              const formattedService = extractedService.charAt(0).toUpperCase() + extractedService.slice(1);
              return `${formattedService} verification is used to confirm ownership of a domain for ${formattedService} services. Domain verification records are added to DNS when an organization needs to prove they control a domain in order to use online services or platforms.`;
            }
          }
        }
      }
      
      return `${service} verification is used to confirm ownership of a domain for ${service}'s services. Domain verification records are added to DNS when an organization needs to prove they control a domain in order to use online services or platforms.`;
    }
    
    // Generate generic information based on what we know
    if (provider.provider) {
      return `${provider.provider} appears to be an email service provider or email infrastructure provider that this organization uses for their email services. SPF records help verify that email messages claiming to come from a specific domain are actually from authorized mail servers.`;
    }
    
    if (provider.type === 'hostname') {
      return `${provider.value} appears in this organization's SPF record, which means it's authorized to send email on behalf of this domain. SPF (Sender Policy Framework) records list all servers that are permitted to send email from a specific domain, helping to prevent email spoofing and phishing attacks.`;
    }
    
    if (provider.type === 'ip') {
      return `The IP address ${provider.value} is authorized to send email on behalf of this domain according to their SPF record. This could be a dedicated mail server operated by the organization or a third-party email service they use.`;
    }
    
    return "Information about this email provider could not be retrieved at this time. SPF records list the authorized servers and services that can send email on behalf of a domain.";
  };
  
  // Add this function after getHostnameColor
  // Function to get a color for verification services
  const getServiceColor = (hostname) => {
    if (!hostname) return '#555555'; // Default darker gray
    
    const lowerHostname = hostname.toLowerCase();
    
    // Microsoft services
    if (lowerHostname.includes('microsoft') || 
        lowerHostname.includes('office365') || 
        lowerHostname.includes('ms=') || 
        lowerHostname.includes('outlook') || 
        lowerHostname.includes('azure')) {
      return '#0078d4'; // Microsoft blue
    }
    
    // Google services
    if (lowerHostname.includes('google') || 
        lowerHostname.includes('googlemail') || 
        lowerHostname.includes('gmail')) {
      return '#4285F4'; // Google blue
    }
    
    // Specific services with brand colors
    if (lowerHostname.includes('docusign')) return '#FFCC00'; // DocuSign yellow
    if (lowerHostname.includes('adobe')) return '#FF0000'; // Adobe red
    if (lowerHostname.includes('facebook')) return '#1877F2'; // Facebook blue
    if (lowerHostname.includes('apple')) return '#000000'; // Apple black
    if (lowerHostname.includes('zoom')) return '#0E71EB'; // Zoom blue
    if (lowerHostname.includes('cisco') || lowerHostname.includes('webex')) return '#009EDC'; // Cisco blue
    if (lowerHostname.includes('atlassian')) return '#0052CC'; // Atlassian blue
    if (lowerHostname.includes('slack')) return '#4A154B'; // Slack purple
    if (lowerHostname.includes('stripe')) return '#635BFF'; // Stripe purple
    if (lowerHostname.includes('dropbox')) return '#0061FF'; // Dropbox blue
    if (lowerHostname.includes('shopify')) return '#96BF48'; // Shopify green
    if (lowerHostname.includes('zendesk')) return '#03363D'; // Zendesk dark teal
    if (lowerHostname.includes('hubspot')) return '#FF7A59'; // HubSpot orange
    if (lowerHostname.includes('salesforce')) return '#00A1E0'; // Salesforce blue
    if (lowerHostname.includes('marketo')) return '#5C4C9F'; // Marketo purple
    if (lowerHostname.includes('mailchimp')) return '#FFE01B'; // Mailchimp yellow
    if (lowerHostname.includes('openai')) return '#10a37f'; // OpenAI green
    if (lowerHostname.includes('globalsign')) return '#00AEEF'; // GlobalSign blue
    if (lowerHostname.includes('airtable')) return '#F82B60'; // Airtable red
    if (lowerHostname.includes('canva')) return '#00C4CC'; // Canva teal
    if (lowerHostname.includes('intercom')) return '#316BEB'; // Intercom blue
    if (lowerHostname.includes('pwned')) return '#2A3A8C'; // Have I Been Pwned blue
    if (lowerHostname.includes('cloudflare')) return '#F38020'; // Cloudflare orange
    if (lowerHostname.includes('sophos')) return '#FF3F3F'; // Sophos red
    if (lowerHostname.includes('proofpoint')) return '#de5917'; // Proofpoint orange
    if (lowerHostname.includes('mimecast')) return '#f4813e'; // Mimecast orange
    if (lowerHostname.includes('barracuda')) return '#008BD3'; // Barracuda blue
    if (lowerHostname.includes('digicert')) return '#0C2340'; // DigiCert navy
    if (lowerHostname.includes('wiz')) return '#1E76F1'; // Wiz blue
    if (lowerHostname.includes('heroku')) return '#430098'; // Heroku purple
    if (lowerHostname.includes('netlify')) return '#00AD9F'; // Netlify teal
    if (lowerHostname.includes('vercel')) return '#000000'; // Vercel black
    if (lowerHostname.includes('bitward')) return '#175DDC'; // BitWarden blue
    if (lowerHostname.includes('onelogin')) return '#0059D6'; // OneLogin blue
    if (lowerHostname.includes('okta')) return '#00297A'; // Okta blue
    if (lowerHostname.includes('auth0')) return '#EB5424'; // Auth0 orange
    if (lowerHostname.includes('sendgrid')) return '#1A82E2'; // SendGrid blue
    if (lowerHostname.includes('let\'s encrypt') || lowerHostname.includes('letsencrypt')) return '#003A70'; // Let's Encrypt dark blue
    
    // Categories if service name matches a category
    if (lowerHostname === 'security') return '#4CAF50'; // Green for Security
    if (lowerHostname.includes('certificate') || lowerHostname.includes('ssl')) return '#FFC107'; // Yellow for Certificate Authority
    if (lowerHostname.includes('cloud')) return '#4285F4'; // Blue for Cloud Services
    if (lowerHostname.includes('marketing') || lowerHostname.includes('analytics')) return '#F44336'; // Red for Marketing and Analytics
    if (lowerHostname.includes('collaboration')) return '#9C27B0'; // Purple for Collaboration
    if (lowerHostname.includes('signing') || lowerHostname.includes('signature')) return '#FF9800'; // Orange for Electronic Signing
    if (lowerHostname.includes('remote') || lowerHostname.includes('management')) return '#607D8B'; // Blueish gray for Remote Management
    if (lowerHostname.includes('email')) return '#00BCD4'; // Teal for Email
    if (lowerHostname.includes('development') || lowerHostname.includes('publishing')) return '#3F51B5'; // Indigo for Development and Publishing
    if (lowerHostname.includes('financial')) return '#009688'; // Teal for Financial Services
    if (lowerHostname.includes('project')) return '#FF5722'; // Deep orange for Project Management

    // Default color for other services
    return '#555555'; // Darker gray for better contrast
  };
  
  // Now add the Domain Verification Records section after the Authorized Email Providers section
  // Look for the closing parenthesis of the Authorized Email Providers section and add the new section after it
  
  // Function to search for company URLs
  const searchCompanyUrls = async (recordId) => {
    try {
      logger.log("Starting URL search for recordId:", recordId);
      
      // Set loading state
      setUrlSearchLoading(prev => ({ ...prev, [recordId]: true }));
      setUrlSearchError(prev => ({ ...prev, [recordId]: null }));
      
      // Get company name and location - use the prop from component parameters
      const localCompanyName = companyName || (contactInfo?.Instellingsnaam || contactInfo?.company_name || 'Unknown Company');
      logger.log("Searching for company:", localCompanyName);
      
      // Include location data if available
      const location = contactInfo?.["Vestigingsadres Plaats"] || contactInfo?.plaats || contactInfo?.city || "";
      const country = contactInfo?.["Vestigingsadres Land"] || contactInfo?.land || contactInfo?.country || "Nederland"; // Default to Netherlands
      
      // Include country in location if not already included
      const fullLocation = location.toLowerCase().includes(country.toLowerCase()) ? 
        location : 
        `${location}${location ? ', ' : ''}${country}`;
      
      logger.log(`Searching URLs for: ${localCompanyName} in ${fullLocation}`);
      
      // Search for URLs
      logger.log("Making API request to searchCompanyUrls");
      const response = await api.searchCompanyUrls({
        company_name: localCompanyName,
        location: fullLocation
      });
      
      logger.log("API response received:", response);
      
      if (response && response.success) {
        // Check both response formats for backward compatibility
        const urls = response.urls || {};
        
        // Also check for data format from older API
        const data = response.data || {};
        
        // Define found URLs
        let foundWebsiteUrl = null;
        let foundLinkedinUrl = null;
        
        // Try to extract URLs from response
        try {
          foundWebsiteUrl = urls.website || data.website_url || null;
          foundLinkedinUrl = urls.linkedin || data.linkedin_url || null;
          
          // Enhanced logging
          logger.log("URL search response:", response);
          logger.log(`Found URLs - Website: ${foundWebsiteUrl}, LinkedIn: ${foundLinkedinUrl}`);
        } catch (extractError) {
          logger.error("Error extracting URLs from response:", extractError);
          logger.log("Full response object:", JSON.stringify(response));
        }
        
        // Get existing URLs for comparison
        const existingWebsite = websiteUrl || '';
        
        logger.log("Existing URLs:", { website: existingWebsite });
        
        // Only proceed if we found at least one URL
        if (foundWebsiteUrl || foundLinkedinUrl) {
          logger.log("URLs found, preparing for display");
          
          // Show results directly for the user to review
          setUrlSearchError(prev => ({ 
            ...prev, 
            [recordId]: {
              message: language === 'nl' 
                ? "URLs gevonden. Bekijk ze hieronder:" 
                : "URLs found. Review them below:",
              isError: false,
              website_url: foundWebsiteUrl,
              linkedin_url: foundLinkedinUrl,
              urls: {
                website: foundWebsiteUrl,
                linkedin: foundLinkedinUrl
              }
            }
          }));
        } else {
          logger.log("No URLs found in the response");
          setUrlSearchError(prev => ({ 
            ...prev, 
            [recordId]: {
              message: language === 'nl' 
                ? "Geen URLs gevonden voor dit bedrijf" 
                : "No URLs found for this company",
              isError: false
            }
          }));
        }
      } else {
        logger.error("API returned error or invalid response:", response);
        setUrlSearchError(prev => ({ 
          ...prev, 
          [recordId]: {
            message: response?.message || 'No URLs found',
            isError: true,
            debug_response: response?.debug_response || ""
          }
        }));
      }
    } catch (err) {
      logger.error("Error searching URLs:", err);
      logger.error("Error stack:", err.stack);
      setUrlSearchError(prev => ({ 
        ...prev, 
        [recordId]: {
          message: err.message || 'Error searching URLs',
          isError: true
        }
      }));
    } finally {
      setUrlSearchLoading(prev => ({ ...prev, [recordId]: false }));
    }
  };
  
  // Function to save found company URLs to database
  const saveCompanyUrls = async (recordId, urls) => {
    try {
      logger.log("DEBUG: saveCompanyUrls called with:", { recordId, urls });
      
      if (!recordId) {
        logger.error("DEBUG: No recordId provided to saveCompanyUrls");
        return;
      }
      
      let websiteUrl = '';
      let linkedinUrl = '';
      
      // Extract URLs from the response
      if (urls) {
        websiteUrl = urls.website || '';
        linkedinUrl = urls.linkedin || '';
      }
      
      logger.log(`DEBUG: Saving URLs for record ${recordId}:`, { websiteUrl, linkedinUrl });
      
      // Save website URL if provided - use 'URL' field which is the actual database field name
      if (websiteUrl) {
        logger.log(`DEBUG: Calling api.updateRecordField for URL: ${websiteUrl}`);
        const websiteResponse = await api.updateRecordField(recordId, 'URL', websiteUrl);
        logger.log('DEBUG: Website URL api response:', websiteResponse);
        if (websiteResponse.success) {
          logger.log('DEBUG: Successfully saved website URL');
          // Update the state to reflect the new URL
          setWebsiteUrl(websiteUrl);
          setNewUrl(websiteUrl);
        } else {
          logger.error('DEBUG: Failed to save website URL:', websiteResponse.message);
        }
      } else {
        logger.log('DEBUG: No website URL to save');
      }
      
      // We don't have a LinkedIn URL field in the main form, so we'll just update the database
      if (linkedinUrl) {
        logger.log(`DEBUG: Calling api.updateRecordField for LinkedIn Link: ${linkedinUrl}`);
        const linkedinResponse = await api.updateRecordField(recordId, 'LinkedIn Link', linkedinUrl);
        logger.log('DEBUG: LinkedIn URL api response:', linkedinResponse);
        if (linkedinResponse.success) {
          logger.log('DEBUG: Successfully saved LinkedIn URL');
        } else {
          logger.error('DEBUG: Failed to save LinkedIn URL:', linkedinResponse.message);
        }
      } else {
        logger.log('DEBUG: No LinkedIn URL to save');
      }
      
      // Show success message
      if (websiteUrl || linkedinUrl) {
        showNotification(
          `Saved ${websiteUrl ? 'Website' : ''}${websiteUrl && linkedinUrl ? ' and ' : ''}${linkedinUrl ? 'LinkedIn' : ''} URL${websiteUrl && linkedinUrl ? 's' : ''}`,
          'success'
        );

        // Re-run DNS check if we saved a new website URL
        if (websiteUrl) {
          await checkDNS();
        }

      } else {
        showNotification('No URLs provided to save', 'warning');
      }
    } catch (err) {
      logger.error('DEBUG: Error saving company URLs:', err);
      logger.error('DEBUG: Error stack:', err.stack);
      showNotification('Failed to save URLs', 'error');
    }
  };
  
  // Add this helper function for category icons - insert before or after the getServiceColor function
  // Helper function to get an icon for a category
  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Cloud Services':
        return <CloudIcon fontSize="small" sx={{ mr: 1, color: '#4285F4' }} />;
      case 'Marketing and Analytics':
        return <TrendingUpIcon fontSize="small" sx={{ mr: 1, color: '#F44336' }} />;
      case 'Security':
        return <ShieldIcon fontSize="small" sx={{ mr: 1, color: '#4CAF50' }} />;
      case 'Certificate Authority':
        return <GppGoodIcon fontSize="small" sx={{ mr: 1, color: '#FFC107' }} />;
      case 'Collaboration':
        return <PeopleIcon fontSize="small" sx={{ mr: 1, color: '#9C27B0' }} />;
      case 'Electronic Signing':
        return <DescriptionIcon fontSize="small" sx={{ mr: 1, color: '#FF9800' }} />;
      case 'Remote Management':
        return <ComputerIcon fontSize="small" sx={{ mr: 1, color: '#607D8B' }} />;
      case 'Configuration Management':
        return <SettingsIcon fontSize="small" sx={{ mr: 1, color: '#795548' }} />;
      case 'Email':
        return <EmailIcon fontSize="small" sx={{ mr: 1, color: '#00BCD4' }} />;
      case 'Development and Publishing':
        return <CodeIcon fontSize="small" sx={{ mr: 1, color: '#3F51B5' }} />;
      case 'Financial Services':
        return <AccountBalanceIcon fontSize="small" sx={{ mr: 1, color: '#009688' }} />;
      case 'Project Management':
        return <AssignmentIcon fontSize="small" sx={{ mr: 1, color: '#FF5722' }} />;
      default:
        return <MoreHorizIcon fontSize="small" sx={{ mr: 1, color: '#9E9E9E' }} />;
    }
  };

  // AI Research functionality - exactly like CompanyResearch
  
  // Function to parse markdown into sections - exactly like CompanyResearch
  const parseMarkdownSections = (markdown) => {
    const sections = [];
    const lines = markdown.split('\n');
    let currentSection = null;
    let currentContent = [];
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      // Check for headers (# ## ###)
      if (line.match(/^#{1,3}\s+/)) {
        // Save previous section if exists
        if (currentSection) {
          sections.push({
            ...currentSection,
            content: currentContent.join('\n').trim()
          });
        }
        
        // Start new section
        const title = line.replace(/^#{1,3}\s+/, '');
        currentSection = {
          title: title,
          isSourcesSection: title.toLowerCase().includes('source') || title.toLowerCase().includes('bron')
        };
        currentContent = [];
      } else {
        // Add line to current content
        currentContent.push(line);
      }
    }
    
    // Add the last section
    if (currentSection) {
      sections.push({
        ...currentSection,
        content: currentContent.join('\n').trim()
      });
    }
    
    // If no sections were found, create a single section with all content
    if (sections.length === 0) {
      sections.push({
        title: 'Research Results',
        content: markdown,
        isSourcesSection: false
      });
    }
    
    return sections;
  };

  // Function to fetch AI research data - exactly like CompanyResearch
  const fetchResearchData = async () => {
    if (!recordId || !companyName || researchLoading) return;

    setResearchLoading(true);
    setResearchError(null);

    try {
      logger.log(`Fetching research data for company: ${companyName}`);
      
      // Create a record object for the API call
      const record = {
        record_id: recordId,
        Instellingsnaam: companyName,
        company_name: companyName,
        ...contactInfo
      };
      
      // Prepare request data - use the 'companies' array format that the backend expects
      const requestData = { 
        companies: [record],
        language: language
      };
      
      logger.log('Sending research request to API:', requestData);
      
      const response = await api.post('/api/pai-research', requestData);
      
      // Check if the response was successful
      if (!response.data.success) {
        logger.error('Research API returned an error:', response.data);
        throw new Error(response.data.message || 'API returned an unsuccessful response');
      }
      
      logger.log('Full research API response:', response.data);
      
      // More flexible handling of research data structure - exactly like CompanyResearch
      let markdown = '';
      
      // Check if we have research data in the expected format
      if (response.data?.data?.results && response.data.data.results.length > 0) {
        const result = response.data.data.results[0];
        markdown = result.content;
        logger.log('Found research data in expected results format');
      } 
      // Check for raw_result format from older API
      else if (response.data?.data?.raw_result?.research_data?.full_response) {
        markdown = response.data.data.raw_result.research_data.full_response;
        logger.log('Found research data in legacy raw_result format');
      }
      // Check for a direct content field in the response
      else if (response.data?.data?.content) {
        markdown = response.data.data.content;
        logger.log('Found research data in direct content field');
      }
      // If no recognizable format is found, throw an error
      else {
        logger.error('Research API response missing expected data:', response.data);
        throw new Error(language === 'nl' ? 
          "Onderzoeksgegevens ontbreken in API-antwoord" : 
          "Research data missing in API response");
      }
      
      logger.log(`Received research data with length: ${markdown.length} characters`);
      
      try {
        const sections = parseMarkdownSections(markdown);
        logger.log(`Parsed ${sections.length} sections from markdown`);
        
        setResearchData({ 
          raw: response.data,
          sections: sections
        });
      } catch (parseError) {
        logger.error('Error parsing markdown sections:', parseError);
        setResearchError('Error parsing research data');
      }
      
    } catch (error) {
      logger.error('Error fetching research data:', error);
      setResearchError(error.message || 'Failed to fetch research data');
    } finally {
      setResearchLoading(false);
    }
  };

  // Citation Link component for handling source citations - exactly like CompanyResearch
  const CitationLink = ({ children }) => {
    if (!children || typeof children !== 'string') {
      return <>{children}</>;
    }

    // Pattern to match citations like [1], [2], etc.
    const citationPattern = /\[(\d+)\]/g;
    
    if (!citationPattern.test(children)) {
      return <>{children}</>;
    }

    const parts = children.split(citationPattern);
    const result = [];
    
    for (let i = 0; i < parts.length; i++) {
      if (i % 2 === 0) {
        // Regular text
        result.push(parts[i]);
      } else {
        // Citation number
        const sourceNumber = parseInt(parts[i]);
        result.push(
          <Link
            key={`citation-${i}`}
            component="button"
            variant="body2"
            onClick={(e) => {
              e.preventDefault();
              handleSourceClick(sourceNumber);
            }}
            sx={{
              color: 'primary.main',
              textDecoration: 'none',
              fontWeight: 'bold',
              fontSize: '0.8rem',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            [{sourceNumber}]
          </Link>
        );
      }
    }
    
    return <>{result}</>;
  };

  // Source dialog handlers - exactly like CompanyResearch
  const handleSourceClick = (sourceNumber) => {
    logger.log(`Source ${sourceNumber} clicked`);
    
    // Try to find source in research data
    if (researchData?.raw?.data?.raw_result?.research_data?.sources) {
      const sources = researchData.raw.data.raw_result.research_data.sources;
      const source = sources.find(s => s.number === sourceNumber);
      if (source) {
        setSelectedSource(source);
        setOpenSourceDialog(true);
        return;
      }
    }
    
    // Fallback - create a basic source object
    setSelectedSource({
      number: sourceNumber,
      title: `Source ${sourceNumber}`,
      url: '#',
      snippet: 'Source details not available'
    });
    setOpenSourceDialog(true);
  };

  const handleCloseSourceDialog = () => {
    setOpenSourceDialog(false);
    setSelectedSource(null);
  };

  // Source Dialog component - exactly like CompanyResearch
  const SourceDialog = ({ open, onClose, source }) => {
    if (!open || !source) return null;
    
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6">
            Source {source.number}: {source.title || 'Untitled'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {source.url && source.url !== '#' && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                URL:
              </Typography>
              <Link href={source.url} target="_blank" rel="noopener noreferrer">
                {source.url}
              </Link>
            </Box>
          )}
          
          {source.snippet && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Excerpt:
              </Typography>
              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                "{source.snippet}"
              </Typography>
            </Box>
          )}
          
          {source.published_date && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Published: {source.published_date}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  };

  // Lusha contacts functionality
  // Function to fetch existing enriched contacts from database - exactly like CompanyResearch
  const fetchExistingEnrichedContacts = async (recordId) => {
    try {
      logger.log(`Checking for existing enriched contacts for record: ${recordId}`);
      
      const response = await api.get(`/api/lusha/enriched-contacts?record_id=${recordId}`);
      
      if (response.data.success && response.data.data && response.data.data.length > 0) {
        logger.log(`Found ${response.data.data.length} existing enriched contacts`);
        
        // Convert enriched contacts to the format expected by the UI
        const enrichedContacts = response.data.data.map(enrichedContact => {
          // Create a contact object that matches the Lusha search format
          const contact = {
            id: enrichedContact.contact_id,
            contactId: enrichedContact.contact_id,
            firstName: enrichedContact.first_name,
            lastName: enrichedContact.last_name,
            fullName: enrichedContact.full_name,
            displayName: enrichedContact.full_name || 'Unknown Contact',
            jobTitle: enrichedContact.job_title,
            companyName: enrichedContact.company_name,
            
            // Mark as already enriched
            isEnriched: true,
            enriched: true,
            enrichedAt: enrichedContact.enriched_at,
            
            // Add enriched data
            workEmail: enrichedContact.work_email,
            email: enrichedContact.work_email,
            personalEmail: enrichedContact.personal_email,
            mobilePhone: enrichedContact.mobile_phone,
            directPhone: enrichedContact.direct_phone,
            phone: enrichedContact.mobile_phone || enrichedContact.direct_phone,
            linkedinUrl: enrichedContact.linkedin_url,
            linkedInUrl: enrichedContact.linkedin_url,
            contactLocation: enrichedContact.contact_location,
            contactCountry: enrichedContact.contact_country,
            contactCity: enrichedContact.contact_city,
            
            // Company information
            companyDescription: enrichedContact.company_description,
            companyEmployees: enrichedContact.company_employees,
            companyIndustry: enrichedContact.company_industry,
            companySubIndustry: enrichedContact.company_sub_industry,
            companyLocation: enrichedContact.company_location,
            companyLinkedin: enrichedContact.company_linkedin,
            companyLogo: enrichedContact.company_logo_url,
            
            // Additional data
            departments: enrichedContact.departments,
            seniority: enrichedContact.seniority,
            // Parse emailAddresses if it's a JSON string, otherwise use as-is
            emailAddresses: typeof enrichedContact.email_addresses === 'string' 
              ? JSON.parse(enrichedContact.email_addresses) 
              : enrichedContact.email_addresses || (enrichedContact.work_email ? [{ email: enrichedContact.work_email, emailType: 'work' }] : []),
            phoneNumbers: typeof enrichedContact.phone_numbers === 'string'
              ? JSON.parse(enrichedContact.phone_numbers)
              : enrichedContact.phone_numbers,
            socialLinks: typeof enrichedContact.social_links === 'string'
              ? JSON.parse(enrichedContact.social_links)
              : enrichedContact.social_links,
            
            // Raw data for reference
            rawEnrichedData: enrichedContact.raw_enriched_data
          };
          
          return contact;
        });
        
        return enrichedContacts;
      }
      
      return null; // No existing enriched contacts found
    } catch (error) {
      logger.error('Error fetching existing enriched contacts:', error);
      return null;
    }
  };

  // Function to merge search contacts with enriched contacts - exactly like CompanyResearch
  const mergeContactsWithEnrichedData = (searchContacts, enrichedContacts) => {
    if (!enrichedContacts || enrichedContacts.length === 0) {
      return searchContacts || [];
    }
    
    if (!searchContacts || searchContacts.length === 0) {
      return enrichedContacts;
    }
    
    logger.log('=== MERGING CONTACTS DEBUG ===');
    logger.log('Enriched contacts:', enrichedContacts.map(c => ({ 
      id: c.id || c.contactId, 
      name: c.fullName || `${c.firstName} ${c.lastName}`,
      isEnriched: c.isEnriched 
    })));
    logger.log('Search contacts:', searchContacts.map(c => ({ 
      id: c.id || c.contactId, 
      name: c.fullName || `${c.firstName} ${c.lastName}` 
    })));
    
    // Create multiple maps for different matching strategies
    const enrichedByContactId = new Map();
    const enrichedByName = new Map();
    
    enrichedContacts.forEach(enriched => {
      const contactId = enriched.id || enriched.contactId;
      const fullName = (enriched.fullName || `${enriched.firstName || ''} ${enriched.lastName || ''}`.trim()).toLowerCase();
      
      logger.log(`Processing enriched contact: id=${contactId}, fullName=${fullName}, email=${enriched.workEmail || enriched.email || 'N/A'}`);
      
      if (contactId) {
        enrichedByContactId.set(contactId, enriched);
        logger.log(`Added enriched contact to map by ID: ${contactId}`);
      }
      if (fullName) {
        enrichedByName.set(fullName, enriched);
        logger.log(`Added enriched contact to map by name: ${fullName}`);
      }
    });
    
    logger.log(`Enriched contacts map: ${enrichedByContactId.size} by ID, ${enrichedByName.size} by name`);
    
    // Merge enriched data into search contacts instead of filtering them out
    const mergedSearchContacts = searchContacts.map(searchContact => {
      const contactId = searchContact.id || searchContact.contactId;
      const fullName = (searchContact.fullName || `${searchContact.firstName || ''} ${searchContact.lastName || ''}`.trim()).toLowerCase();
      const searchEmail = searchContact.workEmail || searchContact.email || (searchContact.emailAddresses && searchContact.emailAddresses[0]?.email);
      
      logger.log(`Checking search contact: id=${contactId}, fullName=${fullName}, email=${searchEmail || 'N/A'}`);
      
      // Try matching by contact ID first
      if (contactId && enrichedByContactId.has(contactId)) {
        const enrichedData = enrichedByContactId.get(contactId);
        logger.log(`✅ MATCHED by ID: Merging enriched data into search contact ${contactId} (${fullName})`);
        // Merge enriched data into search contact, keeping search contact structure
        const merged = {
          ...searchContact,
          ...enrichedData,
          isEnriched: true,
          enriched: true,
          // Preserve search contact's ID structure
          id: searchContact.id || enrichedData.id,
          contactId: searchContact.contactId || enrichedData.contactId,
          // Ensure emailAddresses array is preserved/merged
          emailAddresses: enrichedData.emailAddresses || searchContact.emailAddresses || (enrichedData.workEmail ? [{ email: enrichedData.workEmail, emailType: 'work' }] : [])
        };
        logger.log(`Merged contact has email: ${merged.workEmail || merged.email || 'N/A'}, emailAddresses: ${JSON.stringify(merged.emailAddresses)}`);
        return merged;
      }
      
      // Try matching by full name as fallback
      if (fullName && enrichedByName.has(fullName)) {
        const enrichedData = enrichedByName.get(fullName);
        logger.log(`✅ MATCHED by name: Merging enriched data into search contact ${contactId} (${fullName})`);
        // Merge enriched data into search contact, keeping search contact structure
        const merged = {
          ...searchContact,
          ...enrichedData,
          isEnriched: true,
          enriched: true,
          // Preserve search contact's ID structure
          id: searchContact.id || enrichedData.id,
          contactId: searchContact.contactId || enrichedData.contactId,
          // Ensure emailAddresses array is preserved/merged
          emailAddresses: enrichedData.emailAddresses || searchContact.emailAddresses || (enrichedData.workEmail ? [{ email: enrichedData.workEmail, emailType: 'work' }] : [])
        };
        logger.log(`Merged contact has email: ${merged.workEmail || merged.email || 'N/A'}, emailAddresses: ${JSON.stringify(merged.emailAddresses)}`);
        return merged;
      }
      
      // Try matching by email as additional fallback
      if (searchEmail) {
        const normalizedSearchEmail = searchEmail.toLowerCase().trim();
        for (const [key, enrichedData] of enrichedByContactId.entries()) {
          const enrichedEmail = (enrichedData.workEmail || enrichedData.email || '').toLowerCase().trim();
          if (enrichedEmail && enrichedEmail === normalizedSearchEmail) {
            logger.log(`✅ MATCHED by email: Merging enriched data into search contact ${contactId} (${fullName}) - email: ${searchEmail}`);
            const merged = {
              ...searchContact,
              ...enrichedData,
              isEnriched: true,
              enriched: true,
              id: searchContact.id || enrichedData.id,
              contactId: searchContact.contactId || enrichedData.contactId,
              emailAddresses: enrichedData.emailAddresses || searchContact.emailAddresses || (enrichedData.workEmail ? [{ email: enrichedData.workEmail, emailType: 'work' }] : [])
            };
            logger.log(`Merged contact has email: ${merged.workEmail || merged.email || 'N/A'}`);
            return merged;
          }
        }
      }
      
      logger.log(`❌ NO MATCH: Keeping search contact ${contactId} (${fullName}) as-is`);
      return searchContact; // Keep search contact as-is if no enriched match
    });
    
    // Find enriched contacts that don't appear in search results (enriched but not in current search)
    const enrichedNotInSearch = enrichedContacts.filter(enriched => {
      const enrichedId = enriched.id || enriched.contactId;
      const enrichedFullName = (enriched.fullName || `${enriched.firstName || ''} ${enriched.lastName || ''}`.trim()).toLowerCase();
      
      return !searchContacts.some(searchContact => {
        const searchId = searchContact.id || searchContact.contactId;
        const searchFullName = (searchContact.fullName || `${searchContact.firstName || ''} ${searchContact.lastName || ''}`.trim()).toLowerCase();
        return (enrichedId && searchId && enrichedId === searchId) || (enrichedFullName && searchFullName && enrichedFullName === searchFullName);
      });
    });
    
    // Create final merged list: merged search contacts (with enriched data), then enriched-only contacts
    const mergedContacts = [
      ...mergedSearchContacts, // Search contacts with enriched data merged in
      ...enrichedNotInSearch  // Enriched contacts not in current search results
    ];
    
    logger.log(`Merged result: ${mergedSearchContacts.length} search contacts (${mergedSearchContacts.filter(c => c.isEnriched).length} with enriched data) + ${enrichedNotInSearch.length} enriched-only = ${mergedContacts.length} total`);
    logger.log('Final merged contacts:', mergedContacts.map(c => ({ 
      id: c.id || c.contactId, 
      name: c.fullName || `${c.firstName} ${c.lastName}`,
      isEnriched: c.isEnriched 
    })));
    logger.log('=== END MERGING DEBUG ===');
    
    return mergedContacts;
  };

  const fetchLushaContacts = async () => {
    if (!recordId || lushaLoading) return;

    setLushaLoading(true);
    setLushaError(null);

    try {
      // Get existing enriched contacts for this record - exactly like CompanyResearch
      const existingEnrichedContacts = await fetchExistingEnrichedContacts(recordId);
      logger.log(`Found ${existingEnrichedContacts ? existingEnrichedContacts.length : 0} existing enriched contacts`);

      // Get domain from contact info or websiteUrl state
      let domain = null;
      let companyNameForSearch = companyName;
      let urlSource = null;

      // First, try to get URL from websiteUrl state (populated by useEffect)
      if (websiteUrl) {
        urlSource = websiteUrl;
        logger.log('Using websiteUrl state for domain extraction:', urlSource);
      } 
      // If not in state, try contactInfo.Website
      else if (contactInfo && contactInfo.Website) {
        urlSource = contactInfo.Website;
        logger.log('Using contactInfo.Website for domain extraction:', urlSource);
      }
      // Try other common field names in contactInfo
      else if (contactInfo && typeof contactInfo === 'object') {
        const possibleFields = [
          'URL', 'url', 'Website', 'website', 'Domain', 'domain', 
          'Website URL', 'website_url', 'Company Website', 'company_website',
          'Corporate Website', 'Bedrijfswebsite', 'Homepage'
        ];
        
        for (const field of possibleFields) {
          if (contactInfo[field] && typeof contactInfo[field] === 'string') {
            urlSource = contactInfo[field];
            logger.log(`Found URL in field "${field}":`, urlSource);
            break;
          }
        }
        
        // If no website found in common fields, try to find any field with URL-like value
        if (!urlSource) {
          for (const field in contactInfo) {
            const value = contactInfo[field];
            if (value && typeof value === 'string' && 
                value.includes('.') && 
                (value.toLowerCase().startsWith('http') || 
                 value.toLowerCase().includes('www.'))) {
              urlSource = value;
              logger.log(`Found URL-like value in field "${field}":`, urlSource);
              break;
            }
          }
        }
      }
      // Handle string contactInfo (from EventsDashboard)
      else if (contactInfo && typeof contactInfo === 'string') {
        if (contactInfo.includes('.') && 
            (contactInfo.toLowerCase().startsWith('http') || 
             contactInfo.toLowerCase().includes('www.') || 
             !contactInfo.includes('@'))) {
          urlSource = contactInfo;
          logger.log('Using contactInfo string as URL:', urlSource);
        }
      }

      // Extract domain from URL source
      if (urlSource) {
        try {
          const url = new URL(urlSource.startsWith('http') ? urlSource : `https://${urlSource}`);
          domain = url.hostname.replace(/^www\./, '');
          logger.log('Extracted domain from URL:', domain);
        } catch (e) {
          // If URL parsing fails, try to extract domain directly
          domain = urlSource.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
          logger.log('Extracted domain using fallback method:', domain);
        }
      }

      if (!domain && !companyNameForSearch) {
        setLushaError('No domain or company name available for Lusha search');
        setLushaLoading(false);
        return;
      }

      const requestData = {
        domain: domain,
        company_name: domain ? null : companyNameForSearch,
        job_title: null,
        record_id: recordId,
        enable_cleanup: true
      };

      const response = await api.post('/api/lusha/search', requestData);

      if (!response.data.success) {
        throw new Error(response.data.message || response.data.error || 'API returned an unsuccessful response');
      }

      if (response.data.request_id) {
        setLushaRequestId(response.data.request_id);
      }

      logger.log('Lusha search response:', response.data);

      if (response.data.data && response.data.data.data) {
        const contacts = response.data.data.data;
        logger.log(`Found ${contacts.length} Lusha contacts`);
        
        // Process contacts to ensure name fields are available
        const processedContacts = contacts.map(contact => {
          return {
            ...contact,
            fullName: contact.fullName || `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || contact.name,
            displayName: contact.fullName || `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || contact.name || 'Unknown Contact'
          };
        });

        logger.log('Processed Lusha contacts:', processedContacts);
        
        // Merge with existing enriched contacts - exactly like CompanyResearch
        const mergedContacts = mergeContactsWithEnrichedData(processedContacts, existingEnrichedContacts);
        logger.log('Merged contacts with enriched data:', mergedContacts);
        
        setLushaContacts(mergedContacts);
        
        // Extract and set company logo
        const logo = extractCompanyLogo(mergedContacts);
        setCompanyLogo(logo);
        
        // Check which contacts already exist in Salesforce and HubSpot
        checkContactsInSalesforce(mergedContacts);
        checkContactsInHubspot(mergedContacts);
      } else {
        logger.log('No Lusha contacts data in response');
        // If we have existing enriched contacts but no search results, show the enriched ones
        if (existingEnrichedContacts && existingEnrichedContacts.length > 0) {
          logger.log('No search results, but showing existing enriched contacts');
          setLushaContacts(existingEnrichedContacts);
          
          // Extract and set company logo from enriched contacts
          const logo = extractCompanyLogo(existingEnrichedContacts);
          setCompanyLogo(logo);
          
          // Check which contacts already exist in Salesforce and HubSpot
          checkContactsInSalesforce(existingEnrichedContacts);
          checkContactsInHubspot(existingEnrichedContacts);
          checkContactsInHubspot(existingEnrichedContacts);
        } else {
          setLushaContacts([]);
        }
      }
    } catch (error) {
      logger.error('Error fetching Lusha contacts:', error);
      setLushaError(error.message || 'Failed to fetch Lusha contacts');
    } finally {
      setLushaLoading(false);
    }
  };

  // Function to check which contacts exist in Salesforce
  const checkContactsInSalesforce = async (contacts) => {
    if (!contacts || contacts.length === 0) return;
    
    // Check if Salesforce is connected
    try {
      const status = await api.salesforceStatus();
      if (!status.success || !status.connected) {
        logger.log('Salesforce not connected, skipping lead check');
        setSalesforceConnectionStatus(false);
        return;
      }
      setSalesforceConnectionStatus(true);
    } catch (error) {
      logger.log('Error checking Salesforce status:', error);
      setSalesforceConnectionStatus(false);
      return;
    }
    
    // Extract emails from contacts - keep original case for Salesforce querying
    // Salesforce Email field comparisons are case-sensitive, so we need original case
    const emails = contacts
      .map(contact => {
        // Try emailAddresses array first (from enriched contacts)
        if (contact.emailAddresses && Array.isArray(contact.emailAddresses) && contact.emailAddresses.length > 0) {
          const workEmail = contact.emailAddresses.find(e => e.emailType === 'work' || e.emailType === 'Work');
          if (workEmail && workEmail.email) {
            return workEmail.email.trim();  // Keep original case
          }
          // Fallback to first email if no work email found
          if (contact.emailAddresses[0] && contact.emailAddresses[0].email) {
            return contact.emailAddresses[0].email.trim();
          }
        }
        // Fallback to direct email fields
        const email = contact.workEmail || contact.email || contact.personalEmail;
        return email ? email.trim() : null;  // Keep original case, just trim
      })
      .filter(email => email && email.length > 0);
    
    if (emails.length === 0) {
      logger.log('No emails found in contacts to check');
      return;
    }
    
    logger.log(`Checking ${emails.length} contacts in Salesforce`, { emails: emails.slice(0, 5) });
    
    try {
      setCheckingSalesforce(true);
      const result = await api.salesforceCheckLeadsExist(emails);
      
      if (result.success && result.results) {
        // Update the map - backend returns results keyed by lowercase email
        const leadsMap = {};
        Object.keys(result.results).forEach(emailKey => {
          // Ensure lowercase key (backend normalizes to lowercase for consistent lookup)
          const normalizedKey = emailKey.toLowerCase();
          leadsMap[normalizedKey] = result.results[emailKey];
        });
        setSalesforceLeadsMap(leadsMap);
        logger.log(`Found ${Object.keys(leadsMap).length} contacts already in Salesforce`, {
          totalChecked: emails.length,
          found: Object.keys(leadsMap).length,
          matchedEmails: Object.keys(leadsMap),
          leadsMap: leadsMap
        });
      } else {
        logger.log('No Salesforce leads found or check failed:', result);
      }
    } catch (error) {
      logger.error('Error checking contacts in Salesforce:', error);
    } finally {
      setCheckingSalesforce(false);
    }
  };
  
  // Function to add a contact as a Lead to Salesforce
  const addContactToSalesforce = async (contact) => {
    if (!contact) return;
    
    // Check if Salesforce is connected
    if (!salesforceConnectionStatus) {
      try {
        const status = await api.salesforceStatus();
        if (!status.success || !status.connected) {
          setError('Please connect to Salesforce first');
          return;
        }
        setSalesforceConnectionStatus(true);
      } catch (error) {
        setError('Please connect to Salesforce first');
        return;
      }
    }
    
    // Extract email - prefer emailAddresses array (from enriched contacts)
    let contactEmail = null;
    if (contact.emailAddresses && Array.isArray(contact.emailAddresses) && contact.emailAddresses.length > 0) {
      const workEmail = contact.emailAddresses.find(e => e.emailType === 'work' || e.emailType === 'Work');
      contactEmail = workEmail ? workEmail.email : contact.emailAddresses[0].email;
    } else {
      contactEmail = contact.workEmail || contact.email || contact.personalEmail;
    }
    
    const contactId = contact.id || contact.contactId;
    
    // Check if already exists - normalize email to lowercase for comparison
    const emailKey = contactEmail ? contactEmail.trim().toLowerCase() : null;
    if (emailKey && salesforceLeadsMap[emailKey]) {
      const existingLead = salesforceLeadsMap[emailKey];
      setNotification({
        open: true,
        message: `This contact already exists in Salesforce (Lead: ${existingLead.FirstName || ''} ${existingLead.LastName || ''} - ${existingLead.Company || 'N/A'})`,
        severity: 'warning'
      });
      return;
    }
    
    // Prepare lead data - prefer mobile phone number
    const phoneNumber = contact.mobilePhone || contact.directPhone || contact.phone || '';
    
    const leadData = {
      FirstName: contact.firstName || '',
      LastName: contact.lastName || contact.lastName || 'Unknown',
      Company: companyName || contact.company || 'Unknown Company',
      Email: contactEmail || '',
      Phone: phoneNumber,
      Title: contact.jobTitle || '',
      Website: websiteUrl || contactInfo?.Website || '',
      Industry: '',
      Status: 'Open - Not Contacted',
      Description: `Added from SalesOutreach - Contact ID: ${contactId}`
    };
    
    // Validate required fields
    if (!leadData.Company || leadData.Company === 'Unknown Company') {
      setError('Company name is required. Please ensure company information is available.');
      return;
    }
    
    if (!leadData.LastName || leadData.LastName === 'Unknown') {
      setError('Last name is required for creating a Lead');
      return;
    }
    
    try {
      setAddingToSalesforce(prev => new Set(prev).add(contactId));
      setError(null);
      
      const result = await api.salesforceCreateLead(leadData, true);
      
      if (result.success) {
        // Update the map to mark this contact as existing
        if (contactEmail) {
          setSalesforceLeadsMap(prev => ({
            ...prev,
            [contactEmail.toLowerCase()]: {
              Id: result.lead_id,
              Email: contactEmail,
              FirstName: leadData.FirstName,
              LastName: leadData.LastName,
              Company: leadData.Company
            }
          }));
        }
        // Show success message using notification system
        setNotification({
          open: true,
          message: `Successfully added ${leadData.FirstName} ${leadData.LastName} to Salesforce!`,
          severity: 'success'
        });
      } else if (result.is_duplicate) {
        // Update map with existing lead info
        const existingLead = result.existing_lead || {};
        if (existingLead && contactEmail) {
          setSalesforceLeadsMap(prev => ({
            ...prev,
            [contactEmail.toLowerCase()]: existingLead
          }));
        }
        // Show warning notification instead of error
        const leadName = existingLead.FirstName || existingLead.LastName 
          ? `${existingLead.FirstName || ''} ${existingLead.LastName || ''}`.trim()
          : 'Unknown';
        const leadCompany = existingLead.Company || 'N/A';
        setNotification({
          open: true,
          message: `This contact already exists in Salesforce as Lead: ${leadName} (${leadCompany})${existingLead.Id ? ` - ID: ${existingLead.Id}` : ''}`,
          severity: 'warning'
        });
      } else {
        setError(result.error || 'Failed to add contact to Salesforce');
      }
    } catch (error) {
      logger.error('Error adding contact to Salesforce:', error);
      setError(error.response?.data?.error || error.message || 'Failed to add contact to Salesforce');
    } finally {
      setAddingToSalesforce(prev => {
        const newSet = new Set(prev);
        newSet.delete(contactId);
        return newSet;
      });
    }
  };

  // Function to check if contacts exist in HubSpot
  const checkContactsInHubspot = async (contacts) => {
    if (!contacts || contacts.length === 0) return;
    
    // Check if HubSpot is connected
    try {
      const status = await api.hubspotStatus();
      if (!status.success || !status.connected) {
        logger.log('HubSpot not connected, skipping contact check');
        setHubspotConnectionStatus(false);
        return;
      }
      setHubspotConnectionStatus(true);
    } catch (error) {
      logger.log('Error checking HubSpot status:', error);
      setHubspotConnectionStatus(false);
      return;
    }
    
    // Extract emails from contacts
    const emails = contacts
      .map(contact => {
        // Try emailAddresses array first (from enriched contacts)
        if (contact.emailAddresses && Array.isArray(contact.emailAddresses) && contact.emailAddresses.length > 0) {
          const workEmail = contact.emailAddresses.find(e => e.emailType === 'work' || e.emailType === 'Work');
          if (workEmail && workEmail.email) {
            return workEmail.email.trim();
          }
          // Fallback to first email if no work email found
          if (contact.emailAddresses[0] && contact.emailAddresses[0].email) {
            return contact.emailAddresses[0].email.trim();
          }
        }
        // Fallback to direct email fields
        const email = contact.workEmail || contact.email || contact.personalEmail;
        return email ? email.trim() : null;
      })
      .filter(email => email && email.length > 0);
    
    if (emails.length === 0) {
      logger.log('No emails found in contacts to check');
      return;
    }
    
    logger.log(`Checking ${emails.length} contacts in HubSpot`, { emails: emails.slice(0, 5) });
    
    try {
      setCheckingHubspot(true);
      const result = await api.hubspotCheckContactsExist(emails);
      
      if (result.success && result.results) {
        // Update the map - backend returns results keyed by lowercase email
        const contactsMap = {};
        Object.keys(result.results).forEach(emailKey => {
          // Ensure lowercase key (backend normalizes to lowercase for consistent lookup)
          const normalizedKey = emailKey.toLowerCase();
          contactsMap[normalizedKey] = result.results[emailKey];
        });
        setHubspotContactsMap(contactsMap);
        logger.log(`Found ${Object.keys(contactsMap).length} contacts already in HubSpot`, {
          totalChecked: emails.length,
          found: Object.keys(contactsMap).length,
          matchedEmails: Object.keys(contactsMap),
          contactsMap: contactsMap
        });
      } else {
        logger.log('No HubSpot contacts found or check failed:', result);
      }
    } catch (error) {
      logger.error('Error checking contacts in HubSpot:', error);
    } finally {
      setCheckingHubspot(false);
    }
  };
  
  // Function to add a contact to HubSpot
  const addContactToHubspot = async (contact) => {
    if (!contact) return;
    
    // Check if HubSpot is connected
    if (!hubspotConnectionStatus) {
      try {
        const status = await api.hubspotStatus();
        if (!status.success || !status.connected) {
          setError('Please connect to HubSpot first');
          return;
        }
        setHubspotConnectionStatus(true);
      } catch (error) {
        setError('Please connect to HubSpot first');
        return;
      }
    }
    
    // Extract email - prefer emailAddresses array (from enriched contacts)
    let contactEmail = null;
    if (contact.emailAddresses && Array.isArray(contact.emailAddresses) && contact.emailAddresses.length > 0) {
      const workEmail = contact.emailAddresses.find(e => e.emailType === 'work' || e.emailType === 'Work');
      contactEmail = workEmail ? workEmail.email : contact.emailAddresses[0].email;
    } else {
      contactEmail = contact.workEmail || contact.email || contact.personalEmail;
    }
    
    const contactId = contact.id || contact.contactId;
    
    // Check if already exists - normalize email to lowercase for comparison
    const emailKey = contactEmail ? contactEmail.trim().toLowerCase() : null;
    if (emailKey && hubspotContactsMap[emailKey]) {
      const existingContact = hubspotContactsMap[emailKey];
      setNotification({
        open: true,
        message: `This contact already exists in HubSpot (${existingContact.firstname || ''} ${existingContact.lastname || ''} - ${existingContact.company || 'N/A'})`,
        severity: 'warning'
      });
      return;
    }
    
    // Prepare contact data - prefer mobile phone number
    const phoneNumber = contact.mobilePhone || contact.directPhone || contact.phone || '';
    
    const contactData = {
      email: contactEmail || '',
      firstname: contact.firstName || '',
      lastname: contact.lastName || contact.lastName || 'Unknown',
      phone: phoneNumber,
      jobtitle: contact.jobTitle || '',
      company: companyName || contact.company || 'Unknown Company',
      website: websiteUrl || contactInfo?.Website || '',
      linkedin: contact.linkedin || contact.linkedInUrl || ''
    };
    
    // Validate required fields
    if (!contactData.company || contactData.company === 'Unknown Company') {
      setError('Company name is required. Please ensure company information is available.');
      return;
    }
    
    if (!contactData.lastname || contactData.lastname === 'Unknown') {
      setError('Last name is required for creating a Contact');
      return;
    }
    
    try {
      setAddingToHubspot(prev => new Set(prev).add(contactId));
      setError(null);
      
      const result = await api.hubspotCreateContact(contactData, true);
      
      if (result.success) {
        // Update the map to mark this contact as existing
        if (contactEmail) {
          setHubspotContactsMap(prev => ({
            ...prev,
            [contactEmail.toLowerCase()]: {
              id: result.contact_id,
              email: contactEmail,
              firstname: contactData.firstname,
              lastname: contactData.lastname,
              company: contactData.company
            }
          }));
        }
        // Show success message using notification system
        setNotification({
          open: true,
          message: `Successfully added ${contactData.firstname} ${contactData.lastname} to HubSpot!`,
          severity: 'success'
        });
      } else if (result.is_duplicate) {
        // Update map with existing contact info
        const existingContact = result.existing_contact || {};
        if (existingContact && contactEmail) {
          setHubspotContactsMap(prev => ({
            ...prev,
            [contactEmail.toLowerCase()]: existingContact
          }));
        }
        // Show warning notification instead of error
        const contactName = existingContact.firstname || existingContact.lastname 
          ? `${existingContact.firstname || ''} ${existingContact.lastname || ''}`.trim()
          : 'Unknown';
        const contactCompany = existingContact.company || 'N/A';
        setNotification({
          open: true,
          message: `This contact already exists in HubSpot as Contact: ${contactName} (${contactCompany})${existingContact.id ? ` - ID: ${existingContact.id}` : ''}`,
          severity: 'warning'
        });
      } else {
        setError(result.error || 'Failed to add contact to HubSpot');
      }
    } catch (error) {
      logger.error('Error adding contact to HubSpot:', error);
      setError(error.response?.data?.error || error.message || 'Failed to add contact to HubSpot');
    } finally {
      setAddingToHubspot(prev => {
        const newSet = new Set(prev);
        newSet.delete(contactId);
        return newSet;
      });
    }
  };

  // Function to handle contact enrichment
  const enrichLushaContact = async (contactId) => {
    if (!contactId || !lushaRequestId) {
      logger.error('Missing contactId or lushaRequestId:', { contactId, lushaRequestId });
      return;
    }

    // Check if this contact is already being enriched
    if (enrichingContacts.has(contactId)) {
      logger.log('Contact already being enriched, skipping:', contactId);
      return;
    }

    logger.log('Enriching contact:', { contactId, lushaRequestId, recordId });
    setEnrichLoading(true);
    setEnrichingContacts(prev => new Set(prev).add(contactId));

    try {
      // Find the contact data for fallback
      const contactData = lushaContacts?.find(c => (c.contactId || c.id) === contactId);
      const contactDataMap = {};
      if (contactData) {
        // Extract domain from contact info for fallback
        let domain = null;
        if (contactInfo && contactInfo.Website) {
          try {
            const url = new URL(contactInfo.Website.startsWith('http') ? contactInfo.Website : `https://${contactInfo.Website}`);
            domain = url.hostname.replace(/^www\./, '');
          } catch (e) {
            // If URL parsing fails, try to extract domain directly
            domain = contactInfo.Website.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
          }
        }
        
        // Add domain to contact data for fallback
        const enhancedContactData = {
          ...contactData,
          company_domain: domain,
          companyDomain: domain
        };
        
        contactDataMap[contactId] = enhancedContactData;
        logger.log('Enhanced contact data with domain for fallback:', enhancedContactData);
      }

      const requestData = {
        contact_ids: [contactId], // API expects an array of contact IDs
        request_id: lushaRequestId,
        record_id: recordId,
        contact_data_map: contactDataMap // Include contact data for fallback
      };
      
      logger.log('Enrich request data:', requestData);
      
      const response = await api.post('/api/lusha/enrich', requestData);

      logger.log('Lusha enrich response:', response.data);
      logger.log('Lusha enrich response structure:', {
        success: response.data.success,
        creditsCharged: response.data.creditsCharged,
        hasData: !!response.data.data,
        dataStructure: response.data.data ? Object.keys(response.data.data) : 'no data'
      });

      // Get the enriched data - handle different response structures
      const enrichedData = response.data.data;
      
      // Check if we have enriched contacts - handle both possible structures
      let enrichedContacts = null;
      if (enrichedData && enrichedData.data && Array.isArray(enrichedData.data)) {
        // Structure: response.data.data.data (array)
        enrichedContacts = enrichedData.data;
      } else if (enrichedData && Array.isArray(enrichedData)) {
        // Structure: response.data.data (direct array)
        enrichedContacts = enrichedData;
      }
      
      if (enrichedContacts && Array.isArray(enrichedContacts)) {
        logger.log('Enriched contacts array:', enrichedContacts);
        
        if (enrichedContacts.length > 0) {
          const firstContact = enrichedContacts[0];
          logger.log('First enriched contact data:', {
            id: firstContact.id || firstContact.contactId,
            email: firstContact.email,
            workEmail: firstContact.workEmail,
            phone: firstContact.phone,
            mobilePhone: firstContact.mobilePhone,
            emailAddresses: firstContact.emailAddresses,
            phoneNumbers: firstContact.phoneNumbers,
            linkedin: firstContact.linkedin,
            allKeys: Object.keys(firstContact)
          });
          
          // Check if any contacts have actual enrichment data
          // The enriched data should have email, phone, or other contact details
          const hasEnrichmentData = enrichedContacts.some(contact => 
            contact.email || contact.workEmail || contact.phone || contact.mobilePhone || 
            contact.emailAddresses || contact.phoneNumbers || contact.linkedin
          );
          
          logger.log('Has enrichment data:', hasEnrichmentData);
          
          if (hasEnrichmentData) {
            // Update the contact in-place with enriched data
            const enrichedContact = enrichedContacts[0];
            
            logger.log('Updating contact with enriched data:', {
              contactId,
              enrichedContact,
              mergedData: { ...enrichedContact, enriched: true, isEnriched: true }
            });
            
            setLushaContacts(prev => {
              const updated = prev.map(contact => 
              (contact.id === contactId || contact.contactId === contactId) 
                ? { ...contact, ...enrichedContact, enriched: true, isEnriched: true }
                : contact
              );
              logger.log('Updated lushaContacts:', updated);
              return updated;
            });
            
            // Also update filtered contacts if they exist - preserve order to prevent jumping
            setFilteredLushaContacts(prev => {
              if (prev && Array.isArray(prev)) {
                const updated = prev.map(contact => 
                  (contact.id === contactId || contact.contactId === contactId) 
                    ? { ...contact, ...enrichedContact, enriched: true, isEnriched: true }
                    : contact
                );
                logger.log('Updated filteredLushaContacts (preserving order):', updated);
                return updated;
              }
              return prev;
            });
            
            // If we have active filters, reapply them while preserving order to prevent contact from jumping
            const hasActiveFilters = (lushaFilters.seniority_levels && lushaFilters.seniority_levels.length > 0) ||
                                    (lushaFilters.searchText && lushaFilters.searchText.trim());
            if (hasActiveFilters) {
              setTimeout(() => {
                applyLushaFiltersWithSeniority(lushaFilters.searchText, lushaFilters.seniority_levels, true);
              }, 100);
            }
            
            // Show enrichment modal with the results
            setEnrichmentModal({
              open: true,
              data: {
                contact: enrichedContact,
                contactId: contactId,
                creditsUsed: response.data.creditsCharged || 0,
                originalContact: lushaContacts?.find(c => (c.contactId || c.id) === contactId),
                fallbackUsed: response.data.fallback_used || false,
                fallbackReason: response.data.fallback_reason || null
              }
            });

            if (isMobile) {
              setActiveTab(2);
            }
            
            logger.log('✅ Contact enrichment completed successfully');
            
            // Show notification if fallback was used
            if (response.data.fallback_used) {
              showNotification(
                `Contact enriched using person search fallback (${response.data.fallback_reason})`, 
                'info'
              );
            } else {
              showNotification('Contact enriched successfully!', 'success');
            }
          } else {
            // No enrichment data available, but still mark as enriched to prevent repeated attempts
            logger.log('No enrichment data available, but marking contact as enriched');
            
            setLushaContacts(prev => {
              const updated = prev.map(contact => 
                (contact.id === contactId || contact.contactId === contactId) 
                  ? { ...contact, enriched: true, isEnriched: true }
                  : contact
              );
              logger.log('Updated lushaContacts (no data):', updated);
              return updated;
            });
            
            // Also update filtered contacts if they exist - preserve order
            setFilteredLushaContacts(prev => {
              if (prev && Array.isArray(prev)) {
                const updated = prev.map(contact => 
                  (contact.id === contactId || contact.contactId === contactId) 
                    ? { ...contact, enriched: true, isEnriched: true }
                    : contact
                );
                logger.log('Updated filteredLushaContacts (no data, preserving order):', updated);
                return updated;
              }
              return prev;
            });
            
            // Reapply filters while preserving order if there are active filters
            const hasActiveFilters = (lushaFilters.seniority_levels && lushaFilters.seniority_levels.length > 0) ||
                                    (lushaFilters.searchText && lushaFilters.searchText.trim());
            if (hasActiveFilters) {
              setTimeout(() => {
                applyLushaFiltersWithSeniority(lushaFilters.searchText, lushaFilters.seniority_levels, true);
              }, 100);
            }
            
            // No enrichment data available - delay alert in case there's a retry
            setTimeout(() => {
              // Check if the contact was enriched in the meantime
              const contactStillNotEnriched = lushaContacts?.find(c => 
                (c.id === contactId || c.contactId === contactId) && !c.enriched && !c.isEnriched
              );
              
              if (contactStillNotEnriched) {
                const message = language === 'nl' ? 
                  "Geen aanvullende gegevens voor dit contact (0 credits gebruikt)" : 
                  "No additional data for this contact (0 credits used)";
                alert(message);
              }
            }, 2000); // Wait 2 seconds to see if a retry succeeds
          }
        } else {
          // No enriched contacts returned - delay alert in case there's a retry
          setTimeout(() => {
            // Check if the contact was enriched in the meantime
            const contactStillNotEnriched = lushaContacts?.find(c => 
              (c.id === contactId || c.contactId === contactId) && !c.enriched && !c.isEnriched
            );
            
            if (contactStillNotEnriched) {
              const message = language === 'nl' ? 
                "Geen aanvullende gegevens voor dit contact (0 credits gebruikt)" : 
                "No additional data for this contact (0 credits used)";
              alert(message);
            }
          }, 2000); // Wait 2 seconds to see if a retry succeeds
        }
      } else {
        // Check if this is a response with warning/error information
        if (enrichedData && (enrichedData.warning || enrichedData.possible_reasons || enrichedData.likely_expired)) {
          logger.warn('Enrichment warning/info:', enrichedData);
          
          let warningMessage = language === 'nl' ? 
            "Geen verrijkingsgegevens beschikbaar" : 
            "No enrichment data available";
          
          if (enrichedData.possible_reasons && enrichedData.possible_reasons.includes("Request ID may have expired - try refreshing contacts")) {
            warningMessage += language === 'nl' ? 
              " - Probeer contacten te vernieuwen" : 
              " - Try refreshing contacts";
          }
          
          alert(warningMessage);
        } else {
          // No enriched data available (0 credits case)
          const message = language === 'nl' ? 
            "Geen aanvullende gegevens voor dit contact (0 credits gebruikt)" : 
            "No additional data for this contact (0 credits used)";
          alert(message);
        }
      }
    } catch (error) {
      logger.error('Error enriching contact:', error);
      logger.error('Error response data:', error.response?.data);
      logger.error('Error status:', error.response?.status);
      
      let errorMessage = 'Failed to enrich contact: ';
      if (error.response?.data?.error) {
        errorMessage += error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage += error.response.data.message;
      } else {
        errorMessage += error.message || 'Unknown error';
      }
      
      alert(errorMessage);
    } finally {
      setEnrichLoading(false);
      setEnrichingContacts(prev => {
        const newSet = new Set(prev);
        newSet.delete(contactId);
        return newSet;
      });
    }
  };

  // Function to handle contact details view
  const handleViewContactDetails = (contact) => {
    setSelectedContact(contact);
    setContactDetailsOpen(true);
  };

  // Function to handle enrichment modal close
  const handleEnrichmentModalClose = () => {
    setEnrichmentModal({
      open: false,
      data: null
    });
  };

  // Function to create outreach from contact data with specific method and detail
  const handleAddOutreachFromContact = (contact, specificMethod = null, specificDetail = null) => {
    let outreachMethod = specificMethod || 'phone';
    let contactDetail = specificDetail || '';
    let contactName = contact.fullName || contact.displayName || `${contact.firstName || ''} ${contact.lastName || ''}`.trim();

    // Build comprehensive notes with available contact methods
    let notes = `Contact enriched from Lusha. Company: ${contact.companyName || contact.company?.name || 'Unknown'}`;
    
    // Add all available contact methods to notes for reference
    const availableMethods = [];
    if (contact.emailAddresses && contact.emailAddresses.length > 0) {
      availableMethods.push(`Email: ${contact.emailAddresses.map(e => e.email).join(', ')}`);
    }
    if (contact.phoneNumbers && contact.phoneNumbers.length > 0) {
      const phones = contact.phoneNumbers.map(phoneObj => {
        const possiblePhoneFields = [
          phoneObj?.phoneNumber,
          phoneObj?.phone,
          phoneObj?.number,
          phoneObj?.internationalPhoneNumber,
          phoneObj?.localPhoneNumber,
          phoneObj?.formattedPhoneNumber,
          typeof phoneObj === 'string' ? phoneObj : null
        ];
        return possiblePhoneFields.find(field => field && field.trim() && field !== 'null');
      }).filter(Boolean);
      
      if (phones.length > 0) {
        availableMethods.push(`Phone: ${phones.join(', ')}`);
      }
    }
    if (contact.linkedInUrl || contact.linkedin || contact.linkedinUrl) {
      availableMethods.push(`LinkedIn: ${contact.linkedInUrl || contact.linkedin || contact.linkedinUrl}`);
    }
    
    if (availableMethods.length > 0) {
      notes += `\n\nAvailable contact methods:\n${availableMethods.join('\n')}`;
    }

    // Add seniority and department info if available
    if (contact.seniority && Array.isArray(contact.seniority) && contact.seniority.length > 0) {
      const seniorityText = contact.seniority.map(s => s.value || s).join(', ');
      notes += `\nSeniority: ${seniorityText}`;
    }
    if (contact.departments && contact.departments.length > 0) {
      notes += `\nDepartments: ${contact.departments.join(', ')}`;
    }

    // Pre-populate the form
    const formDataToSet = {
      contact_name: contactName,
      outreach_method: outreachMethod,
      contact_detail: contactDetail,
      outreach_date: new Date(),
      reached_intended_contact: false,
      followup_date: null,
      meeting_scheduled: false,
      meeting_date: null,
      notes: notes,
      completed: false,
      title: `${contactName} - ${contact.companyName || contact.company?.name || 'Contact'}`
    };

    console.log('📝 Setting form data:', formDataToSet);
    setFormData(formDataToSet);

    // Close the enrichment modal and open the outreach form
    handleEnrichmentModalClose();
    if (isMobile) {
      setActiveTab(1);
    }
    setEditMode(false);
    setCurrentEditId(null);
    setShowForm(true);
  };

  // Function to extract company logo from Lusha contacts
  const extractCompanyLogo = (contacts) => {
    if (!contacts || contacts.length === 0) return null;
    
    // Try to find a logo from any contact
    for (const contact of contacts) {
      if (contact.logoUrl) {
        return contact.logoUrl;
      }
      // Also check alternative logo fields
      if (contact.companyLogo) {
        return contact.companyLogo;
      }
      if (contact.company?.logoUrl) {
        return contact.company.logoUrl;
      }
      if (contact.company?.logo) {
        return contact.company.logo;
      }
    }
    
    return null;
  };

  // Function to copy text to clipboard
  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      showNotification(`${label} copied to clipboard!`, 'success');
    } catch (err) {
      console.error('Failed to copy text: ', err);
      showNotification(`Failed to copy ${label}`, 'error');
    }
  };

  // Function to get paginated contacts
  // Function to handle Lusha filter changes
  const handleLushaFilterChange = (filterType, values) => {
    const currentSearchText = lushaFilters.searchText || '';
    const currentSeniorities = lushaFilters.seniority_levels || [];
    
    setLushaFilters(prev => ({
      ...prev,
      [filterType]: values
    }));

    // Apply filters to existing contacts if they exist
    if (lushaContacts) {
      // Use the unified function with current values to avoid timing issues
      if (filterType === 'seniority_levels') {
        applyLushaFiltersWithSeniority(currentSearchText, values);
      } else {
        applyLushaFiltersWithSeniority(currentSearchText, currentSeniorities);
      }
    }
  };

  // Function to handle text search filter
  const handleTextFilterChange = (searchText) => {
    setLushaFilters(prev => ({
      ...prev,
      searchText: searchText
    }));

    // Apply filters to existing contacts if they exist
    if (lushaContacts) {
      // Pass the current search text directly to avoid state timing issues
      applyLushaFiltersWithSeniority(searchText, lushaFilters.seniority_levels, lushaFilters.departments);
    }
  };

  // Function to toggle seniority filter (checkbox style)
  const toggleSeniorityFilter = (seniorityLevel) => {
    const currentSeniorities = lushaFilters.seniority_levels || [];
    const currentSearchText = lushaFilters.searchText || '';
    const currentDepartments = lushaFilters.departments || [];
    
    let newSeniorities;
    if (currentSeniorities.includes(seniorityLevel)) {
      // Remove if already selected
      newSeniorities = currentSeniorities.filter(s => s !== seniorityLevel);
    } else {
      // Add if not selected
      newSeniorities = [...currentSeniorities, seniorityLevel];
    }
    
    // Update state
    setLushaFilters(prev => ({
      ...prev,
      seniority_levels: newSeniorities
    }));

    // Apply filters immediately with the new seniority levels
    if (lushaContacts) {
      applyLushaFiltersWithSeniority(currentSearchText, newSeniorities, currentDepartments);
    }
  };

  // Function to toggle department filter (checkbox style)
  const toggleDepartmentFilter = (department) => {
    const currentDepartments = lushaFilters.departments || [];
    const currentSearchText = lushaFilters.searchText || '';
    const currentSeniorities = lushaFilters.seniority_levels || [];
    
    let newDepartments;
    if (currentDepartments.includes(department)) {
      // Remove if already selected
      newDepartments = currentDepartments.filter(d => d !== department);
    } else {
      // Add if not selected
      newDepartments = [...currentDepartments, department];
    }
    
    // Update state
    setLushaFilters(prev => ({
      ...prev,
      departments: newDepartments
    }));

    // Apply filters immediately with the new departments
    if (lushaContacts) {
      applyLushaFiltersWithSeniority(currentSearchText, currentSeniorities, newDepartments);
    }
  };

  // Function to apply filters to contacts with explicit seniority levels
  const applyLushaFiltersWithSeniority = (searchText = null, seniorityLevels = null, departments = null, preserveOrder = false) => {
    const contacts = lushaContacts || [];
    
    // Use provided values or fall back to state
    const effectiveSearchText = searchText !== null ? searchText : lushaFilters.searchText;
    const effectiveSeniorityLevels = seniorityLevels !== null ? seniorityLevels : lushaFilters.seniority_levels;
    const effectiveDepartments = departments !== null ? departments : lushaFilters.departments;
    
    // Reset to first page when filters are applied
    setLushaContactsPage(0);
    
    let filtered = [...contacts];

    // Apply text search filter first
    if (effectiveSearchText && effectiveSearchText.trim()) {
      const searchLower = effectiveSearchText.toLowerCase().trim();
      filtered = filtered.filter(contact => {
        const searchableText = [
          contact.fullName,
          contact.displayName,
          contact.firstName,
          contact.lastName,
          contact.jobTitle,
          contact.title,
          contact.workEmail,
          contact.email,
          contact.personalEmail,
          contact.company,
          contact.companyName,
          (contact.company && contact.company.companyName)
        ].filter(Boolean).join(' ').toLowerCase();
        
        return searchableText.includes(searchLower);
      });
    }

    // Apply seniority level filter
    if (effectiveSeniorityLevels && effectiveSeniorityLevels.length > 0) {
      logger.log(`Applying seniority filter:`, effectiveSeniorityLevels);
      logger.log(`Original contacts count: ${contacts.length}`);
      
      filtered = filtered.filter(contact => {
        // Handle different seniority field structures
        let contactSeniority = null;
        
        // Check for different possible seniority field formats
        if (contact.seniority) {
          if (Array.isArray(contact.seniority) && contact.seniority.length > 0) {
            // Enriched contact format: [{value: "C-Level"}]
            contactSeniority = contact.seniority[0]?.value;
          } else if (typeof contact.seniority === 'string') {
            // Direct string format
            contactSeniority = contact.seniority;
          }
        }
        
        // Try alternative field names that might contain seniority info
        if (!contactSeniority) {
          contactSeniority = contact.seniorityLevel || contact.level || contact.seniority_level;
        }
        
        // Fallback: try to infer from job title if no explicit seniority field
        if (!contactSeniority && (contact.jobTitle || contact.title || contact.job_title)) {
          const title = (contact.jobTitle || contact.title || contact.job_title).toLowerCase();
          
          // C-Suite detection
          if (title.includes('ceo') || title.includes('chief executive') || 
              title.includes('cfo') || title.includes('chief financial') ||
              title.includes('cto') || title.includes('chief technology') ||
              title.includes('coo') || title.includes('chief operating') ||
              title.includes('cmo') || title.includes('chief marketing') ||
              title.includes('chief') || title.includes('president')) {
            contactSeniority = 'c-suite';
          }
          // Founder detection
          else if (title.includes('founder') || title.includes('co-founder')) {
            contactSeniority = 'founder';
          }
          // VP detection
          else if (title.includes('vice president') || title.includes('vp ') || title.includes('v.p.')) {
            contactSeniority = 'vice president';
          }
          // Director detection
          else if (title.includes('director') || title.includes('head of')) {
            contactSeniority = 'director';
          }
          // Manager detection
          else if (title.includes('manager') || title.includes('lead') || title.includes('supervisor')) {
            contactSeniority = 'manager';
          }
          // Senior detection
          else if (title.includes('senior') || title.includes('sr.') || 
                   title.includes('principal') || title.includes('specialist')) {
            contactSeniority = 'senior';
          }
          // Default to entry level
          else {
            contactSeniority = 'entry';
          }
        }
        
        const isMatch = contactSeniority && effectiveSeniorityLevels.includes(contactSeniority);
        
        return isMatch;
      });
    }
    
    // Apply department filter
    if (effectiveDepartments && effectiveDepartments.length > 0) {
      filtered = filtered.filter(contact => {
        if (!contact.departments || !Array.isArray(contact.departments)) {
          return false; // No departments, doesn't match filter
        }
        
        // Check if any of the contact's departments match the filter
        const hasMatchingDepartment = contact.departments.some(dept => 
          effectiveDepartments.includes(dept.trim())
        );
        
        return hasMatchingDepartment;
      });
    }
    
    // Sort the filtered contacts by seniority before storing (unless preserving order)
    const finalFiltered = preserveOrder ? filtered : sortContactsBySeniority(filtered);
    
    
    setFilteredLushaContacts(finalFiltered);
  };

  // Function to apply filters to contacts with explicit search text
  const applyLushaFiltersWithText = (searchText = null) => {
    applyLushaFiltersWithSeniority(searchText, null);
  };

  // Function to get contact seniority level for display
  const getContactSeniorityLevel = (contact) => {
    // Handle different seniority field structures
    let contactSeniority = null;
    
    if (contact.seniority) {
      if (Array.isArray(contact.seniority) && contact.seniority.length > 0) {
        contactSeniority = contact.seniority[0]?.value;
      } else if (typeof contact.seniority === 'string') {
        contactSeniority = contact.seniority;
      }
    }
    
    if (!contactSeniority) {
      contactSeniority = contact.seniorityLevel || contact.level || contact.seniority_level;
    }
    
    // Fallback: infer from job title
    if (!contactSeniority && (contact.jobTitle || contact.title || contact.job_title)) {
      const title = (contact.jobTitle || contact.title || contact.job_title).toLowerCase();
      
      if (title.includes('ceo') || title.includes('chief executive') || 
          title.includes('cfo') || title.includes('chief financial') ||
          title.includes('cto') || title.includes('chief technology') ||
          title.includes('coo') || title.includes('chief operating') ||
          title.includes('cmo') || title.includes('chief marketing') ||
          title.includes('chief') || title.includes('president')) {
        contactSeniority = 'c-suite';
      } else if (title.includes('founder') || title.includes('co-founder')) {
        contactSeniority = 'founder';
      } else if (title.includes('vice president') || title.includes('vp ') || title.includes('v.p.')) {
        contactSeniority = 'vice president';
      } else if (title.includes('director') || title.includes('head of')) {
        contactSeniority = 'director';
      } else if (title.includes('manager') || title.includes('lead') || title.includes('supervisor')) {
        contactSeniority = 'manager';
      } else if (title.includes('senior') || title.includes('sr.') || 
                 title.includes('principal') || title.includes('specialist')) {
        contactSeniority = 'senior';
      } else {
        contactSeniority = 'entry';
      }
    }
    
    return contactSeniority || 'entry';
  };

  // Function to sort contacts by enrichment status first, then by seniority level
  const sortContactsBySeniority = (contacts) => {
    const seniorityOrder = {
      'c-suite': 1,
      'founder': 2,
      'vice president': 3,
      'director': 4,
      'manager': 5,
      'senior': 6,
      'entry': 7
    };
    
    return contacts.sort((a, b) => {
      // First priority: enriched contacts come first
      const aEnriched = a.isEnriched || a.enriched;
      const bEnriched = b.isEnriched || b.enriched;
      
      if (aEnriched !== bEnriched) {
        return bEnriched ? 1 : -1; // Enriched contacts first
      }
      
      // Second priority: seniority level
      const seniorityA = getContactSeniorityLevel(a);
      const seniorityB = getContactSeniorityLevel(b);
      
      const orderA = seniorityOrder[seniorityA] || 8;
      const orderB = seniorityOrder[seniorityB] || 8;
      
      if (orderA !== orderB) {
        return orderA - orderB;
      }
      
      // Third priority: sort by name
      const nameA = a.fullName || a.displayName || `${a.firstName || ''} ${a.lastName || ''}`.trim();
      const nameB = b.fullName || b.displayName || `${b.firstName || ''} ${b.lastName || ''}`.trim();
      
      return nameA.localeCompare(nameB);
    });
  };

  // Function to get available seniority levels from current contacts
  const getAvailableSeniorityLevels = () => {
    const contacts = lushaContacts || [];
    const seniorityLevels = new Set();
    
    contacts.forEach(contact => {
      const seniority = getContactSeniorityLevel(contact);
      if (seniority) {
        seniorityLevels.add(seniority);
      }
    });
    
    // Return in priority order
    const seniorityOrder = ['c-suite', 'founder', 'vice president', 'director', 'manager', 'senior', 'entry'];
    return seniorityOrder.filter(level => seniorityLevels.has(level));
  };

  // Function to get available departments from current contacts
  const getAvailableDepartments = () => {
    const contacts = lushaContacts || [];
    const departments = new Set();
    
    contacts.forEach(contact => {
      if (contact.departments && Array.isArray(contact.departments)) {
        contact.departments.forEach(dept => {
          if (dept && dept.trim()) {
            departments.add(dept.trim());
          }
        });
      }
    });
    
    // Return sorted alphabetically
    return Array.from(departments).sort();
  };

  // Function to get contacts to display (filtered or all)
  const getContactsToDisplay = () => {
    const hasActiveFilters = (lushaFilters.seniority_levels && lushaFilters.seniority_levels.length > 0) ||
                            (lushaFilters.searchText && lushaFilters.searchText.trim()) ||
                            (lushaFilters.departments && lushaFilters.departments.length > 0);
    
    if (hasActiveFilters) {
      return filteredLushaContacts || [];
    } else {
      return sortContactsBySeniority(lushaContacts || []);
    }
  };

  // Function to clear all filters
  const clearLushaFilters = () => {
    setLushaFilters({
      searchText: '',
      seniority_levels: [], // Clear all filters - show all contacts
      departments: [] // Clear department filters
    });
    
    // Reset to first page when clearing filters
    setLushaContactsPage(0);
    
    // Clear filtered contacts to show all
    setFilteredLushaContacts(null);
  };

  const getPaginatedLushaContacts = () => {
    const contactsToDisplay = getContactsToDisplay();
    if (!contactsToDisplay.length) return { contacts: [], totalContacts: 0, totalPages: 0 };
    
    const startIndex = lushaContactsPage * lushaContactsPerPage;
    const endIndex = startIndex + lushaContactsPerPage;
    
    return {
      contacts: contactsToDisplay.slice(startIndex, endIndex),
      totalContacts: contactsToDisplay.length,
      totalPages: Math.ceil(contactsToDisplay.length / lushaContactsPerPage)
    };
  };

  // Function to handle page change
  const handleLushaPageChange = (event, newPage) => {
    setLushaContactsPage(newPage - 1);
  };

  // Function to get avatar text for contacts
  const getContactAvatarText = (contact) => {
    const name = contact.fullName || contact.displayName || `${contact.firstName || ''} ${contact.lastName || ''}`.trim();
    if (!name) return 'C';
    
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  };

  // Fetch Lusha contacts when component mounts, recordId changes, or websiteUrl becomes available
  useEffect(() => {
    if (recordId && !lushaLoading) {
      // Only fetch if we have a domain (websiteUrl) or company name, and haven't loaded contacts yet
      // This ensures we fetch when URL is extracted and set in websiteUrl state
      if ((websiteUrl || companyName) && !lushaContacts) {
        logger.log('Triggering fetchLushaContacts - recordId:', recordId, 'websiteUrl:', websiteUrl, 'companyName:', companyName);
        fetchLushaContacts();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordId, websiteUrl, companyName]);
  
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ px: { xs: 1, sm: 2 }, pt: { xs: 0, sm: 2 }, pb: isMobile ? 9 : 2 }}>

        {/* Show company-specific header when viewing a specific company */}
        {recordId && companyName && (
          <>
            <Box sx={{
              mb: { sm: 3 },
              ...(isMobile && {
                position: 'fixed',
                top: `${HEADER_HEIGHT.xs}px`,
                left: 0,
                right: 0,
                zIndex: (theme) => theme.zIndex.modal,
                bgcolor: 'background.default',
                px: 1,
                pt: 1,
                pb: 1,
                borderBottom: 1,
                borderColor: 'divider',
              }),
            }}>
              <Paper elevation={0} sx={{ p: { xs: 1, sm: 1.5 }, bgcolor: 'background.paper', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, minWidth: 0 }}>
                {isMobile && (
                  <IconButton onClick={onClose} size="small" sx={{ flexShrink: 0 }}>
                    <ArrowBackIcon />
                  </IconButton>
                )}
                {companyLogo && (
                  <Avatar
                    src={companyLogo}
                    sx={{
                      width: { xs: 28, sm: 32 },
                      height: { xs: 28, sm: 32 },
                      flexShrink: 0,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                    alt={`${companyName} logo`}
                  >
                    <BusinessIcon fontSize="small" />
                  </Avatar>
                )}
                <Typography
                  variant={isMobile ? 'subtitle1' : 'h6'}
                  component="h2"
                  sx={{
                    fontWeight: 'bold',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                    flexShrink: 1,
                  }}
                >
                  {companyName}
                </Typography>
                {websiteUrl && !editingUrl && (
                  <Link
                    href={getWebsiteHref(websiteUrl)}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.25,
                      ml: 'auto',
                      flexShrink: 0,
                      textDecoration: 'none',
                      color: 'primary.main',
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    {getWebsiteLabel(websiteUrl)}
                    <OpenInNewIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
                  </Link>
                )}
                {!isMobile && (
                  <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, ml: websiteUrl && !editingUrl ? 0 : 'auto' }}>
                    {websiteUrl && !editingUrl && (
                      <Tooltip title="Edit website URL">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setNewUrl(websiteUrl);
                            setEditingUrl(true);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {!websiteUrl && !editingUrl && (
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        onClick={() => {
                          setEditingUrl(true);
                          setNewUrl('');
                        }}
                        startIcon={<AddIcon fontSize="small" />}
                      >
                        Add URL
                      </Button>
                    )}
                    <Tooltip title="Close">
                      <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Box>

              {editingUrl && !isMobile && (
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mt: 1.5 }}>
                  <TextField
                    value={newUrl}
                    onChange={(e) => setNewUrl(e.target.value)}
                    placeholder="Enter website URL"
                    size="small"
                    fullWidth
                    autoFocus={openWithUrlSearch}
                  />
                  <Button variant="contained" color="primary" size="small" onClick={updateWebsiteUrl}>
                    Save
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() => {
                      setNewUrl(websiteUrl);
                      setEditingUrl(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    onClick={() => setNewUrl('')}
                    startIcon={<CancelIcon fontSize="small" />}
                  >
                    Clear
                  </Button>
                </Box>
              )}
            </Paper>
          </Box>
            {isMobile && <Box sx={{ height: MOBILE_COMPANY_HEADER_SPACER }} aria-hidden />}
          </>
        )}

        {/* Desktop tabs */}
        {!isMobile && (
          <Box sx={{ width: '100%', mb: 2 }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
            >
              <Tab label="Technology Insights" />
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Outreach Activity
                    {outreaches.length > 0 && (
                      <Badge 
                        color="secondary" 
                        badgeContent={outreaches.length} 
                        sx={{ ml: 2 }}
                        max={99}
                      />
                    )}
                  </Box>
                } 
              />
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Enriched Contacts
                    {lushaContacts && lushaContacts.length > 0 && (
                      <Badge 
                        color="primary" 
                        badgeContent={lushaContacts.length} 
                        sx={{ ml: 2 }}
                        max={99}
                      />
                    )}
                  </Box>
                } 
              />
              <Tab label="AI Company Research" />
            </Tabs>
          </Box>
        )}

        {/* Mobile bottom navigation — visual order: Tech, Contacts, Outreach, AI */}
        {isMobile && (() => {
          const mobileToTab = [0, 2, 1, 3];
          const tabToMobile = [0, 2, 1, 3];
          return (
            <BottomNavigation
              value={tabToMobile[activeTab]}
              onChange={(_, mobileIdx) => setActiveTab(mobileToTab[mobileIdx])}
              showLabels
              sx={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: (theme) => theme.zIndex.modal + 1,
                borderTop: 1,
                borderColor: 'divider',
                '& .MuiBottomNavigationAction-root': {
                  minWidth: 0,
                  px: 0.5,
                },
                '& .MuiBottomNavigationAction-label': {
                  fontSize: '0.625rem',
                  '&.Mui-selected': { fontSize: '0.6875rem' },
                },
              }}
            >
              <BottomNavigationAction label="Tech" icon={<InsightsIcon />} />
              <BottomNavigationAction
                label="Contacts"
                icon={
                  <Badge badgeContent={lushaContacts?.length || 0} color="primary" max={99}>
                    <PeopleIcon />
                  </Badge>
                }
              />
              <BottomNavigationAction
                label="Outreach"
                icon={
                  <Badge badgeContent={outreaches.length || 0} color="secondary" max={99}>
                    <PhoneIcon />
                  </Badge>
                }
              />
              <BottomNavigationAction label="AI Research" icon={<AutoAwesomeIcon />} />
            </BottomNavigation>
          );
        })()}



        {!recordId && activeTab === 1 && (
          <OutreachStats refreshKey={statsRefreshKey} />
        )}
        
        {/* Company Information */}
        
        {/* DNS Information Card - Only show on Email Security Tab */}
        {recordId && activeTab === 0 && (
          <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="subtitle1" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <DnsIcon sx={{ mr: 1 }} fontSize="small" />
                  Publicly available Tech Stack Information
                </Typography>
                
                {lastDnsCheckTime && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -1, mb: 1 }}>
                    Last checked: {new Date(lastDnsCheckTime).toLocaleString()}
                  </Typography>
                )}
                
                {dnsLoading && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    <Typography variant="body2">Checking DNS records...</Typography>
                  </Box>
                )}
                
                {dnsError && !dnsLoading && (
                  <Box sx={{ mt: 1 }}>
                    <Typography color="error" variant="body2" gutterBottom>
                      {dnsError}
                    </Typography>
                  </Box>
                )}
                
                {dnsData && !dnsLoading && !dnsError && (
                  <Box sx={{ mt: 1 }}>
                    {/* Enhanced Email Provider Info with Logo */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Email Provider:
                      </Typography>
                      
                      {/* Container for Email Provider and MDM cards in row */}
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: 2
                      }}>
                        {/* Email Provider with Logo */}
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          mt: 1,
                          p: 1.5,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          bgcolor: 'background.paper',
                          flexGrow: 1
                        }}>
                          {/* Provider Icon/Logo - Larger and more prominent */}
                          <Box sx={{ mr: 2 }}>
                            {dnsData.email?.provider === 'M365' && (
                              <MicrosoftIcon sx={{ fontSize: 36, color: '#0078d4' }} />
                            )}
                            {dnsData.email?.provider === 'M365_Filtered' && (
                              <Box sx={{ position: 'relative' }}>
                                <MicrosoftIcon sx={{ fontSize: 36, color: '#8661C5' }} />
                                <Box 
                                  sx={{ 
                                    position: 'absolute', 
                                    top: -8, 
                                    right: -8, 
                                    bgcolor: '#f8f9fa', 
                                    borderRadius: '50%',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    p: 0.5
                                  }}
                                >
                                  <FilterIcon sx={{ fontSize: 16, color: '#1976d2' }} />
                                </Box>
                              </Box>
                            )}
                            {dnsData.email?.provider === 'Google' && (
                              <GoogleIcon sx={{ fontSize: 36, color: '#4285F4' }} />
                            )}
                            {dnsData.email?.provider === 'Hybrid' && (
                              <Box sx={{ display: 'flex' }}>
                                <MicrosoftIcon sx={{ fontSize: 36, color: '#0078d4', mr: 1 }} />
                                <GoogleIcon sx={{ fontSize: 36, color: '#4285F4' }} />
                              </Box>
                            )}
                            {dnsData.email?.provider === 'Other' && (
                              <EmailIcon sx={{ fontSize: 36, color: '#FF9800' }} />
                            )}
                            {!dnsData.email?.provider && (
                              <QuestionMarkIcon sx={{ fontSize: 36, color: '#888' }} />
                            )}
                          </Box>
                          
                          {/* Provider Name and Details */}
                          <Box>
                            <Typography variant="subtitle1" fontWeight="medium">
                              {dnsData.email?.provider === 'M365' ? 'Microsoft 365' : 
                              dnsData.email?.provider === 'Google' ? 'Google Workspace' :
                              dnsData.email?.provider === 'Hybrid' ? 'Hybrid (M365 & Google)' :
                              dnsData.email?.provider === 'M365_Filtered' ? 'Microsoft 365 (Filtered)' :
                              dnsData.email?.provider === 'Other' ? 'Other Provider' :
                              'Unknown'}
                            </Typography>
                            
                            <Typography variant="body2" color="text.secondary">
                              {dnsData.email?.provider === 'M365' ? 'Exchange Online' : 
                              dnsData.email?.provider === 'Google' ? 'Gmail for Business' :
                              dnsData.email?.provider === 'Hybrid' ? 'Multiple email systems' :
                              dnsData.email?.provider === 'M365_Filtered' ? 'Through mail filtering service' :
                              dnsData.email?.provider === 'Other' ? 'Custom email system' :
                              'Email provider could not be detected'}
                            </Typography>
                            
                            {/* MX Records in tooltip */}
                            {dnsData.email?.records && dnsData.email.records.length > 0 && (
                              <Tooltip
                                title={
                                  <Box sx={{ 
                                    whiteSpace: 'pre-line', 
                                    p: 1, 
                                    fontSize: '0.75rem', 
                                    bgcolor: 'white', 
                                    color: 'text.primary',
                                    borderRadius: 1,
                                    boxShadow: '0px 2px 6px rgba(0,0,0,0.15)'
                                  }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                      MX Records:
                                    </Typography>
                                    {dnsData.email.records.map((record, idx) => (
                                      <Typography key={idx} variant="body2" sx={{ fontSize: '0.75rem' }}>
                                        {record}
                                      </Typography>
                                    ))}
                                  </Box>
                                }
                                arrow
                                componentsProps={{
                                  tooltip: {
                                    sx: {
                                      bgcolor: 'transparent',
                                      p: 0,
                                      '& .MuiTooltip-arrow': {
                                        color: 'white'
                                      }
                                    }
                                  }
                                }}
                              >
                                <Button 
                                  variant="text" 
                                  size="small" 
                                  sx={{ 
                                    textTransform: 'none', 
                                    py: 0, 
                                    minWidth: 'auto', 
                                    fontWeight: 'normal', 
                                    color: 'text.secondary',
                                    fontSize: '0.75rem',
                                    mt: 0.5
                                  }}
                                >
                                  View MX Records
                                </Button>
                              </Tooltip>
                            )}
                          </Box>
                        </Box>
                        
                        {/* MDM Status Card - Only show when MDM/Intune is active */}
                        {dnsData.enterprise?.records && dnsData.enterprise.records.length > 0 && (
                          <Tooltip
                            title={
                              <Box sx={{ 
                                whiteSpace: 'pre-line', 
                                p: 1, 
                                fontSize: '0.75rem', 
                                bgcolor: 'white', 
                                color: 'text.primary',
                                borderRadius: 1,
                                boxShadow: '0px 2px 6px rgba(0,0,0,0.15)'
                              }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                  Microsoft Intune (MDM) Configuration
                                </Typography>
                                {dnsData.enterprise?.records && dnsData.enterprise.records.length > 0 && (
                                  <>
                                    <Typography variant="body2" sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                                      Enterprise Records:
                                    </Typography>
                                    {dnsData.enterprise.records.map((record, idx) => (
                                      <Typography key={idx} variant="body2" sx={{ fontSize: '0.75rem' }}>
                                        {record}
                                      </Typography>
                                    ))}
                                  </>
                                )}
                                
                                <Divider sx={{ my: 1 }} />
                                
                                <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 'medium', color: 'info.main' }}>
                                  Note: A complete MDM setup requires both enterpriseenrollment and enterpriseregistration DNS records.
                                </Typography>
                                
                                <Box sx={{ mt: 1 }}>
                                  <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                    • enterpriseenrollment - Required for device enrollment
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                    • enterpriseregistration - Required for Azure AD registration
                                  </Typography>
                                </Box>
                              </Box>
                            }
                            arrow
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  bgcolor: 'transparent',
                                  p: 0,
                                  '& .MuiTooltip-arrow': {
                                    color: 'white'
                                  }
                                }
                              }
                            }}
                          >
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              mt: 1,
                              p: 1.5,
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                              bgcolor: 'background.paper',
                              minWidth: { xs: '100%', sm: '200px' }
                            }}>
                              {/* MDM Icon */}
                              <Box sx={{ mr: 2 }}>
                                <DevicesIcon sx={{ fontSize: 36, color: '#107C10' }} />
                              </Box>
                              
                              {/* MDM Status */}
                              <Box sx={{ flexGrow: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="subtitle1" fontWeight="medium">
                                    Modern Workplace
                                  </Typography>
                                  
                                  {/* Show "Complete" or "Partial" badge */}
                                  {dnsData.enterprise?.records?.some(r => r.includes('Enterprise-Enrollment')) && 
                                   dnsData.enterprise?.records?.some(r => r.includes('Enterprise-Registration')) ? (
                                    <Chip
                                      size="small"
                                      label="Complete"
                                      color="success"
                                      sx={{ height: 20, fontSize: '0.65rem' }}
                                    />
                                  ) : (
                                    <Chip
                                      size="small"
                                      label="Partial Setup"
                                      color="warning"
                                      sx={{ height: 20, fontSize: '0.65rem' }}
                                    />
                                  )}
                                </Box>
                                <Typography variant="body2" color="text.secondary">
                                  Microsoft Intune MDM
                                </Typography>
                                
                                {/* Status Indicators for Enterprise Records */}
                                <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                  {/* Check for enterprise enrollment */}
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {dnsData.enterprise?.records?.some(r => r.includes('Enterprise-Enrollment')) ? (
                                      <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
                                    ) : (
                                      <CancelIcon fontSize="small" sx={{ color: 'error.main' }} />
                                    )}
                                    <Typography variant="caption" color="text.secondary">
                                      enterpriseenrollment
                                    </Typography>
                                  </Box>
                                  
                                  {/* Check for enterprise registration */}
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {dnsData.enterprise?.records?.some(r => r.includes('Enterprise-Registration')) ? (
                                      <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
                                    ) : (
                                      <CancelIcon fontSize="small" sx={{ color: 'error.main' }} />
                                    )}
                                    <Typography variant="caption" color="text.secondary">
                                      enterpriseregistration
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            </Box>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                    
                    {/* Enhanced security status chips with detailed tooltips - MOVED UP FROM BELOW */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, mt: 1 }}>
                      <Tooltip 
                        title={
                          <Box sx={{ 
                            whiteSpace: 'pre-line', 
                            p: 1, 
                            fontSize: '0.75rem', 
                            bgcolor: 'white', 
                            color: 'text.primary',
                            borderRadius: 1,
                            boxShadow: '0px 2px 6px rgba(0,0,0,0.15)'
                          }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                              {dnsData.spf?.found ? "SPF Record Found" : "No SPF Record"}
                            </Typography>
                            {dnsData.spf?.record && (
                              <Typography variant="body2" sx={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>
                                {dnsData.spf.record}
                              </Typography>
                            )}
                            <Typography variant="body2" sx={{ mt: 0.5, color: dnsData.spf?.found ? '#4caf50' : '#d32f2f', fontSize: '0.75rem' }}>
                              {dnsData.spf?.found 
                                ? "SPF configured - Helps prevent email spoofing" 
                                : "SPF not configured - Susceptible to email spoofing"}
                            </Typography>
                            {hasRecentSecurityChanges('spf') && (
                              <>
                                <Divider sx={{ my: 1 }} />
                                <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#ff9800' }}>
                                  Recent Changes:
                                </Typography>
                                <Typography variant="body2" sx={{ fontSize: '0.7rem', color: '#ff9800' }}>
                                  {getRecentSecurityChange('spf')?.change_summary} - {formatTimeAgo(getRecentSecurityChange('spf')?.detected_at)}
                                </Typography>
                              </>
                            )}
                          </Box>
                        }
                        arrow
                        componentsProps={{
                          tooltip: {
                            sx: {
                              bgcolor: 'transparent',
                              p: 0,
                              '& .MuiTooltip-arrow': {
                                color: 'white'
                              }
                            }
                          }
                        }}
                      >
                        <Chip 
                          size="small"
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              SPF
                              {renderChangeDirectionIcon('spf')}
                            </Box>
                          }
                          color={dnsData.spf?.found ? "success" : "error"}
                          variant={dnsData.spf?.found ? "filled" : "outlined"}
                          sx={{ 
                            height: 36,
                            '& .MuiChip-label': {
                              display: 'flex',
                              alignItems: 'center'
                            }
                          }}
                        />
                      </Tooltip>
                      
                      <Tooltip 
                        title={
                          <Box sx={{ 
                            whiteSpace: 'pre-line', 
                            p: 1, 
                            fontSize: '0.75rem', 
                            bgcolor: 'white', 
                            color: 'text.primary',
                            borderRadius: 1,
                            boxShadow: '0px 2px 6px rgba(0,0,0,0.15)'
                          }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                              {dnsData.dkim?.found ? "DKIM Records Found" : "No DKIM Records"}
                            </Typography>
                            {dnsData.dkim?.selectors && dnsData.dkim.selectors.length > 0 && (
                              <>
                                <Typography variant="body2" sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                                  DKIM Selectors:
                                </Typography>
                                {dnsData.dkim.selectors.map((selector, idx) => (
                                  <Typography key={idx} variant="body2" sx={{ fontSize: '0.75rem' }}>
                                    {selector}
                                  </Typography>
                                ))}
                              </>
                            )}
                            <Typography variant="body2" sx={{ mt: 0.5, color: dnsData.dkim?.found ? '#4caf50' : '#d32f2f', fontSize: '0.75rem' }}>
                              {dnsData.dkim?.found 
                                ? "DKIM configured - Helps verify email integrity" 
                                : "DKIM not configured - Email integrity not verified"}
                            </Typography>
                            {hasRecentSecurityChanges('dkim') && (
                              <>
                                <Divider sx={{ my: 1 }} />
                                <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#ff9800' }}>
                                  Recent Changes:
                                </Typography>
                                <Typography variant="body2" sx={{ fontSize: '0.7rem', color: '#ff9800' }}>
                                  {getRecentSecurityChange('dkim')?.change_summary} - {formatTimeAgo(getRecentSecurityChange('dkim')?.detected_at)}
                                </Typography>
                              </>
                            )}
                          </Box>
                        }
                        arrow
                        componentsProps={{
                          tooltip: {
                            sx: {
                              bgcolor: 'transparent',
                              p: 0,
                              '& .MuiTooltip-arrow': {
                                color: 'white'
                              }
                            }
                          }
                        }}
                      >
                        <Chip 
                          size="small"
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              DKIM
                              {renderChangeDirectionIcon('dkim')}
                            </Box>
                          }
                          color={dnsData.dkim?.found ? "success" : "error"}
                          variant={dnsData.dkim?.found ? "filled" : "outlined"}
                          sx={{ 
                            height: 36,
                            '& .MuiChip-label': {
                              display: 'flex',
                              alignItems: 'center'
                            }
                          }}
                        />
                      </Tooltip>
                      
                      <Tooltip 
                        title={
                          <Box sx={{ 
                            whiteSpace: 'pre-line', 
                            p: 1, 
                            fontSize: '0.75rem', 
                            bgcolor: 'white', 
                            color: 'text.primary',
                            borderRadius: 1,
                            boxShadow: '0px 2px 6px rgba(0,0,0,0.15)'
                          }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                              {dnsData.dmarc?.found 
                                ? `DMARC Policy: ${dnsData.dmarc.policy || 'none'}` 
                                : "No DMARC Record"}
                            </Typography>
                            {dnsData.dmarc?.record && (
                              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                {dnsData.dmarc.record}
                              </Typography>
                            )}
                            <Typography variant="body2" sx={{ 
                              mt: 0.5, 
                              color: !dnsData.dmarc?.found ? '#d32f2f' : 
                                    dnsData.dmarc?.policy === 'reject' ? '#4caf50' :
                                    dnsData.dmarc?.policy === 'quarantine' ? '#ff9800' : '#d32f2f',
                              fontSize: '0.75rem'
                            }}>
                              {!dnsData.dmarc?.found 
                                ? "DMARC not configured - Emails not protected" 
                                : dnsData.dmarc.policy === 'reject'
                                  ? "Highest protection: Unauthorized emails will be rejected"
                                  : dnsData.dmarc.policy === 'quarantine'
                                    ? "Medium protection: Unauthorized emails will be quarantined"
                                    : "Weak protection: Only monitoring, no action taken on unauthorized emails"}
                            </Typography>
                            {hasRecentSecurityChanges('dmarc') && (
                              <>
                                <Divider sx={{ my: 1 }} />
                                <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#ff9800' }}>
                                  Recent Changes:
                                </Typography>
                                <Typography variant="body2" sx={{ fontSize: '0.7rem', color: '#ff9800' }}>
                                  {getRecentSecurityChange('dmarc')?.change_summary} - {formatTimeAgo(getRecentSecurityChange('dmarc')?.detected_at)}
                                </Typography>
                              </>
                            )}
                          </Box>
                        }
                        arrow
                        componentsProps={{
                          tooltip: {
                            sx: {
                              bgcolor: 'transparent',
                              p: 0,
                              '& .MuiTooltip-arrow': {
                                color: 'white'
                              }
                            }
                          }
                        }}
                      >
                        <Chip 
                          size="small"
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              DMARC
                              {renderChangeDirectionIcon('dmarc')}
                            </Box>
                          }
                          color={
                            !dnsData.dmarc?.found ? "error" : 
                            dnsData.dmarc.policy === 'reject' ? "success" :
                            dnsData.dmarc.policy === 'quarantine' ? "warning" :
                            "error" // policy=none
                          }
                          variant={dnsData.dmarc?.found ? "filled" : "outlined"}
                          sx={{ 
                            height: 36,
                            '& .MuiChip-label': {
                              display: 'flex',
                              alignItems: 'center'
                            }
                          }}
                        />
                      </Tooltip>
                    </Box>
                    
                    {/* Progress bar for IP lookups */}
                    {showLookupProgress && lookupProgress.total > 0 && (
                      <Box sx={{ mb: 2, mt: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Looking up IP providers: {lookupProgress.current} of {lookupProgress.total}
                          {lookupProgress.current > 0 && lookupProgress.total > 0 && 
                            ` (${Math.round((lookupProgress.current / lookupProgress.total) * 100)}%)`}
                        </Typography>
                        <LinearProgress 
                          variant="determinate" 
                          value={(lookupProgress.current / lookupProgress.total) * 100} 
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                      </Box>
                    )}
                    
                    {/* Authorized Email Providers Section - Moved from tooltip to main UI */}
                    {dnsData.spf?.record && (() => {
                      const spfEntries = extractSpfEntries(dnsData.spf.record);
                      if (spfEntries.length === 0) return null;
                      
                      return (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Authorized Email Providers:
                          </Typography>
                          
                          <Box sx={{ 
                            mt: 1,
                            p: 1.5,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            bgcolor: 'background.paper'
                          }}>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                              {spfEntries.map((entry, idx) => {
                                // Build tooltip content based on entry type
                                let tooltipContent = "";
                                
                                if (entry.type === 'hostname') {
                                  if (entry.provider) {
                                    tooltipContent = `Provider: ${entry.provider}\nDomain: ${entry.value}`;
                                  } else {
                                    tooltipContent = `Domain: ${entry.value}`;
                                  }
                                } else if (entry.type === 'hostname-group') {
                                  // For grouped hostname entries
                                  tooltipContent = `Base domain: ${entry.displayName.split(' ')[0]}\nEntries: ${entry.count}\n\n`;
                                  
                                  // List all the hostnames in this group
                                  entry.entries.forEach((subEntry, i) => {
                                    tooltipContent += `${i+1}. ${subEntry.value}\n`;
                                  });
                                } else if (entry.type === 'ip') {
                                  tooltipContent = `IP: ${entry.value}\nReverse lookup: ${entry.displayName}`;
                                } else if (entry.type === 'grouped') {
                                  // For grouped entries, show all the IPs/hostnames that are part of this group
                                  tooltipContent = `Base domain: ${getBaseDomain(entry.displayName)}\nEntries: ${entry.count}\n\n`;
                                  
                                  // Add details for each entry in the group
                                  entry.entries.forEach((subEntry, i) => {
                                    const ip = subEntry.value || '';
                                    const hostname = subEntry.hostname || '';
                                    tooltipContent += `${i+1}. ${ip} → ${hostname}\n`;
                                  });
                                } else if (entry.type === 'failed-group') {
                                  // For failed lookups, show all the IPs that failed
                                  tooltipContent = `Failed lookups: ${entry.entries.length}\n\n`;
                                  
                                  // List the failed IPs
                                  entry.entries.forEach((failedIp, i) => {
                                    tooltipContent += `${i+1}. ${failedIp.value}\n`;
                                  });
                                  
                                  tooltipContent += `\nThese IPs could not be resolved after multiple attempts.`;
                                }
                                
                                return (
                                  <Tooltip 
                                    key={idx}
                                    title={
                                      <Box sx={{ whiteSpace: 'pre-line', p: 0.5 }}>
                                        {tooltipContent}
                                      </Box>
                                    }
                                    arrow
                                  >
                                    <Chip
                                      size="small"
                                      label={entry.displayName}
                                      onClick={() => handleOpenProviderInfo(entry)}
                                      sx={{
                                        // Use different styling based on type of entry
                                        ...(entry.type === 'ip' && entry.reverseLookup 
                                          ? {
                                              // Reverse lookup IPs: white background with colored border and text
                                              bgcolor: '#ffffff',
                                              color: entry.color,
                                              border: `2px solid ${entry.color}`,
                                              fontWeight: 'bold',
                                            }
                                          : entry.type === 'grouped'
                                          ? {
                                              // Grouped IP lookups: green styling
                                              bgcolor: '#e8f5e9', // Light green background
                                              color: '#2e7d32', // Dark green text
                                              border: `2px solid #4caf50`,
                                              fontWeight: 'bold',
                                            }
                                          : entry.type === 'hostname-group'
                                          ? {
                                              // Grouped hostname entries: purple styling
                                              bgcolor: '#f3e5f5', // Light purple background
                                              color: '#6a1b9a', // Dark purple text
                                              border: `2px solid #9c27b0`,
                                              fontWeight: 'bold',
                                            }
                                          : entry.type === 'failed-group'
                                          ? {
                                              // Failed lookup group: distinct styling
                                              bgcolor: '#ffebee',
                                              color: '#d32f2f',
                                              border: `1px solid #f44336`,
                                              fontWeight: 'medium',
                                              fontStyle: 'italic',
                                            }
                                          : entry.isPending
                                          ? {
                                              // Pending lookups: light blue background with blue border
                                              bgcolor: entry.color || '#e3f2fd',
                                              color: 'rgba(0, 0, 0, 0.7)',
                                              border: `1px solid ${entry.borderColor || '#2196f3'}`,
                                              fontWeight: 'medium',
                                              fontStyle: 'italic',
                                            }
                                          : entry.hasFailed
                                          ? {
                                              // Individual failed lookups: light red background with red border
                                              bgcolor: entry.color || '#ffebee',
                                              color: 'rgba(0, 0, 0, 0.7)',
                                              border: `1px solid ${entry.borderColor || '#f44336'}`,
                                              fontWeight: 'medium',
                                              fontStyle: 'italic',
                                              opacity: 0.8,
                                            }
                                          : {
                                              // Normal entries: colored background with white text
                                              bgcolor: entry.color,
                                              color: 'white',
                                              fontWeight: 'medium',
                                            }
                                        ),
                                        fontSize: '0.8rem',
                                        height: 36,
                                        maxWidth: '180px',
                                        cursor: 'pointer',
                                        '&:hover': {
                                          opacity: 0.9,
                                          boxShadow: 1
                                        },
                                        '& .MuiChip-label': {
                                          px: 1.5,
                                          whiteSpace: 'nowrap',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis'
                                        }
                                      }}
                                    />
                                  </Tooltip>
                                );
                              })}
                            </Box>
                          </Box>
                        </Box>
                      );
                    })()}
                    
                    {/* Domain Verification Records Section */}
                    {dnsData && dnsData.verifications?.records && (() => {
                      // Only display if we have verification records
                      if (!dnsData.verifications.found || !dnsData.verifications.records || dnsData.verifications.records.length === 0) {
                        return null;
                      }
                      
                      // Check if we have categorized data from backend
                      let categorizedRecords = dnsData.verifications.categories || {};
                      
                      // Always categorize using the 'category' field from each record
                      categorizedRecords = {};
                      dnsData.verifications.records.forEach(record => {
                        const category = record.category || "Other";
                        if (!categorizedRecords[category]) {
                          categorizedRecords[category] = [];
                          categorizedRecords[category + "_seen"] = new Set();
                        }
                        // Use service+pattern as a unique key (or just service if you want to deduplicate by service only)
                        const uniqueKey = record.service + (record.pattern || "");
                        if (!categorizedRecords[category + "_seen"].has(uniqueKey)) {
                          categorizedRecords[category].push(record);
                          categorizedRecords[category + "_seen"].add(uniqueKey);
                        }
                      });
                      // Remove the _seen sets before rendering
                      Object.keys(categorizedRecords).forEach(key => {
                        if (key.endsWith("_seen")) delete categorizedRecords[key];
                      });
                      
                      // Build a sorted list of categories (with count of records)
                      const categories = Object.keys(categorizedRecords)
                        .filter(category => category !== "Other") // Filter out "Other" to handle separately
                        .map(category => ({
                          name: category,
                          count: categorizedRecords[category].length,
                          records: categorizedRecords[category]
                        }))
                        .sort((a, b) => b.count - a.count); // Sort by count descending
                      
                      // Add "Other" category at the end if it exists
                      if (categorizedRecords["Other"] && categorizedRecords["Other"].length > 0) {
                        categories.push({
                          name: "Other",
                          count: categorizedRecords["Other"].length,
                          records: categorizedRecords["Other"]
                        });
                      }
                      
                      // If categories are empty or only have "Other", just show the flat list
                      const shouldShowFlatList = categories.length === 0 || 
                        (categories.length === 1 && categories[0].name === "Other");
                      
                      return (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Domain Verification Records:
                          </Typography>
                          
                          {/* Verification records container */}
                          <Box sx={{ 
                            mt: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            overflow: 'hidden',
                            bgcolor: 'background.paper'
                          }}>
                            {shouldShowFlatList ? (
                              // Show flat list of chips
                              <Box sx={{ p: 1.5 }}>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                  {dnsData.verifications.records.map((entry, idx) => {
                                    let service = entry.service || 'Unknown';
                                    const record = entry.record || '';
                                    
                                    // Fix for shortened service names - if service name is the first part of a key=value pair
                                    // This ensures the service name is not truncated if it's extracted from the record
                                    if (service === 'Unknown' && record.includes('=')) {
                                      const keyPart = record.split('=')[0].trim();
                                      if (keyPart && keyPart.length < 50) { // Reasonable key size
                                        service = keyPart;
                                      }
                                    }
                                    

                                    
                                    // Truncate very long records for display
                                    const displayRecord = record.length > 20 
                                      ? record.substring(0, 17) + '...' 
                                      : record;
                                    
                                    // Build tooltip content
                                    const tooltipContent = `Service: ${service}\nRecord: ${record}`;
                                
                                return (
                                  <Tooltip 
                                    key={idx}
                                    title={
                                      <Box sx={{ whiteSpace: 'pre-line', p: 0.5 }}>
                                        {tooltipContent}
                                      </Box>
                                    }
                                    arrow
                                  >
                                    <Chip
                                      size="small"
                                          label={service}
                                      onClick={() => handleOpenProviderInfo({
                                        type: 'verification',
                                        value: record,
                                            displayName: service,
                                            provider: service
                                      })}
                                      sx={{
                                            bgcolor: getServiceColor(service),
                                        color: 'white',
                                        fontWeight: 'medium',
                                        fontSize: '0.8rem',
                                        height: 36,
                                        maxWidth: '180px',
                                        cursor: 'pointer',
                                        '&:hover': {
                                          opacity: 0.9,
                                          boxShadow: 1
                                        },
                                        '& .MuiChip-label': {
                                          px: 1.5,
                                          whiteSpace: 'nowrap',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis'
                                        }
                                      }}
                                    />
                                  </Tooltip>
                                );
                              })}
                            </Box>
                          </Box>
                            ) : (
                              // Show categorized view
                              <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
                                {categories.map((category, catIndex) => (
                                  <Box 
                                    key={category.name} 
                                    sx={{ 
                                      borderBottom: catIndex < categories.length - 1 ? '1px solid' : 'none',
                                      borderColor: 'divider'
                                    }}
                                  >
                                    <Box sx={{ 
                                      display: 'flex', 
                                      justifyContent: 'space-between', 
                                      alignItems: 'center',
                                      px: 2, 
                                      py: 1,
                                      bgcolor: 'action.hover'
                                    }}>
                                      <Typography 
                                        variant="subtitle2" 
                                        sx={{ 
                                          display: 'flex', 
                                          alignItems: 'center',
                                          color: 'text.primary',
                                          fontWeight: 'medium'
                                        }}
                                      >
                                        {getCategoryIcon(category.name)}
                                        {category.name}
                                      </Typography>
                                      <Chip 
                                        size="small" 
                                        label={category.count} 
                                        color="primary" 
                                        sx={{ height: 20, fontSize: '0.75rem' }}
                                      />
                                    </Box>
                                    <Box sx={{ p: 1.5 }}>
                                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                        {category.records.map((entry, idx) => {
                                          let service = entry.service || 'Unknown';
                                          const record = entry.record || '';
                                          const description = entry.description || '';
                                          
                                          // Fix for shortened service names - if service name is the first part of a key=value pair
                                          // This ensures the service name is not truncated if it's extracted from the record
                                          if (service === 'Unknown' && record.includes('=')) {
                                            const keyPart = record.split('=')[0].trim();
                                            if (keyPart && keyPart.length < 50) { // Reasonable key size
                                              service = keyPart;
                                            }
                                          }
                                          

                                          
                                          // Build tooltip content
                                          const tooltipContent = `Service: ${service}\n${description ? `Description: ${description}\n` : ''}Record: ${record}`;
                                          
                                          return (
                                            <Tooltip 
                                              key={idx}
                                              title={
                                                <Box sx={{ whiteSpace: 'pre-line', p: 0.5 }}>
                                                  {tooltipContent}
                                                </Box>
                                              }
                                              arrow
                                            >
                                              <Chip
                                                size="small"
                                                label={service}
                                                onClick={() => handleOpenProviderInfo({
                                                  type: 'verification',
                                                  value: record,
                                                  displayName: service,
                                                  provider: service,
                                                  description: description,
                                                  category: category.name
                                                })}
                                                sx={{
                                                  bgcolor: getServiceColor(service),
                                                  color: 'white',
                                                  fontWeight: 'medium',
                                                  fontSize: '0.8rem',
                                                  height: 36,
                                                  maxWidth: '180px',
                                                  cursor: 'pointer',
                                                  '&:hover': {
                                                    opacity: 0.9,
                                                    boxShadow: 1
                                                  },
                                                  '& .MuiChip-label': {
                                                    px: 1.5,
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                  }
                                                }}
                                              />
                                            </Tooltip>
                                          );
                                        })}
                                      </Box>
                                    </Box>
                                  </Box>
                                ))}
                              </Box>
                            )}
                          </Box>
                        </Box>
                      );
                                          })()}
                    </Box>
                )}
              </Box>
            </Box>
          </Paper>
        )}
        
        {/* Action buttons removed - now shown right after company name and URL */}
        
        {/* Error message */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {/* Outreach records table - Only show on Outreach Activity tab */}
        {activeTab === 1 && (
          <>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : outreaches.length > 0 ? (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleAddNewOutreach}
              >
                Record New Outreach
              </Button>
            </Box>
            <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Date & Time</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Method</TableCell>
                  <TableCell>Reached?</TableCell>
                  <TableCell>Follow-up / Meeting</TableCell>
                  {/* Only show the User column when an admin is viewing company outreaches
                      or when we're not viewing a specific company record */}
                  {showOutreachUser && <TableCell>User</TableCell>}
                  <TableCell>Completed</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {outreaches.map((outreach) => (
                  <React.Fragment key={outreach.id}>
                    <TableRow>
                      <TableCell>{formatDateTime(outreach.outreach_date)}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{outreach.contact_name}</Typography>
                        {getContactDetail(outreach) && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {getContactDetail(outreach)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip 
                          title={getContactDetail(outreach) ? `${getContactDetailLabel(outreach.outreach_method)}: ${getContactDetail(outreach)}` : 'No contact details recorded'}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {renderMethodIcon(outreach.outreach_method)}
                            {' '}
                            {outreach.outreach_method.charAt(0).toUpperCase() + outreach.outreach_method.slice(1)}
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {outreach.reached_intended_contact ? (
                          <SuccessIcon color="success" fontSize="small" />
                        ) : (
                          <FailIcon color="error" fontSize="small" />
                        )}
                      </TableCell>
                      <TableCell>
                        {outreach.meeting_scheduled ? (
                          <Tooltip title="Meeting scheduled">
                            <Box>
                              <EventIcon color="primary" fontSize="small" sx={{ mr: 1 }} />
                              {formatDateTime(outreach.meeting_date)}
                            </Box>
                          </Tooltip>
                        ) : outreach.followup_date ? (
                          <Tooltip title="Follow-up scheduled">
                            <Box>
                              <TimeIcon color="secondary" fontSize="small" sx={{ mr: 1 }} />
                              {formatDateTime(outreach.followup_date)}
                            </Box>
                          </Tooltip>
                        ) : (
                          'N/A'
                        )}
                      </TableCell>
                      {showOutreachUser && (
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar
                              sx={{
                                width: 24,
                                height: 24,
                                fontSize: '0.875rem',
                                bgcolor: outreach.username === user?.username ? 'primary.main' : 'text.secondary',
                              }}
                            >
                              {(outreach.display_name || outreach.username || '?').charAt(0).toUpperCase()}
                            </Avatar>
                            <Typography variant="body2">
                              {outreach.display_name || outreach.username}
                            </Typography>
                          </Box>
                        </TableCell>
                      )}
                      {/* Add Completed column */}
                      <TableCell>
                        <Tooltip title={outreach.completed ? "Mark as not completed" : "Mark as completed"}>
                          <IconButton 
                            size="small" 
                            color={outreach.completed ? "success" : "default"}
                            onClick={() => handleToggleCompletion(outreach.id, !outreach.completed)}
                            disabled={!outreach.followup_date} // Only enable for follow-ups
                          >
                            {outreach.completed ? 
                              <CheckCircleIcon fontSize="small" /> : 
                              <UncheckedIcon fontSize="small" />
                            }
                          </IconButton>
                        </Tooltip>
                        {outreach.completed && outreach.completed_date && (
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            {formatDateTime(outreach.completed_date)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Record follow-up with these contact details">
                          <IconButton
                            size="small"
                            color="secondary"
                            aria-label="Record follow-up"
                            onClick={() => handleFollowUp(outreach)}
                          >
                            <FollowUpIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {/* Only allow edit/delete for admin or if the outreach belongs to current user */}
                        {(user?.is_admin || outreach.username === user?.username) && (
                          <>
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => handleEdit(outreach)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => handleDelete(outreach.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                    {getCleanNotes(outreach) && (
                      <TableRow>
                        <TableCell 
                          colSpan={showOutreachUser ? 8 : 7}
                          sx={{ 
                            borderTop: 0,
                            backgroundColor: '#f5f5f5',
                            py: 1
                          }}
                        >
                          <Typography 
                            variant="body2"
                            color="text.secondary"
                            sx={{ pl: 2, borderLeft: '3px solid', borderColor: 'primary.main' }}
                          >
                            <strong>Notes:</strong> {getCleanNotes(outreach)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          </>
        ) : (
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <Typography variant="body1" align="center">
                No outreach records found. You can conveniently add outreach activity through the Enriched Contacts tab, or use the button below to record a new outreach manually.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleAddNewOutreach}
              >
                Record New Outreach
              </Button>
            </Box>
          </Paper>
        )}
          </>
        )}
        
        {/* Lusha Contacts Tab - Only show on Lusha Contacts tab */}
        {activeTab === 2 && (
          <Box sx={{ mt: 2 }}>
            <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 1 }}>
              <Typography variant="subtitle1" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <ContactPageIcon sx={{ mr: 1 }} fontSize="small" />
                Enriched Contacts
              </Typography>
              
              {lushaLoading && (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  <Typography variant="body2">Loading contacts...</Typography>
                </Box>
              )}
              
              {lushaError && !lushaLoading && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {lushaError}
                </Alert>
              )}
              
              {lushaContacts && lushaContacts.length > 0 && !lushaLoading && (
                <Box sx={{ mt: 2 }}>
                  {/* Filter Controls */}
                  <Box sx={{ mb: 2, p: 2, backgroundColor: 'rgba(0, 0, 0, 0.02)', borderRadius: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                      Filters
                    </Typography>
                    
                    {/* Text Search */}
                    <Box sx={{ mb: 2 }}>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="Search by name, title, email..."
                        value={lushaFilters.searchText || ''}
                        onChange={(e) => {
                          handleTextFilterChange(e.target.value);
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>
                    
                    {/* Dynamic Seniority Level Checkboxes */}
                    {getAvailableSeniorityLevels().length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                          Seniority Level: ({getAvailableSeniorityLevels().length} available)
                        </Typography>
                        <FormGroup row>
                          {getAvailableSeniorityLevels().map((level) => {
                            const seniorityLabels = {
                              'c-suite': 'C-Suite',
                              'founder': 'Founder',
                              'vice president': 'VP',
                              'director': 'Director',
                              'manager': 'Manager',
                              'senior': 'Senior',
                              'entry': 'Entry'
                            };
                            
                            return (
                              <FormControlLabel
                                key={level}
                                control={
                                  <Checkbox
                                    checked={(lushaFilters.seniority_levels || []).includes(level)}
                                    onChange={() => toggleSeniorityFilter(level)}
                                    size="small"
                                  />
                                }
                                label={seniorityLabels[level] || level}
                                sx={{ mr: 2 }}
                              />
                            );
                          })}
                        </FormGroup>
                      </Box>
                    )}

                    {/* Dynamic Department Checkboxes */}
                    {getAvailableDepartments().length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                          Departments: ({getAvailableDepartments().length} available)
                        </Typography>
                        <FormGroup row>
                          {getAvailableDepartments().map((department) => (
                            <FormControlLabel
                              key={department}
                              control={
                                <Checkbox
                                  checked={(lushaFilters.departments || []).includes(department)}
                                  onChange={() => toggleDepartmentFilter(department)}
                                  size="small"
                                />
                              }
                              label={department}
                              sx={{ mr: 2 }}
                            />
                          ))}
                        </FormGroup>
                      </Box>
                    )}
                    
                    {/* Clear Filters Button */}
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={clearLushaFilters}
                      startIcon={<CancelIcon />}
                    >
                      Clear Filters
                    </Button>
                  </Box>

                  {/* Results Summary and Controls */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body2" color="text.secondary">
                        {getPaginatedLushaContacts().totalContacts} contacts found
                        {lushaContacts && getPaginatedLushaContacts().totalContacts !== lushaContacts.length && 
                          ` (filtered from ${lushaContacts.length})`
                        }
                      </Typography>
                      {checkingSalesforce && (
                        <CircularProgress size={16} sx={{ ml: 1 }} />
                      )}
                      {salesforceConnectionStatus && !checkingSalesforce && (
                        <Chip 
                          icon={<CloudDoneIcon />}
                          label="Salesforce Connected" 
                          size="small" 
                          variant="outlined"
                          sx={{ 
                            fontSize: '0.7rem', 
                            height: 20,
                            borderColor: '#00A1E0',
                            color: '#00A1E0',
                            '& .MuiChip-label': {
                              color: '#00A1E0'
                            },
                            '& .MuiChip-icon': {
                              color: '#00A1E0 !important'
                            }
                          }}
                        />
                      )}
                      {checkingHubspot && (
                        <CircularProgress size={16} sx={{ ml: 1 }} />
                      )}
                      {hubspotConnectionStatus && !checkingHubspot && (
                        <Chip 
                          icon={<CloudDoneIcon />}
                          label="HubSpot Connected" 
                          size="small" 
                          variant="outlined"
                          sx={{ 
                            fontSize: '0.7rem', 
                            height: 20,
                            ml: 0.5,
                            borderColor: '#FF7A59',
                            color: '#FF7A59',
                            '& .MuiChip-label': {
                              color: '#FF7A59'
                            },
                            '& .MuiChip-icon': {
                              color: '#FF7A59 !important'
                            }
                          }}
                        />
                      )}
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={fetchLushaContacts}
                      startIcon={<RefreshIcon />}
                    >
                      Refresh
                    </Button>
                  </Box>
                  
                  {/* Contacts List - Compact Single Line Design */}
                  <Paper variant="outlined">
                    <List dense sx={{ width: '100%', bgcolor: 'background.paper' }}>
                    {getPaginatedLushaContacts().contacts.map((contact, index) => {
                      const contactId = contact.id || contact.contactId;
                      const displayName = contact.displayName || contact.fullName || `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || 'Unknown Contact';
                        const seniorityLevel = getContactSeniorityLevel(contact);
                        
                        const seniorityColors = {
                          'c-suite': 'error',      // Red for C-Suite (highest)
                          'founder': 'secondary',   // Purple for Founder
                          'vice president': 'warning', // Orange for VP
                          'director': 'info',       // Blue for Director
                          'manager': 'success',     // Green for Manager
                          'senior': 'default',     // Gray for Senior
                          'entry': 'default'       // Gray for Entry
                        };
                        
                        const seniorityLabels = {
                          'c-suite': 'C-Suite',
                          'founder': 'Founder',
                          'vice president': 'VP',
                          'director': 'Director',
                          'manager': 'Manager',
                          'senior': 'Senior',
                          'entry': 'Entry'
                        };
                      
                      return (
                          <React.Fragment key={`${contactId}-${index}`}>
                        <ListItem
                              alignItems="flex-start"
                          sx={{ 
                                py: 1,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease-in-out',
                                display: 'flex',
                                justifyContent: 'space-between',
                                '&:hover': {
                                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                  transform: 'translateX(4px)'
                                },
                                // Subtle highlight for enriched contacts
                                backgroundColor: 'transparent',
                                borderLeft: (contact.isEnriched || contact.enriched) ? 
                                  '3px solid #e0e0e0' : '3px solid transparent'
                              }}
                              onClick={() => {
                                if (contact.isEnriched || contact.enriched) {
                                  if (isMobile) {
                                    setEnrichmentModal({
                                      open: true,
                                      data: {
                                        contact,
                                        contactId,
                                        creditsUsed: 0,
                                        originalContact: contact,
                                      },
                                    });
                                    return;
                                  }
                                } else {
                                  enrichLushaContact(contactId);
                                }
                              }}
                              onDoubleClick={() => {
                                if (!isMobile && (contact.isEnriched || contact.enriched)) {
                                  setEnrichmentModal({
                                    open: true,
                                    data: {
                                      contact: contact,
                                      contactId: contactId,
                                      creditsUsed: 0,
                                      originalContact: contact
                                    }
                                  });
                                }
                              }}
                            >
                              {/* Avatar */}
                          <ListItemAvatar>
                                <Avatar 
                                  sx={{ 
                                    bgcolor: (contact.isEnriched || contact.enriched) ? 'success.main' : 'primary.main',
                                    width: 32,
                                    height: 32,
                                    fontSize: '0.875rem'
                                  }}
                                >
                              {getContactAvatarText(contact)}
                            </Avatar>
                          </ListItemAvatar>
                              
                              {/* Single Line Contact Info */}
                              <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                {/* Name */}
                                <Typography 
                                  variant="subtitle2" 
                                  sx={{ 
                                    fontWeight: 600,
                                    minWidth: 120,
                                    flexShrink: 0
                                  }}
                                >
                                {displayName}
                              </Typography>
                                
                                {/* Job Title */}
                                {contact.jobTitle && (
                                  <Typography 
                                    variant="body2" 
                                    color="text.secondary"
                                    sx={{ 
                                      minWidth: 100,
                                      flexShrink: 0,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      maxWidth: 200
                                    }}
                                  >
                                    {contact.jobTitle}
                                  </Typography>
                                )}
                                
                                {/* Seniority Badge */}
                                <Chip
                                  label={seniorityLabels[seniorityLevel] || 'Entry'}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontSize: '0.7rem', height: 20, flexShrink: 0 }}
                                />
                                
                                {/* Enriched Status */}
                              {(contact.isEnriched || contact.enriched) && (
                                <Chip 
                                  icon={<CheckCircleIcon />}
                                  label="Enriched" 
                                  size="small" 
                                  variant="outlined"
                                    sx={{ fontSize: '0.7rem', height: 20, flexShrink: 0 }}
                                  />
                                )}
                                
                                
                                {/* Contact Details for Enriched Contacts */}
                              {(contact.isEnriched || contact.enriched) && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                                    {/* Email */}
                                  {(contact.workEmail || contact.email || contact.personalEmail) && (
                                      <Tooltip title={`Email: ${contact.workEmail || contact.email || contact.personalEmail}`}>
                                        <Link 
                                          href={`mailto:${contact.workEmail || contact.email || contact.personalEmail}`}
                                          sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <EmailIcon fontSize="small" color="action" />
                                        </Link>
                                      </Tooltip>
                                  )}
                                  
                                  {/* Phone */}
                                  {(contact.directPhone || contact.phone || contact.mobilePhone) && (
                                      <Tooltip title={`Phone: ${contact.mobilePhone || contact.directPhone || contact.phone}`}>
                                        <Link 
                                          href={`tel:${contact.mobilePhone || contact.directPhone || contact.phone}`}
                                          sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <PhoneIcon fontSize="small" color="action" />
                                        </Link>
                                      </Tooltip>
                                  )}
                                  
                                  {/* LinkedIn */}
                                  {(contact.linkedinUrl || contact.linkedInUrl) && (
                                      <Tooltip title="LinkedIn Profile">
                                        <Link 
                                          href={contact.linkedinUrl || contact.linkedInUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <LinkedInIcon fontSize="small" sx={{ color: '#0077b5' }} />
                                        </Link>
                                      </Tooltip>
                                  )}
                                </Box>
                              )}
                                
                                {/* Interaction Hints */}
                                {!(contact.isEnriched || contact.enriched) ? (
                                  <Typography 
                                    variant="body2" 
                                    color="text.secondary"
                                    sx={{ 
                                      fontStyle: 'italic',
                                      opacity: 0.7,
                                      flexShrink: 0
                                    }}
                                  >
                                    Click to enrich
                                  </Typography>
                                ) : (
                                  <Typography 
                                    variant="body2" 
                                    color="success.main"
                                    sx={{ 
                                      fontStyle: 'italic',
                                      opacity: 0.8,
                                      flexShrink: 0,
                                      fontSize: '0.75rem'
                                    }}
                                  >
                                    Double-click for details
                                  </Typography>
                              )}
                            </Box>
                            
                            {/* Salesforce and HubSpot Actions - Right Aligned */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto', flexShrink: 0 }}>
                              {(() => {
                                // Extract email - prefer emailAddresses array (from enriched contacts)
                                let contactEmail = null;
                                if (contact.emailAddresses && Array.isArray(contact.emailAddresses) && contact.emailAddresses.length > 0) {
                                  const workEmail = contact.emailAddresses.find(e => e.emailType === 'work' || e.emailType === 'Work');
                                  contactEmail = workEmail ? workEmail.email : contact.emailAddresses[0].email;
                                } else {
                                  contactEmail = contact.workEmail || contact.email || contact.personalEmail;
                                }
                                
                                // Normalize email to lowercase for consistent matching
                                const emailKey = contactEmail ? contactEmail.trim().toLowerCase() : null;
                                const isInSalesforce = emailKey && salesforceLeadsMap[emailKey];
                                const isInHubspot = emailKey && hubspotContactsMap[emailKey];
                                const contactId = contact.id || contact.contactId;
                                const isAddingToSalesforce = addingToSalesforce.has(contactId);
                                const isAddingToHubspot = addingToHubspot.has(contactId);
                                const isEnriched = contact.isEnriched || contact.enriched;
                                
                                return (
                                  <>
                                    {/* Salesforce Actions */}
                                    {isInSalesforce ? (
                                      <Tooltip title="Already in Salesforce">
                                        <Chip 
                                          icon={<CloudDoneIcon />}
                                          label="In Salesforce" 
                                          size="small" 
                                          variant="outlined"
                                          sx={{ 
                                            fontSize: '0.7rem', 
                                            height: 20, 
                                            flexShrink: 0,
                                            borderColor: '#00A1E0',
                                            color: '#00A1E0',
                                            '& .MuiChip-label': {
                                              color: '#00A1E0'
                                            },
                                            '& .MuiChip-icon': {
                                              color: '#00A1E0 !important'
                                            }
                                          }}
                                        />
                                      </Tooltip>
                                    ) : salesforceConnectionStatus && isEnriched ? (
                                      <Tooltip title="Add to Salesforce">
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            addContactToSalesforce(contact);
                                          }}
                                          disabled={isAddingToSalesforce}
                                          sx={{ p: 0.5, color: '#00A1E0' }}
                                        >
                                          {isAddingToSalesforce ? (
                                            <CircularProgress size={16} sx={{ color: '#00A1E0' }} />
                                          ) : (
                                            <PersonAddIcon fontSize="small" sx={{ color: '#00A1E0' }} />
                                          )}
                                        </IconButton>
                                      </Tooltip>
                                    ) : null}
                                    
                                    {/* HubSpot Actions */}
                                    {isInHubspot ? (
                                      <Tooltip title="Already in HubSpot">
                                        <Chip 
                                          icon={<CloudDoneIcon />}
                                          label="In HubSpot" 
                                          size="small" 
                                          variant="outlined"
                                          sx={{ 
                                            fontSize: '0.7rem', 
                                            height: 20, 
                                            flexShrink: 0,
                                            borderColor: '#FF7A59',
                                            color: '#FF7A59',
                                            '& .MuiChip-label': {
                                              color: '#FF7A59'
                                            },
                                            '& .MuiChip-icon': {
                                              color: '#FF7A59 !important'
                                            }
                                          }}
                                        />
                                      </Tooltip>
                                    ) : hubspotConnectionStatus && isEnriched ? (
                                      <Tooltip title="Add to HubSpot">
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            addContactToHubspot(contact);
                                          }}
                                          disabled={isAddingToHubspot}
                                          sx={{ p: 0.5, color: '#FF7A59' }}
                                        >
                                          {isAddingToHubspot ? (
                                            <CircularProgress size={16} sx={{ color: '#FF7A59' }} />
                                          ) : (
                                            <PersonAddIcon fontSize="small" sx={{ color: '#FF7A59' }} />
                                          )}
                                        </IconButton>
                                      </Tooltip>
                                    ) : null}
                                  </>
                                );
                              })()}
                            </Box>
                            
                        </ListItem>
                            {index < getPaginatedLushaContacts().contacts.length - 1 && <Divider />}
                          </React.Fragment>
                      );
                    })}
                  </List>
                  </Paper>
                  
                  {/* Pagination */}
                  {getPaginatedLushaContacts().totalPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                      <Pagination
                        count={getPaginatedLushaContacts().totalPages}
                        page={lushaContactsPage + 1}
                        onChange={handleLushaPageChange}
                        color="primary"
                      />
                    </Box>
                  )}
                </Box>
              )}
              
              {lushaContacts && lushaContacts.length === 0 && !lushaLoading && !lushaError && (
                <Box sx={{ mt: 1 }}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    No contacts found for this company.
                  </Alert>
                  <Button
                    variant="outlined"
                    onClick={fetchLushaContacts}
                    startIcon={<RefreshIcon />}
                  >
                    Try Again
                  </Button>
                </Box>
              )}
              
              {!lushaContacts && !lushaLoading && !lushaError && (
                <Box sx={{ mt: 1 }}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Click "Load Contacts" to search for contacts from this company.
                  </Alert>
                  <Button
                    variant="contained"
                    onClick={fetchLushaContacts}
                    startIcon={<ContactPageIcon />}
                  >
                    Load Contacts
                  </Button>
                </Box>
              )}
            </Paper>
          </Box>
        )}
        
        {/* AI Research Tab - Only show on AI Research tab */}
        {activeTab === 3 && (
          <Box sx={{ mt: 2 }}>
            <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 1 }}>
              <Typography variant="subtitle1" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <DescriptionIcon sx={{ mr: 1 }} fontSize="small" />
                AI Research
              </Typography>
              
              {researchLoading && (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Loading AI research data...
                  </Typography>
                </Box>
              )}
              
              {researchError && (
                <Box sx={{ mt: 1 }}>
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {researchError}
                  </Alert>
                  <Button
                    variant="outlined"
                    onClick={fetchResearchData}
                    startIcon={<RefreshIcon />}
                  >
                    Try Again
                  </Button>
                </Box>
              )}
              
              {researchData?.sections && (
                <Box sx={{ mt: 2 }}>
                  <Grid container spacing={2} sx={{ width: '100%' }}>
                    {(() => {
                      const sections = researchData.sections;
                      const midpoint = Math.ceil(sections.length / 2);
                      const leftColumnSections = sections.slice(0, midpoint);
                      const rightColumnSections = sections.slice(midpoint);
                      
                      return (
                        <>
                          {/* Left column */}
                          <Grid item xs={12} md={6}>
                            {leftColumnSections.map((section, idx) => (
                              <Card elevation={2} sx={{ 
                                mb: 2,
                                ...(section.isSourcesSection ? { borderLeft: '3px solid', borderColor: 'primary.main' } : {})
                              }} key={`left-${idx}`}>
                                <CardContent sx={{ 
                                  '& .MuiCardHeader-root': { pb: 1 },
                                  '& .MuiCardContent-root': { pt: 0 }
                                }}>
                                  <Typography 
                                    variant="subtitle2" 
                                    color="primary"
                                    sx={{ 
                                      fontWeight: "bold",
                                      fontSize: '0.9rem',
                                      mb: 1
                                    }}
                                  >
                                    {section.title}
                                  </Typography>
                                  <Box sx={{ 
                                    '& p': { mt: 0.5, mb: 0.5, fontSize: '0.85rem' },
                                    '& ul, & ol': { ml: 2, mb: 1, mt: 0.5, pl: 1 },
                                    '& li': { mb: 0.25, fontSize: '0.85rem' }
                                  }}>
                                    <ReactMarkdown
                                      components={{
                                        p: ({ node, children, ...props }) => (
                                          <Typography variant="body2" sx={{ mt: 1, mb: 1 }} {...props}>
                                            <CitationLink>{children}</CitationLink>
                                          </Typography>
                                        ),
                                        h1: ({ node, children, ...props }) => (
                                          <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }} {...props}>
                                            <CitationLink>{children}</CitationLink>
                                          </Typography>
                                        ),
                                        h2: ({ node, children, ...props }) => (
                                          <Typography variant="subtitle1" sx={{ mt: 1.5, mb: 1, fontWeight: 'bold' }} {...props}>
                                            <CitationLink>{children}</CitationLink>
                                          </Typography>
                                        ),
                                        h3: ({ node, children, ...props }) => (
                                          <Typography variant="subtitle2" sx={{ mt: 1.5, mb: 0.5, fontWeight: 'bold' }} {...props}>
                                            <CitationLink>{children}</CitationLink>
                                          </Typography>
                                        ),
                                        ul: ({ node, ...props }) => <ul style={{ marginLeft: 20, marginTop: 8, marginBottom: 8 }} {...props} />,
                                        ol: ({ node, ...props }) => <ol style={{ marginLeft: 20, marginTop: 8, marginBottom: 8 }} {...props} />,
                                        li: ({ node, children, ...props }) => (
                                          <li {...props}>
                                            <Typography variant="body2" component="span">
                                              <CitationLink>{children}</CitationLink>
                                            </Typography>
                                          </li>
                                        ),
                                        a: ({ node, href, children, ...props }) => (
                                          <Link href={href} target="_blank" rel="noopener" color="primary" {...props}>
                                            {children}
                                          </Link>
                                        ),
                                        strong: ({ node, children, ...props }) => (
                                          <Typography component="span" sx={{ fontWeight: 'bold' }} {...props}>
                                            {children}
                                          </Typography>
                                        ),
                                        em: ({ node, children, ...props }) => (
                                          <Typography component="span" sx={{ fontStyle: 'italic' }} {...props}>
                                            {children}
                                          </Typography>
                                        ),
                                        code: ({ node, inline, children, ...props }) => (
                                          inline ? 
                                            <Typography component="code" sx={{ fontFamily: 'monospace', backgroundColor: '#f5f5f5', px: 0.5, borderRadius: 0.5 }} {...props}>
                                              {children}
                                            </Typography> :
                                            <Box component="pre" sx={{ fontFamily: 'monospace', backgroundColor: '#f5f5f5', p: 1, borderRadius: 1, overflow: 'auto' }} {...props}>
                                              <code>{children}</code>
                                            </Box>
                                        ),
                                        blockquote: ({ node, children, ...props }) => (
                                          <Box component="blockquote" sx={{ borderLeft: 4, borderColor: 'grey.400', pl: 2, py: 0.5, my: 1 }} {...props}>
                                            {children}
                                          </Box>
                                        ),
                                        hr: ({ node, ...props }) => <Divider sx={{ my: 2 }} {...props} />
                                      }}
                                    >
                                      {section.content}
                                    </ReactMarkdown>
                                  </Box>
                                </CardContent>
                              </Card>
                            ))}
                          </Grid>
                          
                          {/* Right column */}
                          <Grid item xs={12} md={6}>
                            {rightColumnSections.map((section, idx) => (
                              <Card elevation={2} sx={{ 
                                mb: 2,
                                ...(section.isSourcesSection ? { borderLeft: '3px solid', borderColor: 'primary.main' } : {})
                              }} key={`right-${idx}`}>
                                <CardContent sx={{ 
                                  '& .MuiCardHeader-root': { pb: 1 },
                                  '& .MuiCardContent-root': { pt: 0 }
                                }}>
                                  <Typography 
                                    variant="subtitle2" 
                                    color="primary"
                                    sx={{ 
                                      fontWeight: "bold",
                                      fontSize: '0.9rem',
                                      mb: 1
                                    }}
                                  >
                                    {section.title}
                                  </Typography>
                                  <Box sx={{ 
                                    '& p': { mt: 0.5, mb: 0.5, fontSize: '0.85rem' },
                                    '& ul, & ol': { ml: 2, mb: 1, mt: 0.5, pl: 1 },
                                    '& li': { mb: 0.25, fontSize: '0.85rem' }
                                  }}>
                                    <ReactMarkdown
                                      components={{
                                        p: ({ node, children, ...props }) => (
                                          <Typography variant="body2" sx={{ mt: 1, mb: 1 }} {...props}>
                                            <CitationLink>{children}</CitationLink>
                                          </Typography>
                                        ),
                                        h1: ({ node, children, ...props }) => (
                                          <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }} {...props}>
                                            <CitationLink>{children}</CitationLink>
                                          </Typography>
                                        ),
                                        h2: ({ node, children, ...props }) => (
                                          <Typography variant="subtitle1" sx={{ mt: 1.5, mb: 1, fontWeight: 'bold' }} {...props}>
                                            <CitationLink>{children}</CitationLink>
                                          </Typography>
                                        ),
                                        h3: ({ node, children, ...props }) => (
                                          <Typography variant="subtitle2" sx={{ mt: 1.5, mb: 0.5, fontWeight: 'bold' }} {...props}>
                                            <CitationLink>{children}</CitationLink>
                                          </Typography>
                                        ),
                                        ul: ({ node, ...props }) => <ul style={{ marginLeft: 20, marginTop: 8, marginBottom: 8 }} {...props} />,
                                        ol: ({ node, ...props }) => <ol style={{ marginLeft: 20, marginTop: 8, marginBottom: 8 }} {...props} />,
                                        li: ({ node, children, ...props }) => (
                                          <li {...props}>
                                            <Typography variant="body2" component="span">
                                              <CitationLink>{children}</CitationLink>
                                            </Typography>
                                          </li>
                                        ),
                                        a: ({ node, href, children, ...props }) => (
                                          <Link href={href} target="_blank" rel="noopener" color="primary" {...props}>
                                            {children}
                                          </Link>
                                        ),
                                        strong: ({ node, children, ...props }) => (
                                          <Typography component="span" sx={{ fontWeight: 'bold' }} {...props}>
                                            {children}
                                          </Typography>
                                        ),
                                        em: ({ node, children, ...props }) => (
                                          <Typography component="span" sx={{ fontStyle: 'italic' }} {...props}>
                                            {children}
                                          </Typography>
                                        ),
                                        code: ({ node, inline, children, ...props }) => (
                                          inline ? 
                                            <Typography component="code" sx={{ fontFamily: 'monospace', backgroundColor: '#f5f5f5', px: 0.5, borderRadius: 0.5 }} {...props}>
                                              {children}
                                            </Typography> :
                                            <Box component="pre" sx={{ fontFamily: 'monospace', backgroundColor: '#f5f5f5', p: 1, borderRadius: 1, overflow: 'auto' }} {...props}>
                                              <code>{children}</code>
                                            </Box>
                                        ),
                                        blockquote: ({ node, children, ...props }) => (
                                          <Box component="blockquote" sx={{ borderLeft: 4, borderColor: 'grey.400', pl: 2, py: 0.5, my: 1 }} {...props}>
                                            {children}
                                          </Box>
                                        ),
                                        hr: ({ node, ...props }) => <Divider sx={{ my: 2 }} {...props} />
                                      }}
                                    >
                                      {section.content}
                                    </ReactMarkdown>
                                  </Box>
                                </CardContent>
                              </Card>
                            ))}
                          </Grid>
                        </>
                      );
                    })()}
                  </Grid>
                </Box>
              )}
              
              {!researchData && !researchLoading && !researchError && (
                <Box sx={{ mt: 1 }}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Click "Load AI Research" to generate comprehensive research about this company.
                  </Alert>
                  <Button
                    variant="contained"
                    onClick={fetchResearchData}
                    startIcon={<DescriptionIcon />}
                  >
                    Load AI Research
                  </Button>
                </Box>
              )}
            </Paper>
          </Box>
        )}
        
        {/* Source Dialog for AI Research citations */}
        <SourceDialog 
          open={openSourceDialog} 
          onClose={handleCloseSourceDialog} 
          source={selectedSource}
        />
        
        {/* Outreach form dialog */}
        <Dialog
          open={showForm}
          onClose={resetForm}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {editMode ? 'Edit Outreach Record' : followUpMode ? 'Follow-up Outreach' : 'Record New Outreach'}
          </DialogTitle>
          <DialogContent>
            <Box component="form" sx={{ mt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Contact Name"
                    fullWidth
                    required
                    margin="normal"
                    value={formData.contact_name}
                    onChange={(e) => handleFormChange('contact_name', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Outreach Method</InputLabel>
                    <Select
                      value={formData.outreach_method}
                      label="Outreach Method"
                      onChange={(e) => handleFormChange('outreach_method', e.target.value)}
                    >
                      <MenuItem value="phone">Phone</MenuItem>
                      <MenuItem value="email">Email</MenuItem>
                      <MenuItem value="linkedin">LinkedIn</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  {formData.outreach_method === 'phone' && findContactPhones().length > 1 ? (
                    <FormControl fullWidth margin="normal">
                      <InputLabel>{getContactDetailLabel(formData.outreach_method)}</InputLabel>
                      <Select
                        value={formData.contact_detail}
                        label={getContactDetailLabel(formData.outreach_method)}
                        onChange={(e) => handleFormChange('contact_detail', e.target.value)}
                      >
                        {findContactPhones().map((phone, index) => (
                          <MenuItem key={index} value={phone.number}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <PhoneIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                              <Typography>
                                {phone.number} 
                                <Typography component="span" color="text.secondary" sx={{ ml: 1, fontSize: '0.85em' }}>
                                  ({phone.label})
                                </Typography>
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <TextField
                      label={getContactDetailLabel(formData.outreach_method)}
                      fullWidth
                      required
                      margin="normal"
                      type={getContactDetailInputType(formData.outreach_method)}
                      value={formData.contact_detail}
                      onChange={(e) => handleFormChange('contact_detail', e.target.value)}
                      placeholder={
                        formData.outreach_method === 'phone' ? '+1 (555) 123-4567' :
                        formData.outreach_method === 'email' ? 'name@company.com' :
                        'https://linkedin.com/in/username'
                      }
                      error={!!validationError}
                      helperText={validationError || 
                        (formData.outreach_method === 'phone' ? 'Example: +1 (555) 123-4567 or 123-456-7890' :
                        formData.outreach_method === 'email' ? 'Example: name@company.com' :
                        'Example: https://linkedin.com/in/username')
                      }
                    />
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DateTimePicker
                    label="Outreach Date & Time"
                    value={formData.outreach_date}
                    onChange={(newValue) => handleFormChange('outreach_date', newValue)}
                    sx={{ width: '100%' }}
                    slotProps={{ textField: { margin: 'normal' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.reached_intended_contact}
                        onChange={(e) => handleFormChange('reached_intended_contact', e.target.checked)}
                        sx={{ mt: 2 }}
                      />
                    }
                    label="Reached Intended Contact?"
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Follow-up Details
                    </Typography>
                  </Divider>
                </Grid>
                
                {!formData.reached_intended_contact && (
                  <Grid item xs={12} sm={6}>
                    <DateTimePicker
                      label="Follow-up Date & Time"
                      value={formData.followup_date}
                      onChange={(newValue) => handleFormChange('followup_date', newValue)}
                      sx={{ width: '100%' }}
                      slotProps={{ textField: { margin: 'normal' } }}
                    />
                  </Grid>
                )}
                
                {formData.reached_intended_contact && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.meeting_scheduled}
                            onChange={(e) => handleFormChange('meeting_scheduled', e.target.checked)}
                            sx={{ mt: 2 }}
                          />
                        }
                        label="Meeting Scheduled?"
                      />
                    </Grid>
                    
                    {formData.meeting_scheduled ? (
                      <Grid item xs={12} sm={6}>
                        <DateTimePicker
                          label="Meeting Date & Time"
                          value={formData.meeting_date}
                          onChange={(newValue) => handleFormChange('meeting_date', newValue)}
                          sx={{ width: '100%' }}
                          slotProps={{ textField: { margin: 'normal' } }}
                        />
                      </Grid>
                    ) : (
                      <Grid item xs={12} sm={6}>
                        <DateTimePicker
                          label="Follow-up Date & Time"
                          value={formData.followup_date}
                          onChange={(newValue) => handleFormChange('followup_date', newValue)}
                          sx={{ width: '100%' }}
                          slotProps={{ textField: { margin: 'normal' } }}
                        />
                      </Grid>
                    )}
                  </>
                )}
                
                {(formData.followup_date || formData.meeting_scheduled) && (
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.completed}
                          onChange={(e) => handleFormChange('completed', e.target.checked)}
                          sx={{ mt: 2 }}
                          disabled={formData.meeting_scheduled} // Disable for meetings
                        />
                      }
                      label="Mark Follow-up as Completed"
                    />
                  </Grid>
                )}
                
                <Grid item xs={12}>
                  <TextField
                    label="Notes"
                    fullWidth
                    multiline
                    rows={4}
                    margin="normal"
                    value={formData.notes}
                    onChange={(e) => handleFormChange('notes', e.target.value)}
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={resetForm}>Cancel</Button>
            <Button onClick={handleSubmit} variant="contained">
              {editMode ? 'Update' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Notification snackbar */}
        <Snackbar
          open={notification.open}
          autoHideDuration={6000}
          onClose={handleCloseNotification}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={handleCloseNotification}
            severity={notification.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
        
        {/* Research Dialog */}
        <CompanyResearchDialog 
          open={researchDialogOpen}
          onClose={handleCloseResearch}
          record={contactInfo}
        />
        
        {/* Provider Info Dialog */}
        <ProviderInfoDialog
          open={providerInfoOpen}
          onClose={handleCloseProviderInfo}
          provider={selectedProvider}
          result={providerSearchResult}
          loading={providerSearchLoading}
        />

        {/* Contact Details Dialog */}
        {selectedContact && (
          <Dialog
            open={contactDetailsOpen}
            onClose={() => setContactDetailsOpen(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle sx={{ 
              backgroundColor: 'primary.main', 
              color: 'white',
              pb: 1
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar 
                  sx={{ 
                    bgcolor: 'primary.light',
                    color: 'white',
                    width: 48, 
                    height: 48,
                    mr: 2,
                    border: '2px solid white'
                  }}
                >
                  {getContactAvatarText(selectedContact)}
                </Avatar>
                <Box>
                  <Typography variant="h6">
                    {selectedContact.displayName || selectedContact.fullName || 'Contact Details'}
                  </Typography>
                  {selectedContact.jobTitle && (
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      {selectedContact.jobTitle}
                    </Typography>
                  )}
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              <Grid container spacing={3}>
                {(selectedContact.company || selectedContact.companyName) && (
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="subtitle2">Company</Typography>
                    </Box>
                    <Typography variant="body1">
                      {typeof selectedContact.company === 'string' ? selectedContact.company : 
                       selectedContact.companyName || 
                       (selectedContact.company && selectedContact.company.companyName) ||
                       'Company'}
                    </Typography>
                  </Grid>
                )}
                
                {(selectedContact.workEmail || selectedContact.personalEmail) && (
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <EmailIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="subtitle2">Email</Typography>
                    </Box>
                    <Typography variant="body1">
                      {selectedContact.workEmail || selectedContact.personalEmail}
                    </Typography>
                  </Grid>
                )}
                
                {(selectedContact.mobilePhone || selectedContact.directPhone) && (
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <PhoneIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="subtitle2">Phone</Typography>
                    </Box>
                    <Typography variant="body1">
                      {selectedContact.mobilePhone || selectedContact.directPhone}
                    </Typography>
                  </Grid>
                )}
                
                {selectedContact.linkedinUrl && (
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <LinkedInIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="subtitle2">LinkedIn</Typography>
                    </Box>
                    <Link 
                      href={selectedContact.linkedinUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      sx={{ display: 'flex', alignItems: 'center' }}
                    >
                      {selectedContact.linkedinUrl}
                      <OpenInNewIcon fontSize="small" sx={{ ml: 1 }} />
                    </Link>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={() => setContactDetailsOpen(false)} variant="outlined">
                Close
              </Button>
              {!selectedContact.enriched && (
                <Button 
                  onClick={() => enrichLushaContact(selectedContact.id || selectedContact.contactId)}
                  variant="contained"
                  disabled={enrichLoading}
                  startIcon={enrichLoading ? <CircularProgress size={16} /> : undefined}
                >
                  {enrichLoading ? 'Enriching...' : 'Enrich Contact'}
                </Button>
              )}
            </DialogActions>
          </Dialog>
        )}

        {/* Mobile concise enrichment details modal */}
        {isMobile && enrichmentModal.open && enrichmentModal.data && (
          <Dialog
            open={enrichmentModal.open}
            onClose={handleEnrichmentModalClose}
            fullWidth
            maxWidth="sm"
            scroll="paper"
            sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
            PaperProps={{
              sx: {
                mx: 1.5,
                mb: 10,
                borderRadius: 2,
                maxHeight: 'calc(100vh - 120px)',
              },
            }}
          >
            <DialogContent sx={{ p: 0 }}>
              <EnrichedContactPanel
                contact={enrichmentModal.data.contact}
                creditsUsed={enrichmentModal.data.creditsUsed}
                onClose={handleEnrichmentModalClose}
                onCopy={copyToClipboard}
                onAddOutreach={handleAddOutreachFromContact}
                getContactAvatarText={getContactAvatarText}
                embedded
              />
            </DialogContent>
          </Dialog>
        )}

        {/* Enrichment Results Modal — desktop only */}
        {enrichmentModal.open && enrichmentModal.data && !isMobile && (
          <Dialog 
            open={enrichmentModal.open} 
            onClose={handleEnrichmentModalClose} 
            maxWidth="md" 
            fullWidth
            PaperProps={{
              sx: { borderRadius: 2 }
            }}
          >
            <DialogTitle sx={{ 
              pb: 1, 
              backgroundColor: 'success.main',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <CheckCircleIcon />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" component="div">
                  Contact Enriched Successfully!
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {enrichmentModal.data.creditsUsed} Contacts credits used
                </Typography>
              </Box>
              <IconButton
                onClick={handleEnrichmentModalClose}
                sx={{ 
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            
            <DialogContent sx={{ p: 0 }}>
              {(() => {
                const contact = enrichmentModal.data.contact;
                const displayName = contact.fullName || contact.displayName || `${contact.firstName || ''} ${contact.lastName || ''}`.trim();
                
                return (
                  <Box>
                    {/* Header Section */}
                    <Box sx={{ 
                      p: 3, 
                      backgroundColor: 'grey.50',
                      borderBottom: '1px solid #e0e0e0'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {/* Contact Avatar */}
                        <Avatar 
                          sx={{ 
                            width: 64, 
                            height: 64, 
                            bgcolor: 'success.main',
                            fontSize: '1.5rem',
                            fontWeight: 'bold'
                          }}
                        >
                          {getContactAvatarText(contact)}
                        </Avatar>
                        
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                            {displayName}
                          </Typography>
                          <Typography variant="h6" color="text.secondary">
                            {contact.jobTitle || contact.title || 'Position not specified'}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                            <BusinessIcon fontSize="small" color="action" />
                            <Typography variant="body1">
                              {contact.company?.name || contact.companyName || 'Company not specified'}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Company Logo - Moved to right side */}
                        {(contact.companyLogo || contact.company?.logoUrl || contact.company?.logo) && (
                          <Box 
                            component="img"
                            src={contact.companyLogo || contact.company?.logoUrl || contact.company?.logo}
                            alt={`${contact.company?.name || contact.companyName} logo`}
                            sx={{ 
                              maxHeight: 80,
                              maxWidth: 120,
                              width: 'auto',
                              height: 'auto',
                              objectFit: 'contain',
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                              backgroundColor: 'white',
                              p: 1
                            }}
                          />
                        )}
                      </Box>
                    </Box>

                    {/* Main Content Grid */}
                    <Box sx={{ p: 3 }}>
                      <Grid container spacing={3}>
                        {/* Contact Information Section */}
                        <Grid item xs={12} md={6}>
                          <Paper elevation={1} sx={{ p: 2, height: '100%', borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                              <ContactPageIcon color="primary" />
                              Contact Information
                            </Typography>
                            
                            <Stack spacing={2}>
                              {/* Personal Details */}
                              <Box>
                                <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main' }}>
                                  👤 Personal Details
                                </Typography>
                                <Stack spacing={1}>
                                  <Box>
                                    <Typography variant="body2" color="text.secondary">Full Name:</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                      {contact.fullName || `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || 'Not specified'}
                                    </Typography>
                                  </Box>
                                  <Box>
                                    <Typography variant="body2" color="text.secondary">Job Title:</Typography>
                                    <Typography variant="body1">
                                      {contact.jobTitle || contact.title || 'Not specified'}
                                    </Typography>
                                  </Box>
                                  {contact.departments && contact.departments.length > 0 && (
                                    <Box>
                                      <Typography variant="body2" color="text.secondary">Department:</Typography>
                                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                        {contact.departments.map((dept, index) => (
                                          <Chip key={index} label={dept} size="small" variant="outlined" />
                                        ))}
                                      </Box>
                                    </Box>
                                  )}
                                  {contact.seniority && Array.isArray(contact.seniority) && contact.seniority.length > 0 && (
                                    <Box>
                                      <Typography variant="body2" color="text.secondary">Seniority Level:</Typography>
                                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                        {contact.seniority.map((sen, index) => (
                                          <Chip 
                                            key={index} 
                                            label={sen.value || sen} 
                                            size="small" 
                                            variant="outlined" 
                                          />
                                        ))}
                                      </Box>
                                    </Box>
                                  )}
                                  {(contact.contactLocation || contact.contactCity || contact.contactCountry) && (
                                    <Box>
                                      <Typography variant="body2" color="text.secondary">Location:</Typography>
                                      <Typography variant="body1">
                                        {[contact.contactCity, contact.contactCountry, contact.contactLocation]
                                          .filter(Boolean).join(', ') || 'Not specified'}
                                      </Typography>
                                    </Box>
                                  )}
                                </Stack>
                              </Box>

                              {/* Contact Methods */}
                              <Box>
                                <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main' }}>
                                  📞 Contact Methods
                                </Typography>
                                <Stack spacing={1}>
                                  {/* Email Addresses */}
                                  {contact.emailAddresses && contact.emailAddresses.length > 0 ? (
                                    contact.emailAddresses.map((emailObj, index) => (
                                      <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <EmailIcon fontSize="small" color="primary" />
                                        <Box sx={{ flex: 1 }}>
                                          <Link href={`mailto:${emailObj.email}`} sx={{ textDecoration: 'none', fontWeight: 500 }}>
                                            {emailObj.email}
                                          </Link>
                                          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                                            <Chip label={emailObj.emailType || 'Email'} size="small" variant="outlined" />
                                            {emailObj.emailConfidence && (
                                              <Chip label={`Confidence: ${emailObj.emailConfidence}`} size="small" variant="outlined" color="success" />
                                            )}
                                          </Box>
                                        </Box>
                                        <IconButton
                                          size="small"
                                          onClick={() => copyToClipboard(emailObj.email, 'Email')}
                                          sx={{ ml: 1 }}
                                          title="Copy email"
                                        >
                                          <CopyIcon fontSize="small" />
                                        </IconButton>
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          onClick={() => handleAddOutreachFromContact(contact, 'email', emailObj.email)}
                                          startIcon={<PersonAddIcon />}
                                          sx={{
                                            ml: 1,
                                            minWidth: 'auto',
                                            fontSize: '0.75rem',
                                            py: 0.5,
                                            px: 1,
                                            color: 'primary.main',
                                            borderColor: 'primary.main',
                                            '&:hover': {
                                              backgroundColor: 'primary.main',
                                              color: 'white',
                                            }
                                          }}
                                          title="Create outreach with this email"
                                        >
                                          Outreach
                                        </Button>
                                      </Box>
                                    ))
                                  ) : (
                                    // Fallback for simple email fields
                                    <>
                                      {contact.workEmail && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <EmailIcon fontSize="small" color="primary" />
                                          <Box sx={{ flex: 1 }}>
                                            <Link href={`mailto:${contact.workEmail}`} sx={{ textDecoration: 'none', fontWeight: 500 }}>
                                              {contact.workEmail}
                                            </Link>
                                            <Chip label="Work" size="small" variant="outlined" sx={{ ml: 1 }} />
                                          </Box>
                                          <IconButton
                                            size="small"
                                            onClick={() => copyToClipboard(contact.workEmail, 'Work Email')}
                                            sx={{ ml: 1 }}
                                            title="Copy email"
                                          >
                                            <CopyIcon fontSize="small" />
                                          </IconButton>
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => handleAddOutreachFromContact(contact, 'email', contact.workEmail)}
                                            startIcon={<PersonAddIcon />}
                                            sx={{
                                              ml: 1,
                                              minWidth: 'auto',
                                              fontSize: '0.75rem',
                                              py: 0.5,
                                              px: 1,
                                              color: 'primary.main',
                                              borderColor: 'primary.main',
                                              '&:hover': {
                                                backgroundColor: 'primary.main',
                                                color: 'white',
                                              }
                                            }}
                                            title="Create outreach with this email"
                                          >
                                            Outreach
                                          </Button>
                                        </Box>
                                      )}
                                      {contact.email && contact.email !== contact.workEmail && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <EmailIcon fontSize="small" color="primary" />
                                          <Box sx={{ flex: 1 }}>
                                            <Link href={`mailto:${contact.email}`} sx={{ textDecoration: 'none', fontWeight: 500 }}>
                                              {contact.email}
                                            </Link>
                                          </Box>
                                          <IconButton
                                            size="small"
                                            onClick={() => copyToClipboard(contact.email, 'Email')}
                                            sx={{ ml: 1 }}
                                            title="Copy email"
                                          >
                                            <CopyIcon fontSize="small" />
                                          </IconButton>
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => handleAddOutreachFromContact(contact, 'email', contact.email)}
                                            startIcon={<PersonAddIcon />}
                                            sx={{
                                              ml: 1,
                                              minWidth: 'auto',
                                              fontSize: '0.75rem',
                                              py: 0.5,
                                              px: 1,
                                              color: 'primary.main',
                                              borderColor: 'primary.main',
                                              '&:hover': {
                                                backgroundColor: 'primary.main',
                                                color: 'white',
                                              }
                                            }}
                                            title="Create outreach with this email"
                                          >
                                            Outreach
                                          </Button>
                                        </Box>
                                      )}
                                    </>
                                  )}

                                  {/* Phone Numbers */}
                                  {contact.phoneNumbers && contact.phoneNumbers.length > 0 ? (
                                    contact.phoneNumbers.map((phoneObj, index) => (
                                      <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <PhoneIcon fontSize="small" color="success" />
                                        <Box sx={{ flex: 1 }}>
                                          <Link href={`tel:${phoneObj.number}`} sx={{ textDecoration: 'none', fontWeight: 500 }}>
                                            {phoneObj.number}
                                          </Link>
                                          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                                            <Chip label={phoneObj.phoneType || 'Phone'} size="small" variant="outlined" />
                                            {phoneObj.doNotCall === false && (
                                              <Chip label="OK to call" size="small" variant="outlined" color="success" />
                                            )}
                                            {phoneObj.doNotCall === true && (
                                              <Chip label="Do not call" size="small" variant="outlined" />
                                            )}
                                          </Box>
                                        </Box>
                                        <IconButton
                                          size="small"
                                          onClick={() => copyToClipboard(phoneObj.number, 'Phone Number')}
                                          sx={{ ml: 1 }}
                                          title="Copy phone number"
                                        >
                                          <CopyIcon fontSize="small" />
                                        </IconButton>
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          onClick={() => handleAddOutreachFromContact(contact, 'phone', phoneObj.number)}
                                          startIcon={<PersonAddIcon />}
                                          sx={{
                                            ml: 1,
                                            minWidth: 'auto',
                                            fontSize: '0.75rem',
                                            py: 0.5,
                                            px: 1,
                                            color: 'success.main',
                                            borderColor: 'success.main',
                                            '&:hover': {
                                              backgroundColor: 'success.main',
                                              color: 'white',
                                            }
                                          }}
                                          title="Create outreach with this phone number"
                                        >
                                          Outreach
                                        </Button>
                                      </Box>
                                    ))
                                  ) : (
                                    // Fallback for simple phone fields
                                    <>
                                      {contact.directPhone && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <PhoneIcon fontSize="small" color="success" />
                                          <Box sx={{ flex: 1 }}>
                                            <Link href={`tel:${contact.directPhone}`} sx={{ textDecoration: 'none', fontWeight: 500 }}>
                                              {contact.directPhone}
                                            </Link>
                                            <Chip label="Direct" size="small" variant="outlined" sx={{ ml: 1 }} />
                                          </Box>
                                          <IconButton
                                            size="small"
                                            onClick={() => copyToClipboard(contact.directPhone, 'Direct Phone')}
                                            sx={{ ml: 1 }}
                                            title="Copy phone number"
                                          >
                                            <CopyIcon fontSize="small" />
                                          </IconButton>
                                        </Box>
                                      )}
                                      {contact.phone && contact.phone !== contact.directPhone && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <PhoneIcon fontSize="small" color="success" />
                                          <Box sx={{ flex: 1 }}>
                                            <Link href={`tel:${contact.phone}`} sx={{ textDecoration: 'none', fontWeight: 500 }}>
                                              {contact.phone}
                                            </Link>
                                          </Box>
                                          <IconButton
                                            size="small"
                                            onClick={() => copyToClipboard(contact.phone, 'Phone Number')}
                                            sx={{ ml: 1 }}
                                            title="Copy phone number"
                                          >
                                            <CopyIcon fontSize="small" />
                                          </IconButton>
                                        </Box>
                                      )}
                                    </>
                                  )}

                                  {/* Social Media */}
                                  {(contact.linkedinUrl || contact.linkedin || contact.socialLinks?.linkedin) && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <LinkedInIcon sx={{ color: '#0077b5' }} />
                                      <Box sx={{ flex: 1 }}>
                                        <Link 
                                          href={contact.linkedinUrl || contact.linkedin || contact.socialLinks?.linkedin}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          sx={{ textDecoration: 'none', fontWeight: 500 }}
                                        >
                                          LinkedIn Profile
                                        </Link>
                                      </Box>
                                      <IconButton
                                        size="small"
                                        onClick={() => copyToClipboard(
                                          contact.linkedinUrl || contact.linkedin || contact.socialLinks?.linkedin, 
                                          'LinkedIn URL'
                                        )}
                                        sx={{ ml: 1 }}
                                        title="Copy LinkedIn URL"
                                      >
                                        <CopyIcon fontSize="small" />
                                      </IconButton>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => handleAddOutreachFromContact(
                                          contact, 
                                          'linkedin', 
                                          contact.linkedinUrl || contact.linkedin || contact.socialLinks?.linkedin
                                        )}
                                        startIcon={<PersonAddIcon />}
                                        sx={{
                                          ml: 1,
                                          minWidth: 'auto',
                                          fontSize: '0.75rem',
                                          py: 0.5,
                                          px: 1,
                                          color: '#0077b5',
                                          borderColor: '#0077b5',
                                          '&:hover': {
                                            backgroundColor: '#0077b5',
                                            color: 'white',
                                          }
                                        }}
                                        title="Create outreach with LinkedIn profile"
                                      >
                                        Outreach
                                      </Button>
                                    </Box>
                                  )}
                                </Stack>
                              </Box>
                            </Stack>
                          </Paper>
                        </Grid>

                        {/* Company Information Section */}
                        <Grid item xs={12} md={6}>
                          <Paper elevation={1} sx={{ p: 2, height: '100%', borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                              <BusinessIcon color="primary" />
                              Company Information
                            </Typography>
                            
                            <Stack spacing={2}>
                              {/* Company Overview */}
                              <Box>
                                <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main' }}>
                                  🏢 Company Overview
                                </Typography>
                                <Stack spacing={1}>
                                  <Box>
                                    <Typography variant="body2" color="text.secondary">Company Name:</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                      {contact.companyName || contact.company?.name || 'Not specified'}
                                    </Typography>
                                  </Box>
                                  
                                  {contact.companyDescription && (
                                    <Box>
                                      <Typography variant="body2" color="text.secondary">Description:</Typography>
                                      <Typography 
                                        variant="body2" 
                                        sx={{ 
                                          maxHeight: 120, 
                                          overflow: 'auto',
                                          backgroundColor: '#f8f9fa',
                                          p: 1,
                                          borderRadius: 1,
                                          fontSize: '0.875rem',
                                          lineHeight: 1.4
                                        }}
                                      >
                                        {contact.companyDescription}
                                      </Typography>
                                    </Box>
                                  )}
                                  
                                  <Grid container spacing={1}>
                                    {contact.companyIndustry && (
                                      <Grid item xs={12}>
                                        <Typography variant="body2" color="text.secondary">Industry:</Typography>
                                        <Chip label={contact.companyIndustry} size="small" variant="outlined" />
                                      </Grid>
                                    )}
                                    {contact.companySubIndustry && (
                                      <Grid item xs={12}>
                                        <Typography variant="body2" color="text.secondary">Sub-Industry:</Typography>
                                        <Chip label={contact.companySubIndustry} size="small" color="info" variant="outlined" />
                                      </Grid>
                                    )}
                                    {contact.companyEmployees && (
                                      <Grid item xs={12}>
                                        <Typography variant="body2" color="text.secondary">Employee Count:</Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                          {contact.companyEmployees}
                                        </Typography>
                                      </Grid>
                                    )}
                                  </Grid>
                                </Stack>
                              </Box>

                              {/* Company Details */}
                              <Box>
                                <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main' }}>
                                  📍 Company Details
                                </Typography>
                                <Stack spacing={1}>
                                  {contact.companyLocation && (
                                    <Box>
                                      <Typography variant="body2" color="text.secondary">Location:</Typography>
                                      <Typography variant="body1">
                                        {contact.companyLocation}
                                      </Typography>
                                    </Box>
                                  )}
                                  
                                  {contact.company?.location && (
                                    <Box>
                                      <Typography variant="body2" color="text.secondary">Geographic Details:</Typography>
                                      <Typography variant="body2">
                                        {[
                                          contact.company.location.city,
                                          contact.company.location.country,
                                          contact.company.location.continent
                                        ].filter(Boolean).join(', ')}
                                      </Typography>
                                    </Box>
                                  )}

                                  {contact.companyLinkedin && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <LinkedInIcon sx={{ color: '#0077b5' }} />
                                      <Box sx={{ flex: 1 }}>
                                        <Link 
                                          href={contact.companyLinkedin}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          sx={{ textDecoration: 'none', fontWeight: 500 }}
                                        >
                                          Company LinkedIn
                                        </Link>
                                      </Box>
                                      <IconButton
                                        size="small"
                                        onClick={() => copyToClipboard(contact.companyLinkedin, 'Company LinkedIn URL')}
                                        sx={{ ml: 1 }}
                                        title="Copy company LinkedIn URL"
                                      >
                                        <CopyIcon fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  )}

                                  {contact.company?.fqdn && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <LanguageIcon color="action" />
                                      <Box sx={{ flex: 1 }}>
                                        <Link 
                                          href={`https://${contact.company.fqdn}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          sx={{ textDecoration: 'none', fontWeight: 500 }}
                                        >
                                          {contact.company.fqdn}
                                        </Link>
                                      </Box>
                                      <IconButton
                                        size="small"
                                        onClick={() => copyToClipboard(`https://${contact.company.fqdn}`, 'Company Website URL')}
                                        sx={{ ml: 1 }}
                                        title="Copy company website URL"
                                      >
                                        <CopyIcon fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  )}
                                </Stack>
                              </Box>

                              {/* Additional Insights */}
                              {(contact.company?.revenueRange || contact.personId || contact.companyId) && (
                                <Box>
                                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main' }}>
                                    📊 Additional Insights
                                  </Typography>
                                  <Stack spacing={1}>
                                    {contact.company?.revenueRange && contact.company.revenueRange[0] > 0 && (
                                      <Box>
                                        <Typography variant="body2" color="text.secondary">Revenue Range:</Typography>
                                        <Typography variant="body1">
                                          ${contact.company.revenueRange[0].toLocaleString()} - ${contact.company.revenueRange[1].toLocaleString()}
                                        </Typography>
                                      </Box>
                                    )}
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                      {contact.personId && (
                                        <Chip label={`Person ID: ${contact.personId}`} size="small" variant="outlined" />
                                      )}
                                      {contact.companyId && (
                                        <Chip label={`Company ID: ${contact.companyId}`} size="small" variant="outlined" />
                                      )}
                                    </Box>
                                  </Stack>
                                </Box>
                              )}
                            </Stack>
                          </Paper>
                        </Grid>
                      </Grid>
                    </Box>
                  </Box>
                );
              })()}
            </DialogContent>
            
            
          </Dialog>
        )}



      </Box>
    </LocalizationProvider>
  );
};

export default SalesOutreach; 
